/**
 * What every column on every form is - generated, not written.
 *
 * Source: core-api `src/utils/taxFormsStructures`, the same per-year definitions that drive
 * core-api's own upload validation. Regenerate with:
 *
 *   node packages/iris-engine/scripts/extract-core-api-field-rules.mjs
 *
 * `kind` says what the column holds: an amount, a TIN, a ZIP, a state code, a date, an X
 * checkbox, or free text. `required` is core-api's own .required() on that column.
 *
 * Generated 2026-10-06. Do not edit by hand.
 */

export type FieldKind =
  | "amount"
  | "tin"
  | "zip"
  | "state"
  | "date"
  | "checkbox"
  | "idType"
  | "text";

export interface FieldRule {
  kind: FieldKind;
  required?: boolean;
  decimal?: boolean;
  label?: string;
}

export type FormFieldRules = Record<string, FieldRule>;

export const FIELD_RULES_BY_YEAR: Record<string, Record<string, FormFieldRules>> = {
  "2025": {
    "3921": {
      "RECIPIENT_ID_NUMBER": {
        "kind": "text",
        "required": true,
        "label": "Recipient ID Number"
      },
      "EMPLOYEE_NAME": {
        "kind": "text",
        "required": true,
        "label": "Employee Name"
      },
      "EMPLOYEE_SSN": {
        "kind": "tin",
        "required": true,
        "label": "Employee SSN"
      },
      "EMPLOYEE_NAME2": {
        "kind": "text",
        "label": "Employee Name 2"
      },
      "EMPLOYEE_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Employee Address 1"
      },
      "EMPLOYEE_ADDRESS2": {
        "kind": "text",
        "label": "Employee Address 2"
      },
      "EMPLOYEE_ADDRESS3": {
        "kind": "text",
        "label": "Employee Address 3"
      },
      "EMPLOYEE_CITY": {
        "kind": "text",
        "required": true,
        "label": "Employee City"
      },
      "EMPLOYEE_STATE": {
        "kind": "text",
        "required": true,
        "label": "Employee State"
      },
      "EMPLOYEE_ZIP": {
        "kind": "text",
        "required": true,
        "label": "Employee ZIP"
      },
      "ACCOUNT_NUMBER": {
        "kind": "text",
        "label": "Account Number"
      },
      "transectionId": {
        "kind": "text",
        "label": "Transaction ID"
      },
      "BOX1_DATE_GRANTED": {
        "kind": "date",
        "label": "Date Granted"
      },
      "BOX2_DATE_EXERCISED": {
        "kind": "date",
        "label": "Date Exercised"
      },
      "BOX3_EXE_PRICE_PER_SHARE": {
        "kind": "text",
        "decimal": true,
        "label": "Exercise Price per Share"
      },
      "BOX4_FAIR_MARKET_VALUE_ON_SHARE": {
        "kind": "text",
        "decimal": true,
        "label": "Fair Market Value per Share"
      },
      "BOX5_SHARES_TRANSFERRED": {
        "kind": "amount",
        "label": "Shares Transferred"
      },
      "BOX6_EIN": {
        "kind": "text",
        "label": "Employer Identification Number"
      },
      "BOX6_COMPANY": {
        "kind": "text",
        "label": "Company"
      },
      "BOX6_ADDRESS": {
        "kind": "text",
        "label": "Company Address"
      },
      "BOX_6D": {
        "kind": "text",
        "label": "Box 6D"
      },
      "1099_YEAR": {
        "kind": "amount",
        "required": true,
        "label": "1099 Year"
      },
      "SITE_ID": {
        "kind": "text",
        "required": true,
        "label": "Site ID"
      },
      "FILER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Filer Name"
      },
      "FILER_NAME2": {
        "kind": "text",
        "label": "Filer Name 2"
      },
      "FILER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Filer Address 1"
      },
      "FILER_ADDRESS2": {
        "kind": "text",
        "label": "Filer Address 2"
      },
      "FILER_ADDRESS3": {
        "kind": "text",
        "label": "Filer Address 3"
      },
      "FILER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Filer City"
      },
      "FILER_STATE": {
        "kind": "text",
        "required": true,
        "label": "Filer State"
      },
      "FILER_ZIP": {
        "kind": "text",
        "required": true,
        "label": "Filer ZIP"
      },
      "FILER_PHONE": {
        "kind": "text",
        "label": "Filer Phone"
      },
      "FILER_FEDERAL_ID_NUMBER": {
        "kind": "text",
        "required": true,
        "label": "Filer Federal ID Number"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "3922": {
      "RECIPIENT_ID_NUMBER": {
        "kind": "tin",
        "required": true,
        "label": "Recipient ID Number"
      },
      "SITE_ID": {
        "kind": "text",
        "required": true,
        "label": "Site ID"
      },
      "1099_YEAR": {
        "kind": "amount",
        "required": true,
        "label": "Tax Year"
      },
      "CORPORATION_NAME_1": {
        "kind": "text",
        "required": true,
        "label": "Corporation Name 1"
      },
      "CORPORATION_NAME_2": {
        "kind": "text",
        "label": "Corporation Name 2"
      },
      "CORPORATION_ADDRESS": {
        "kind": "text",
        "required": true,
        "label": "Corporation Address"
      },
      "CORPORATION_ADDRESS2": {
        "kind": "text",
        "label": "Corporation Address 2"
      },
      "CORPORATION_CITY": {
        "kind": "text",
        "required": true,
        "label": "Corporation City"
      },
      "CORPORATION_STATE": {
        "kind": "state",
        "required": true,
        "label": "Corporation State"
      },
      "CORPORATION_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Corporation ZIP"
      },
      "CORPORATION_PHONE": {
        "kind": "text",
        "label": "Corporation Phone"
      },
      "CORPORATION_EIN": {
        "kind": "text",
        "label": "Corporation EIN"
      },
      "EMPLOYEE_SSN": {
        "kind": "tin",
        "required": true,
        "label": "Employee SSN"
      },
      "EMPLOYEE_NAME": {
        "kind": "text",
        "required": true,
        "label": "Employee Name"
      },
      "EMPLOYEE_ADDRESS_1": {
        "kind": "text",
        "required": true,
        "label": "Employee Address 1"
      },
      "EMPLOYEE_ADDRESS_2": {
        "kind": "text",
        "label": "Employee Address 2"
      },
      "EMPLOYEE_ADDRESS_3": {
        "kind": "text",
        "label": "Employee Address 3"
      },
      "EMPLOYEE_CITY": {
        "kind": "text",
        "required": true,
        "label": "Employee City"
      },
      "EMPLOYEE_STATE": {
        "kind": "state",
        "required": true,
        "label": "Employee State"
      },
      "EMPLOYEE_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Employee ZIP"
      },
      "EMPLOYEE_ACCOUNT_NO": {
        "kind": "text",
        "label": "Employee Account Number"
      },
      "BOX1_DATE_GRANTED": {
        "kind": "text",
        "label": "Box 1"
      },
      "BOX2_DATE_EXERCISED": {
        "kind": "text",
        "label": "Box 2"
      },
      "BOX3_FAIR_MARKET_VALUE_GRANT_DATE": {
        "kind": "amount",
        "decimal": true,
        "label": "Box 3"
      },
      "BOX4_FAIR_MARKET_VALUE_EXE_DATE": {
        "kind": "amount",
        "decimal": true,
        "label": "Box 4"
      },
      "BOX5_EXERCISE_PRICE_PAID": {
        "kind": "amount",
        "decimal": true,
        "label": "Box 5"
      },
      "BOX6_NO_SHARES_TRANSFERRED": {
        "kind": "amount",
        "label": "Box 6"
      },
      "BOX7_DATE_TITLE_TRANSFERRED": {
        "kind": "text",
        "label": "Box 7"
      },
      "BOX8_EXERCISE_PRICE_PAID_ON_GRANT_DATE": {
        "kind": "amount",
        "decimal": true,
        "label": "Box 8"
      },
      "ROW_INDEX": {
        "kind": "text",
        "label": "Row Index"
      },
      "transectionId": {
        "kind": "text",
        "label": "Transaction ID"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "1099-MISC": {
      "SITE_ID": {
        "kind": "text",
        "required": true,
        "label": "Site ID"
      },
      "JOB_NUMBER": {
        "kind": "text",
        "label": "Job Number"
      },
      "1099_YEAR": {
        "kind": "text",
        "required": true,
        "label": "1099 Year"
      },
      "SSN/TIN": {
        "kind": "text",
        "label": "SSN/TIN"
      },
      "FILER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Filer Name"
      },
      "FILER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Filer Address 1"
      },
      "FILER_ADDRESS2": {
        "kind": "text",
        "label": "Filer Address 2"
      },
      "FILER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Filer City"
      },
      "FILER_STATE": {
        "kind": "state",
        "required": true,
        "label": "Filer State"
      },
      "FILER_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Filer ZIP"
      },
      "FILER_PHONE": {
        "kind": "text",
        "required": true,
        "label": "Filer Phone"
      },
      "FILER_FEDERAL_ID_NUMBER": {
        "kind": "text",
        "required": true,
        "label": "Filer Federal ID Number"
      },
      "BOX_2ND_TIN_NOT_IND": {
        "kind": "checkbox",
        "label": "2nd TIN Not Indicator"
      },
      "BOX1_RENTS": {
        "kind": "amount",
        "label": "Rents"
      },
      "BOX2_ROYALTIES": {
        "kind": "amount",
        "label": "Royalties"
      },
      "BOX3_OTHER_INCOME": {
        "kind": "amount",
        "label": "Other Income"
      },
      "BOX4_FED_INCOME_TAX_WITHHELD": {
        "kind": "amount",
        "label": "Federal Income Tax Withheld"
      },
      "BOX5_FISHING_BOAT_PROCEEDS": {
        "kind": "amount",
        "label": "Fishing Boat Proceeds"
      },
      "BOX6_MEDICAL_HEALTHCARE_PAYMENTS": {
        "kind": "amount",
        "label": "Medical Healthcare Payments"
      },
      "BOX7_DIRECT_SALES_IND": {
        "kind": "checkbox",
        "label": "Direct Sales Indicator"
      },
      "BOX8_SUBSTITUTE_PAYMENTS": {
        "kind": "amount",
        "label": "Substitute Payments"
      },
      "BOX9_CROP_INSURANCE_PROCEEDS": {
        "kind": "amount",
        "label": "Crop Insurance Proceeds"
      },
      "BOX10_GROSS_PROCEEDS_ATTORNEY": {
        "kind": "amount",
        "label": "Gross Proceeds Paid to Attorney"
      },
      "BOX11_FISH_PURCHASED_RESALE": {
        "kind": "amount",
        "label": "Fish Purchased for Resale"
      },
      "BOX12_SECTION_409A_DEFERRAL": {
        "kind": "amount",
        "label": "Section 409A Deferral"
      },
      "BOX13_FATCA_REQUIREMENT": {
        "kind": "checkbox",
        "label": "FATCA Requirement"
      },
      "BOX15_NONQUALIFIED_COMPENSATION": {
        "kind": "amount",
        "label": "Nonqualified Compensation"
      },
      "BOX16_STATE_TAX_WITHHELD1": {
        "kind": "amount",
        "label": "State Tax Withheld 1"
      },
      "BOX16_STATE_TAX_WITHHELD2": {
        "kind": "amount",
        "label": "State Tax Withheld 2"
      },
      "BOX17_PAYER_STATE_NUMBER1": {
        "kind": "text",
        "label": "Payer State Number 1"
      },
      "BOX17_PAYER_STATE_NUMBER2": {
        "kind": "text",
        "label": "Payer State Number 2"
      },
      "BOX18_STATE_INCOME1": {
        "kind": "amount",
        "label": "State Income 1"
      },
      "BOX18_STATE_INCOME2": {
        "kind": "amount",
        "label": "State Income 2"
      },
      "FOREIGN_COUNTRY": {
        "kind": "text",
        "label": "Foreign Country"
      },
      "FOREIGN_COUNTRY_INDICATOR": {
        "kind": "checkbox",
        "label": "Foreign Country Indicator"
      },
      "ACCOUNT_NUMBER": {
        "kind": "text",
        "label": "Account Number"
      },
      "RECIPIENT_NAME": {
        "kind": "text",
        "required": true,
        "label": "Recipient Name"
      },
      "RECIPIENT_NAME2": {
        "kind": "text",
        "label": "Recipient Name 2"
      },
      "RECIPIENT_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Recipient Address 1"
      },
      "RECIPIENT_ADDRESS2": {
        "kind": "text",
        "label": "Recipient Address 2"
      },
      "RECIPIENT_ADDRESS3": {
        "kind": "text",
        "label": "Recipient Address 3"
      },
      "RECIPIENT_CITY": {
        "kind": "text",
        "required": true,
        "label": "Recipient City"
      },
      "RECIPIENT_STATE": {
        "kind": "state",
        "required": true,
        "label": "Recipient State"
      },
      "RECIPIENT_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Recipient ZIP"
      },
      "RECIPIENT_EMAIL": {
        "kind": "text",
        "label": "Recipient Email"
      },
      "RECIPIENT_ID_NUMBER": {
        "kind": "tin",
        "required": true,
        "label": "RECIPIENT_ID_NUMBER"
      },
      "createdBy": {
        "kind": "text"
      },
      "userId": {
        "kind": "text"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "1098-E": {
      "SITE_ID": {
        "kind": "text",
        "required": true,
        "label": "Site ID"
      },
      "1098_YEAR": {
        "kind": "amount",
        "required": true,
        "label": "1098 Year"
      },
      "BORROWER_SSN": {
        "kind": "text",
        "required": true,
        "label": "Borrower SSN"
      },
      "BORROWER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Borrower Name"
      },
      "BORROWER_NAME2": {
        "kind": "text",
        "label": "Borrower Name 2"
      },
      "JOB_NUMBER": {
        "kind": "text",
        "label": "Job Number"
      },
      "BORROWER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Borrower Address 1"
      },
      "BORROWER_ADDRESS2": {
        "kind": "text",
        "label": "Borrower Address 2"
      },
      "BORROWER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Borrower City"
      },
      "BORROWER_STATE": {
        "kind": "text",
        "required": true,
        "label": "Borrower State"
      },
      "BORROWER_ZIP": {
        "kind": "text",
        "required": true,
        "label": "Borrower ZIP"
      },
      "FOREIGN_COUNTRY_INDICATOR": {
        "kind": "checkbox",
        "label": "Foreign Country Indicator"
      },
      "FOREIGN_COUNTRY": {
        "kind": "text",
        "label": "Foreign Country"
      },
      "BOX1_STUDENT_LOAN_INTEREST": {
        "kind": "amount",
        "label": "Student Loan Interest"
      },
      "BOX2_PRE_2004_ORIGINATION_FEES": {
        "kind": "checkbox",
        "label": "Pre-2004 Origination Fees"
      },
      "BORROWER_ACCOUNT_NUMBER": {
        "kind": "text",
        "label": "Borrower Account Number"
      },
      "FILER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Filer Name"
      },
      "FILER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Filer Address 1"
      },
      "FILER_ADDRESS2": {
        "kind": "text",
        "label": "Filer Address 2"
      },
      "FILER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Filer City"
      },
      "FILER_STATE": {
        "kind": "text",
        "required": true,
        "label": "Filer State"
      },
      "FILER_ZIP": {
        "kind": "text",
        "required": true,
        "label": "Filer ZIP"
      },
      "FILER_PHONE": {
        "kind": "text",
        "required": true,
        "label": "Filer Phone"
      },
      "FILER_FEDERAL_ID_NUMBER": {
        "kind": "text",
        "required": true,
        "label": "Filer Federal ID Number"
      },
      "createdBy": {
        "kind": "text"
      },
      "userId": {
        "kind": "text"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "1098-T": {
      "SITE_ID": {
        "kind": "text",
        "required": true,
        "label": "Site ID"
      },
      "FILER_FEDERAL_ID_NUMBER": {
        "kind": "text",
        "required": true,
        "label": "Filer Federal ID Number"
      },
      "FILER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Filer Name"
      },
      "FILER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Filer Address 1"
      },
      "FILER_ADDRESS2": {
        "kind": "text",
        "label": "Filer Address 2"
      },
      "FILER_ADDRESS3": {
        "kind": "text",
        "label": "Filer Address 3"
      },
      "FILER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Filer City"
      },
      "FILER_STATE": {
        "kind": "state",
        "required": true,
        "label": "Filer State"
      },
      "FILER_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Filer ZIP"
      },
      "JOB_NUMBER": {
        "kind": "text",
        "label": "Job Number"
      },
      "FILER_PHONE": {
        "kind": "text",
        "required": true,
        "label": "Filer Phone"
      },
      "STUDENT_SSN": {
        "kind": "tin",
        "required": true,
        "label": "Student SSN"
      },
      "STUDENT_FIRST_NAME": {
        "kind": "text",
        "required": true,
        "label": "Student First Name"
      },
      "STUDENT_MIDDLE_NAME": {
        "kind": "text",
        "label": "Student Middle Name"
      },
      "STUDENT_LAST_NAME": {
        "kind": "text",
        "required": true,
        "label": "Student Last Name"
      },
      "STUDENT_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Student Address 1"
      },
      "STUDENT_ADDRESS2": {
        "kind": "text",
        "label": "Student Address 2"
      },
      "STUDENT_CITY": {
        "kind": "text",
        "required": true,
        "label": "Student City"
      },
      "STUDENT_STATE": {
        "kind": "state",
        "required": true,
        "label": "Student State"
      },
      "STUDENT_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Student ZIP"
      },
      "FOREIGN_COUNTRY_INDICATOR": {
        "kind": "checkbox",
        "label": "Foreign Country Indicator"
      },
      "FOREIGN_COUNTRY": {
        "kind": "text",
        "label": "Foreign Country"
      },
      "EMAIL": {
        "kind": "text",
        "label": "Email"
      },
      "SCHOOL_STUDENT_ID": {
        "kind": "text",
        "label": "School Student ID"
      },
      "BOX1_PAYMENTS_RECEIVED": {
        "kind": "amount",
        "label": "Payments Received"
      },
      "BOX4_ADJ_MADE_PRIOR_YR": {
        "kind": "amount",
        "label": "Adjustments Made Prior Year"
      },
      "BOX5_SCHOLARSHIP_GRANTS": {
        "kind": "amount",
        "label": "Scholarships or Grants"
      },
      "BOX6_ADJ_SCHOLARSHIP_PRIOR_YR": {
        "kind": "amount",
        "label": "Adjustments to Scholarships or Grants for Prior Year"
      },
      "BOX7_ACADEMIC_PERIOD_IND": {
        "kind": "checkbox",
        "label": "Academic Period Indicator"
      },
      "BOX8_HALF_TIME_STUDENT_IND": {
        "kind": "checkbox",
        "label": "Half-time Student Indicator"
      },
      "BOX9_GRADUATE_STUDENT_IND": {
        "kind": "checkbox",
        "label": "Graduate Student Indicator"
      },
      "BOX10_REIMBURSEMENT_OR_REFUND": {
        "kind": "amount",
        "label": "Reimbursement or Refund"
      },
      "SSN_SOLICITED": {
        "kind": "text",
        "label": "SSN Solicited"
      },
      "1098_YEAR": {
        "kind": "amount",
        "required": true,
        "label": "1098 Year"
      },
      "SSN/TIN": {
        "kind": "tin",
        "required": true,
        "label": "SSN/TIN"
      },
      "TOKEN": {
        "kind": "text",
        "label": "Token"
      },
      "STUDENT_CONSENT_DATE": {
        "kind": "text",
        "label": "Student Consent Date"
      },
      "createdBy": {
        "kind": "text"
      },
      "userId": {
        "kind": "text"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "1099-B": {
      "SITE_ID": {
        "kind": "text",
        "required": true,
        "label": "Site ID"
      },
      "FILER_FEDERAL_ID_NUMBER": {
        "kind": "tin",
        "required": true,
        "label": "Filer Federal ID Number"
      },
      "FILER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Filer Name"
      },
      "FILER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Filer Address 1"
      },
      "FILER_ADDRESS2": {
        "kind": "text",
        "label": "Filer Address 2"
      },
      "FILER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Filer City"
      },
      "FILER_STATE": {
        "kind": "state",
        "required": true,
        "label": "Filer State"
      },
      "FILER_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Filer ZIP"
      },
      "FILER_PHONE": {
        "kind": "text",
        "required": true,
        "label": "Filer Phone"
      },
      "RECIPIENT_ID_NUMBER": {
        "kind": "tin",
        "required": true,
        "label": "Recipient ID Number"
      },
      "RECIPIENT_NAME1": {
        "kind": "text",
        "required": true,
        "label": "Recipient Name 1"
      },
      "RECIPIENT_NAME2": {
        "kind": "text",
        "required": true,
        "label": "Recipient Name 2"
      },
      "RECIPIENT_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Recipient Address 1"
      },
      "RECIPIENT_ADDRESS2": {
        "kind": "text",
        "label": "Recipient Address 2"
      },
      "RECIPIENT_CITY": {
        "kind": "text",
        "required": true,
        "label": "Recipient City"
      },
      "RECIPIENT_STATE": {
        "kind": "state",
        "required": true,
        "label": "Recipient State"
      },
      "RECIPIENT_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Recipient ZIP"
      },
      "FOREIGN_ADDRESS_INDICATOR": {
        "kind": "checkbox",
        "label": "Foreign Address Indicator"
      },
      "FOREIGN_CITY_PROVINCE_POSTAL": {
        "kind": "text",
        "label": "Foreign City"
      },
      "ACCOUNT_NUMBER": {
        "kind": "text",
        "label": "Account Number"
      },
      "TIN_NOTICE_INDICATOR": {
        "kind": "checkbox",
        "label": "TIN Notice Indicator"
      },
      "CUSIP_NUMBER": {
        "kind": "text",
        "label": "CUSIP Number"
      },
      "FATCA_REQUIREMENT": {
        "kind": "text",
        "label": "FATCA Requirement"
      },
      "FORM_8949_CHECKBOX": {
        "kind": "text",
        "label": "Form 8949 Checkbox"
      },
      "BOX_1A_DESCRIPTION": {
        "kind": "text",
        "label": "Description of property (Example: 100 sh. XYZ Co.)"
      },
      "BOX_1B_DATE_ACQUIRED": {
        "kind": "text",
        "label": "Date Acquired"
      },
      "BOX_1C_DATE_SOLD": {
        "kind": "text",
        "label": "Date Sold or Disposed"
      },
      "BOX_1D_PROCEEDS": {
        "kind": "amount",
        "decimal": true,
        "label": "Proceeds"
      },
      "BOX_1E_COST_BASIS": {
        "kind": "amount",
        "decimal": true,
        "label": "Cost or Other Basis"
      },
      "BOX_1F_MARKET_DISCOUNT": {
        "kind": "amount",
        "decimal": true,
        "label": "Market Discount"
      },
      "BOX_1G_WASH_SALE_LOSS": {
        "kind": "amount",
        "decimal": true,
        "label": "Wash Sale Loss Disallowed"
      },
      "BOX_2_SHORT_TERM_INDICATOR": {
        "kind": "checkbox",
        "label": "Short-term transaction indicator"
      },
      "BOX_2_LONG_TERM_INDICATOR": {
        "kind": "checkbox",
        "label": "Long-term transaction indicator"
      },
      "BOX_2_ORDINARY_INDICATOR": {
        "kind": "checkbox",
        "label": "Ordinary transaction indicator"
      },
      "BOX_3_PROCEEDS_COLLECTIBLES": {
        "kind": "amount",
        "decimal": true,
        "label": "Proceeds from Collectibles"
      },
      "BOX_3_PROCEEDS_QOF": {
        "kind": "amount",
        "decimal": true,
        "label": "Proceeds from QOF"
      },
      "BOX_4_FEDERAL_TAX_WITHHELD": {
        "kind": "amount",
        "decimal": true,
        "label": "Federal Income Tax Withheld"
      },
      "BOX_5_NONCOVERED_SECURITY_INDICATOR": {
        "kind": "checkbox",
        "label": "Noncovered Security"
      },
      "BOX_6_GROSS_PROCEEDS_INDICATOR": {
        "kind": "checkbox",
        "label": "Gross Proceeds"
      },
      "BOX_6_NET_PROCEEDS_INDICATOR": {
        "kind": "checkbox",
        "label": "Net Proceeds"
      },
      "BOX_7_LOSS_NOT_ALLOWED_INDICATOR": {
        "kind": "checkbox",
        "label": "Loss is not allowed based on amount in 1d"
      },
      "BOX_8_PROFIT_LOSS_2019": {
        "kind": "amount",
        "decimal": true,
        "label": "Profit or (loss) realized in 2019"
      },
      "BOX_9_UNREALIZED_PL_2018": {
        "kind": "amount",
        "decimal": true,
        "label": "Unrealized profit or (loss) on open contracts—12/31/2018"
      },
      "BOX_10_UNREALIZED_PL_2019": {
        "kind": "amount",
        "decimal": true,
        "label": "Unrealized profit or (loss) on open contracts—12/31/2019"
      },
      "BOX_11_AGGREGATE_PL": {
        "kind": "amount",
        "decimal": true,
        "label": "Aggregate profit or (loss) on contracts"
      },
      "BOX_12_PROCEEDS_COLLECTIBLES_INDICATOR": {
        "kind": "checkbox",
        "label": "If checked"
      },
      "BOX_13_BARTERING": {
        "kind": "amount",
        "decimal": true,
        "label": "Bartering"
      },
      "BOX_14_STATE_NAME1": {
        "kind": "state",
        "required": true,
        "label": "State Name 1"
      },
      "BOX_15_STATE_ID1": {
        "kind": "text",
        "label": "State ID Number 1"
      },
      "BOX_16_STATE_TAX_WITHHELD1": {
        "kind": "amount",
        "decimal": true,
        "label": "State Tax Withheld 1"
      },
      "BOX_14_STATE_NAME2": {
        "kind": "state",
        "required": true,
        "label": "State Name 2"
      },
      "BOX_15_STATE_ID2": {
        "kind": "text",
        "label": "State ID Number 2"
      },
      "BOX_16_STATE_TAX_WITHHELD2": {
        "kind": "amount",
        "decimal": true,
        "label": "State Tax Withheld 2"
      }
    },
    "1099-C": {
      "JOB_NUMBER": {
        "kind": "text",
        "label": "Job Number"
      },
      "SITE_ID": {
        "kind": "text",
        "required": true,
        "label": "Site ID"
      },
      "FILER_FEDERAL_ID_NUMBER": {
        "kind": "text",
        "required": true,
        "label": "Filer Federal ID Number"
      },
      "FILER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Filer Name"
      },
      "FILER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Filer Address 1"
      },
      "FILER_ADDRESS2": {
        "kind": "text",
        "label": "Filer Address 2"
      },
      "FILER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Filer City"
      },
      "FILER_STATE": {
        "kind": "state",
        "required": true,
        "label": "Filer State"
      },
      "FILER_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Filer ZIP"
      },
      "FILER_PHONE": {
        "kind": "text",
        "required": true,
        "label": "Filer Phone"
      },
      "RECIPIENT_ID_NUMBER": {
        "kind": "text",
        "required": true,
        "label": "Recipient ID Number"
      },
      "RECIPIENT_NAME": {
        "kind": "text",
        "required": true,
        "label": "Recipient Name"
      },
      "RECIPIENT_NAME2": {
        "kind": "text",
        "label": "Recipient Name 2"
      },
      "RECIPIENT_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Recipient Address 1"
      },
      "RECIPIENT_ADDRESS2": {
        "kind": "text",
        "label": "Recipient Address 2"
      },
      "RECIPIENT_ADDRESS3": {
        "kind": "text",
        "label": "Recipient Address 3"
      },
      "RECIPIENT_CITY": {
        "kind": "text",
        "required": true,
        "label": "Recipient City"
      },
      "RECIPIENT_STATE": {
        "kind": "state",
        "required": true,
        "label": "Recipient State"
      },
      "RECIPIENT_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Recipient ZIP"
      },
      "RECIPIENT_EMAIL": {
        "kind": "text",
        "label": "Recipient Email"
      },
      "ACCOUNT_NUMBER": {
        "kind": "text",
        "label": "Account Number"
      },
      "BOX1_DATE_OF_IDENTIFIABLE_EVENT": {
        "kind": "date",
        "label": "Date of Identifiable Event"
      },
      "BOX2_AMT_OF_DEBT_DISCHARGED": {
        "kind": "amount",
        "label": "Amount of Debt Discharged"
      },
      "BOX3_INTEREST_INCL_IN_BOX2": {
        "kind": "amount",
        "label": "Interest Included in Box 2"
      },
      "BOX4_DEBT_DESCRIPTION": {
        "kind": "text",
        "label": "Debt Description"
      },
      "BOX5_BORROWER_LIABLE_YES_IND": {
        "kind": "checkbox",
        "label": "Borrower Liable Yes Indicator"
      },
      "BOX5_BORROWER_LIABLE_NO_IND": {
        "kind": "checkbox",
        "label": "Borrower Liable No Indicator"
      },
      "BOX6_IDENTIFIABLE_EVENT_CODE": {
        "kind": "text",
        "label": "Identifiable Event Code"
      },
      "BOX7_FAIR_MARKET_VALUE": {
        "kind": "amount",
        "label": "Fair Market Value"
      },
      "1099_YEAR": {
        "kind": "text",
        "required": true,
        "label": "1099 Year"
      },
      "SSN/TIN": {
        "kind": "tin",
        "required": true,
        "label": "SSN/TIN"
      },
      "FOREIGN_COUNTRY_INDICATOR": {
        "kind": "checkbox",
        "label": "Foreign Country Indicator"
      },
      "FOREIGN_COUNTRY": {
        "kind": "text",
        "label": "Foreign Country"
      },
      "createdBy": {
        "kind": "text"
      },
      "userId": {
        "kind": "text"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "1099-DIV": {
      "JOB_NUMBER": {
        "kind": "text",
        "label": "Job Number"
      },
      "SITE_ID": {
        "kind": "text",
        "required": true,
        "label": "Site ID"
      },
      "FILER_FEDERAL_ID_NUMBER": {
        "kind": "text",
        "required": true,
        "label": "Filer Federal ID Number"
      },
      "FILER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Filer Name"
      },
      "FILER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Filer Address 1"
      },
      "FILER_ADDRESS2": {
        "kind": "text",
        "label": "Filer Address 2"
      },
      "FILER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Filer City"
      },
      "FILER_STATE": {
        "kind": "state",
        "required": true,
        "label": "Filer State"
      },
      "FILER_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Filer ZIP"
      },
      "FILER_PHONE": {
        "kind": "text",
        "required": true,
        "label": "Filer Phone"
      },
      "RECIPIENT_ID_NUMBER": {
        "kind": "text",
        "required": true,
        "label": "Recipient ID Number"
      },
      "RECIPIENT_NAME": {
        "kind": "text",
        "required": true,
        "label": "Recipient Name"
      },
      "RECIPIENT_NAME2": {
        "kind": "text",
        "label": "Recipient Name 2"
      },
      "RECIPIENT_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Recipient Address 1"
      },
      "RECIPIENT_ADDRESS2": {
        "kind": "text",
        "label": "Recipient Address 2"
      },
      "RECIPIENT_CITY": {
        "kind": "text",
        "required": true,
        "label": "Recipient City"
      },
      "RECIPIENT_STATE": {
        "kind": "state",
        "required": true,
        "label": "Recipient State"
      },
      "RECIPIENT_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Recipient ZIP"
      },
      "RECIPIENT_EMAIL": {
        "kind": "text",
        "label": "Recipient Email"
      },
      "FOREIGN_COUNTRY_INDICATOR": {
        "kind": "checkbox",
        "label": "Foreign Country Indicator"
      },
      "FOREIGN_COUNTRY": {
        "kind": "text",
        "label": "Foreign Country"
      },
      "ACCOUNT_NUMBER": {
        "kind": "text",
        "label": "Account Number"
      },
      "BOX1A_TOTAL_ORDINARY_DIVIDENDS": {
        "kind": "amount",
        "label": "Total Ordinary Dividends"
      },
      "BOX1B_QUALIFIED_DIVIDENDS": {
        "kind": "amount",
        "label": "Qualified Dividends"
      },
      "BOX2A_TOTAL_CAPITAL_GAIN_DISTR": {
        "kind": "amount",
        "label": "Total Capital Gain Distributions"
      },
      "BOX2B_UNRECAP_SEC_1250_GAIN": {
        "kind": "amount",
        "label": "Unrecaptured Section 1250 Gain"
      },
      "BOX2C_SECTION_1202_GAIN": {
        "kind": "amount",
        "label": "Section 1202 Gain"
      },
      "BOX2D_COLLECTIBLES_GAIN": {
        "kind": "amount",
        "label": "Collectibles Gain"
      },
      "BOX2E_SECTION_897_ORD_DIV": {
        "kind": "amount",
        "label": "Section 897 Ordinary Dividends"
      },
      "BOX2F_SECTION_897_CAP_GAIN": {
        "kind": "amount",
        "label": "Section 897 Capital Gain"
      },
      "BOX3_NONDIVIDEND_DISTRIBUTIONS": {
        "kind": "amount",
        "label": "Nondividend Distributions"
      },
      "BOX4_FEDERAL_INCOME_TAX_WITHHELD": {
        "kind": "amount",
        "label": "Federal Income Tax Withheld"
      },
      "BOX5_SECTION_199A_DIVIDENDS": {
        "kind": "amount",
        "label": "Section 199A Dividends"
      },
      "BOX6_INVESTMENT_EXPENSES": {
        "kind": "amount",
        "label": "Investment Expenses"
      },
      "BOX7_FOREIGN_TAX_PAID": {
        "kind": "amount",
        "label": "Foreign Tax Paid"
      },
      "BOX8_FOREIGN_COUNTRY": {
        "kind": "text",
        "label": "Foreign Country"
      },
      "BOX9_CASH_LIQUIDATION_DISTRIBUTIONS": {
        "kind": "amount",
        "label": "Cash Liquidation Distributions"
      },
      "BOX10_NONCASH_LIQUIDATION_DISTRIBUTIONS": {
        "kind": "amount",
        "label": "Noncash Liquidation Distributions"
      },
      "BOX12_EXEMPT_INTEREST_DIVIDENDS": {
        "kind": "amount",
        "label": "Exempt Interest Dividends"
      },
      "BOX13_SPECIFIED_PRIVATE_ACTIVITY_BOND": {
        "kind": "amount",
        "label": "Specified Private Activity Bond"
      },
      "BOX14_STATE1": {
        "kind": "text",
        "label": "State 1"
      },
      "BOX14_STATE2": {
        "kind": "text",
        "label": "State 2"
      },
      "BOX15_STATE_IDENTIFICATION_NO1": {
        "kind": "text",
        "label": "State Identification No 1"
      },
      "BOX15_STATE_IDENTIFICATION_NO2": {
        "kind": "text",
        "label": "State Identification No 2"
      },
      "BOX16_STATE_TAX_WITHHELD1": {
        "kind": "amount",
        "label": "State Tax Withheld 1"
      },
      "BOX16_STATE_TAX_WITHHELD2": {
        "kind": "amount",
        "label": "State Tax Withheld 2"
      },
      "BOX11_FATCA_REQUIREMENT": {
        "kind": "checkbox",
        "label": "FATCA Requirement"
      },
      "BOX_2ND_TIN_NOT_IND": {
        "kind": "checkbox",
        "label": "2nd TIN Not Indicator"
      },
      "1099_YEAR": {
        "kind": "text",
        "required": true,
        "label": "1099 Year"
      },
      "createdBy": {
        "kind": "text"
      },
      "userId": {
        "kind": "text"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "1099-INT": {
      "RECIPIENT_ID_NUMBER": {
        "kind": "tin",
        "required": true,
        "label": "Recipient ID Number"
      },
      "RECIPIENT_NAME": {
        "kind": "text",
        "required": true,
        "label": "Recipient Name"
      },
      "RECIPIENT_NAME2": {
        "kind": "text",
        "label": "Recipient Name 2"
      },
      "RECIPIENT_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Recipient Address 1"
      },
      "RECIPIENT_ADDRESS2": {
        "kind": "text",
        "label": "Recipient Address 2"
      },
      "RECIPIENT_ADDRESS3": {
        "kind": "text",
        "label": "Recipient Address 3"
      },
      "RECIPIENT_CITY": {
        "kind": "text",
        "required": true,
        "label": "Recipient City"
      },
      "RECIPIENT_STATE": {
        "kind": "state",
        "required": true,
        "label": "Recipient State"
      },
      "RECIPIENT_ZIP": {
        "kind": "text",
        "required": true,
        "label": "Recipient ZIP"
      },
      "FOREIGN_COUNTRY_INDICATOR": {
        "kind": "checkbox",
        "label": "Foreign Country Indicator"
      },
      "FOREIGN_COUNTRY": {
        "kind": "text",
        "label": "Foreign Country"
      },
      "ACCOUNT_NUMBER": {
        "kind": "text",
        "label": "Account Number"
      },
      "BOX1_INTEREST_INCOME_NOT_IN_BOX3": {
        "kind": "amount",
        "label": "Interest Income Not in Box 3"
      },
      "BOX2_EARLY_WITHDRAWAL_PENALTY": {
        "kind": "amount",
        "label": "Early Withdrawal Penalty"
      },
      "BOX3_INTEREST_ON_US_SAVINGS": {
        "kind": "amount",
        "label": "Interest on US Savings"
      },
      "BOX4_FED_INCOME_TAX_WITHHELD": {
        "kind": "amount",
        "label": "Federal Income Tax Withheld"
      },
      "BOX5_INVESTMENT_EXPENSES": {
        "kind": "amount",
        "label": "Investment Expenses"
      },
      "BOX6_FOREIGN_TAX_PAID": {
        "kind": "amount",
        "label": "Foreign Tax Paid"
      },
      "BOX7_FOREIGN_COUNTRY_US_POSSESSION": {
        "kind": "text",
        "label": "Foreign Country US Possession"
      },
      "BOX8_TAX_EXEMPT_INTEREST": {
        "kind": "amount",
        "label": "Tax-Exempt Interest"
      },
      "BOX9_SPEC_PRIVATE_ACT_BOND_INT": {
        "kind": "amount",
        "label": "Specified Private Activity Bond Interest"
      },
      "BOX10_MARKET_DISCOUNT_BOND": {
        "kind": "amount",
        "label": "Market Discount Bond"
      },
      "BOX11_BOND_PREMIUM": {
        "kind": "amount",
        "label": "Bond Premium"
      },
      "BOX12_BOND_PREMIUM_ON_TREASURY": {
        "kind": "amount",
        "label": "Bond Premium on Treasury"
      },
      "BOX13_TAX_EXEMPT_BOND": {
        "kind": "amount",
        "label": "Tax-Exempt Bond"
      },
      "BOX14_TAX_EXEMPT_BOND_CUSIP": {
        "kind": "text",
        "label": "Tax-Exempt Bond CUSIP"
      },
      "BOX15_STATE": {
        "kind": "text",
        "label": "State"
      },
      "BOX15_STATE2": {
        "kind": "text",
        "label": "State 2"
      },
      "BOX16_STATE_ID": {
        "kind": "text",
        "label": "State ID"
      },
      "BOX16_STATE_ID2": {
        "kind": "text",
        "label": "State ID 2"
      },
      "BOX17_STATE_TAX_WITHHELD": {
        "kind": "amount",
        "decimal": true,
        "label": "State Tax Withheld"
      },
      "BOX17_STATE_TAX_WITHHELD2": {
        "kind": "amount",
        "label": "State Tax Withheld 2"
      },
      "FATCA_REQUIREMENT": {
        "kind": "checkbox",
        "label": "FATCA Requirement"
      },
      "1099_YEAR": {
        "kind": "text",
        "label": "1099 Year"
      },
      "SITE_ID": {
        "kind": "text",
        "label": "Site ID"
      },
      "FILER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Filer Name"
      },
      "FILER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Filer Address 1"
      },
      "FILER_ADDRESS2": {
        "kind": "text",
        "label": "Filer Address 2"
      },
      "JOB_NUMBER": {
        "kind": "text",
        "label": "Job Number"
      },
      "FILER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Filer City"
      },
      "FILER_STATE": {
        "kind": "state",
        "required": true,
        "label": "Filer State"
      },
      "FILER_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Filer ZIP"
      },
      "FILER_PHONE": {
        "kind": "text",
        "required": true,
        "label": "Filer Phone"
      },
      "FILER_FEDERAL_ID_NUMBER": {
        "kind": "text",
        "label": "Filer Federal ID Number"
      },
      "BOX_2ND_TIN_NOT_IND": {
        "kind": "checkbox",
        "label": "2nd TIN Not IND"
      },
      "createdBy": {
        "kind": "text"
      },
      "userId": {
        "kind": "text"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "1099-K": {
      "SITE_ID": {
        "kind": "text",
        "label": "Site ID"
      },
      "1099_YEAR": {
        "kind": "text",
        "label": "1099 Year"
      },
      "JOB_NUMBER": {
        "kind": "text",
        "label": "Job Number"
      },
      "FILER_FEDERAL_ID_NUMBER": {
        "kind": "text",
        "required": true,
        "label": "Filer Federal ID Number"
      },
      "FILER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Filer Name"
      },
      "FILER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Filer Address 1"
      },
      "FILER_ADDRESS2": {
        "kind": "text",
        "label": "Filer Address 2"
      },
      "FILER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Filer City"
      },
      "FILER_STATE": {
        "kind": "state",
        "required": true,
        "label": "Filer State"
      },
      "FILER_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Filer ZIP Code"
      },
      "FILER_PHONE": {
        "kind": "text",
        "required": true,
        "label": "Filer Phone"
      },
      "PAYEE_TIN": {
        "kind": "text",
        "required": true,
        "label": "Payee TIN"
      },
      "PAYEE_NAME": {
        "kind": "text",
        "required": true,
        "label": "Payee Name"
      },
      "PAYEE_NAME2": {
        "kind": "text",
        "label": "Payee Name 2"
      },
      "PAYEE_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Payee Address 1"
      },
      "PAYEE_ADDRESS2": {
        "kind": "text",
        "label": "Payee Address 2"
      },
      "PAYEE_CITY": {
        "kind": "text",
        "required": true,
        "label": "Payee City"
      },
      "PAYEE_STATE": {
        "kind": "state",
        "required": true,
        "label": "Payee State"
      },
      "PAYEE_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Payee ZIP Code"
      },
      "TYPE_OF_PAYMENT_INDICATOR_PAYMENT_CARD": {
        "kind": "checkbox",
        "label": "Type of Payment Indicator (Payment Card)"
      },
      "TYPE_OF_PAYMENT_INDICATOR_THIRD_PARTY": {
        "kind": "checkbox",
        "label": "Type of Payment Indicator (Third Party)"
      },
      "TYPE_OF_FILER_INDICATOR_PSE": {
        "kind": "checkbox",
        "label": "Type of Filer Indicator (PSE)"
      },
      "TYPE_OF_FILER_INDICATOR_3RD_PARTY": {
        "kind": "checkbox",
        "label": "Type of Filer Indicator (3rd Party)"
      },
      "BOX1A_GROSS_AMT_PAYMENT_CARD_TPN_TRANS": {
        "kind": "amount",
        "label": "Box 1a Gross Amount Payment Card/TPN Transactions"
      },
      "BOX1B_CARD_NOT_PRESENT_TRANS": {
        "kind": "amount",
        "label": "Box 1b Card Not Present Transactions"
      },
      "BOX2_MERCHANT_CAT_CODE": {
        "kind": "text",
        "label": "Box 2 Merchant Category Code"
      },
      "BOX3_NO_PAYMENT_TRANSACTIONS": {
        "kind": "amount",
        "label": "Box 3 Number of Payment Transactions"
      },
      "BOX4_FED_INCOME_TAX_WTHLD": {
        "kind": "amount",
        "label": "Box 4 Federal Income Tax Withheld"
      },
      "BOX5A_JANUARY": {
        "kind": "amount",
        "label": "Box 5a January"
      },
      "BOX5B_FEBRUARY": {
        "kind": "amount",
        "label": "Box 5b February"
      },
      "BOX5C_MARCH": {
        "kind": "amount",
        "label": "Box 5c March"
      },
      "BOX5D_APRIL": {
        "kind": "amount",
        "label": "Box 5d April"
      },
      "BOX5E_MAY": {
        "kind": "amount",
        "label": "Box 5e May"
      },
      "BOX5F_JUNE": {
        "kind": "amount",
        "label": "Box 5f June"
      },
      "BOX5G_JULY": {
        "kind": "amount",
        "label": "Box 5g July"
      },
      "BOX5H_AUGUST": {
        "kind": "amount",
        "label": "Box 5h August"
      },
      "BOX5I_SEPTEMBER": {
        "kind": "amount",
        "label": "Box 5i September"
      },
      "BOX5J_OCTOBER": {
        "kind": "amount",
        "label": "Box 5j October"
      },
      "BOX5K_NOVEMBER": {
        "kind": "amount",
        "label": "Box 5k November"
      },
      "BOX5L_DECEMBER": {
        "kind": "amount",
        "label": "Box 5l December"
      },
      "BOX6A_STATE": {
        "kind": "text",
        "label": "Box 6A State"
      },
      "BOX6B_STATE": {
        "kind": "text",
        "label": "Box 6B State"
      },
      "BOX7A_STATE_ID_NO": {
        "kind": "text",
        "label": "Box 7A State ID Number"
      },
      "BOX7B_STATE_ID_NO": {
        "kind": "text",
        "label": "Box 7B State ID Number"
      },
      "BOX8A_STATE_TAX": {
        "kind": "amount",
        "label": "Box 8A State Income Tax Withheld"
      },
      "BOX8B_STATE_TAX": {
        "kind": "amount",
        "label": "Box 8B State Income Tax Withheld"
      },
      "PSE_NAME": {
        "kind": "text",
        "required": true,
        "label": "PSE Name"
      },
      "PSE_TELEPHONE": {
        "kind": "text",
        "required": true,
        "label": "PSE Telephone"
      },
      "PAYEE_INTERNAL_ACCT_NO": {
        "kind": "text",
        "label": "Payee Internal Account Number"
      },
      "createdBy": {
        "kind": "text"
      },
      "userId": {
        "kind": "text"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "1099-LTC": {
      "SITE_ID": {
        "kind": "text",
        "label": "Site ID"
      },
      "POLICYHOLDER_TAX_ID": {
        "kind": "tin",
        "required": true,
        "label": "Policyholder Tax ID"
      },
      "POLICYHOLDER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Policyholder Name"
      },
      "POLICYHOLDER_STREET_ADDRESS_1": {
        "kind": "text",
        "required": true,
        "label": "Policyholder Street Address 1"
      },
      "POLICYHOLDER_STREET_ADDRESS_2": {
        "kind": "text",
        "label": "Policyholder Street Address 2"
      },
      "POLICYHOLDER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Policyholder City"
      },
      "POLICYHOLDER_STATE": {
        "kind": "state",
        "required": true,
        "label": "Policyholder State"
      },
      "POLICYHOLDER_ZIP_CODE": {
        "kind": "zip",
        "required": true,
        "label": "Policyholder Zip Code"
      },
      "INSURED_SSN": {
        "kind": "tin",
        "required": true,
        "label": "Insured SSN"
      },
      "INSURED_NAME": {
        "kind": "text",
        "required": true,
        "label": "Insured Name"
      },
      "INSURED_STREET_ADDRESS_1": {
        "kind": "text",
        "required": true,
        "label": "Insured Street Address 1"
      },
      "INSURED_STREET_ADDRESS_2": {
        "kind": "text",
        "label": "Insured Street Address 2"
      },
      "INSURED_CITY": {
        "kind": "text",
        "required": true,
        "label": "Insured City"
      },
      "INSURED_STATE": {
        "kind": "state",
        "required": true,
        "label": "Insured State"
      },
      "INSURED_ZIP_CODE": {
        "kind": "zip",
        "required": true,
        "label": "Insured Zip Code"
      },
      "INSURED_ACCOUNT_NUMBER": {
        "kind": "text",
        "required": true,
        "label": "Insured Account Number"
      },
      "MAILING_ADDRESS_INDICATOR": {
        "kind": "text",
        "label": "Mailing Address Indicator"
      },
      "BOX1_GROSS_BENEFITS_PAID": {
        "kind": "amount",
        "label": "Box 1 Gross Benefits Paid"
      },
      "BOX2_ACCELERATED_DEATH_BENEFITS_PAID": {
        "kind": "amount",
        "label": "Box 2 Accelerated Death Benefits Paid"
      },
      "PAYMENT_TYPE": {
        "kind": "amount",
        "label": "Payment Type"
      },
      "QUALIFIED_CONTRACT": {
        "kind": "checkbox",
        "label": "Qualified Contract"
      },
      "CHRONICALLY_ILL": {
        "kind": "checkbox",
        "label": "Chronically Ill or Terminal"
      },
      "PAYER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Payer Name"
      },
      "PAYER_STREET_ADDRESS_1": {
        "kind": "text",
        "required": true,
        "label": "Payer Street Address 1"
      },
      "PAYER_STREET_ADDRESS_2": {
        "kind": "text",
        "label": "Payer Street Address 2"
      },
      "FOREIGN_COUNTRY": {
        "kind": "text",
        "label": "Foreign Country"
      },
      "FOREIGN_COUNTRY_INDICATOR": {
        "kind": "checkbox",
        "label": "Foreign Country Indicator"
      },
      "PAYER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Payer City"
      },
      "PAYER_STATE": {
        "kind": "state",
        "required": true,
        "label": "Payer State"
      },
      "PAYER_ZIP_CODE": {
        "kind": "zip",
        "required": true,
        "label": "Payer Zip Code"
      },
      "JOB_NUMBER": {
        "kind": "text",
        "label": "Job Number"
      },
      "PAYER_PHONE": {
        "kind": "text",
        "label": "Payer Phone"
      },
      "PAYERS_TIN": {
        "kind": "tin",
        "required": true,
        "label": "Payer's TIN"
      },
      "1099_YEAR": {
        "kind": "text",
        "label": "1099 Year"
      },
      "DATE_CERTIFIED": {
        "kind": "text",
        "label": "Date Certified"
      },
      "REIMBURSED_AMOUNT": {
        "kind": "amount",
        "decimal": true,
        "label": "Reimbursed Amount"
      },
      "TERMINAL_ILL": {
        "kind": "checkbox",
        "label": "Terminally Ill"
      },
      "PER_DIEM": {
        "kind": "amount",
        "decimal": true,
        "label": "Per Diem"
      },
      "createdBy": {
        "kind": "text"
      },
      "userId": {
        "kind": "text"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "1099-NEC": {
      "1099_YEAR": {
        "kind": "amount",
        "required": true,
        "label": "1099 Year"
      },
      "JOB_NUMBER": {
        "kind": "text",
        "label": "Job Number"
      },
      "ACCOUNT_NUMBER": {
        "kind": "text",
        "label": "Account Number"
      },
      "BOX1_NONEMPLOYEE_COMPENSATION": {
        "kind": "amount",
        "required": true,
        "label": "Nonemployee Compensation"
      },
      "BOX2_DIRECT_SALE": {
        "kind": "checkbox",
        "label": "Direct Sale"
      },
      "BOX3_GOLDEN_PARACHUTE": {
        "kind": "amount",
        "required": true,
        "label": "Golden Parachute"
      },
      "BOX4_FED_INCOME_TAX_WITHHELD": {
        "kind": "amount",
        "required": true,
        "label": "Federal Income Tax Withheld"
      },
      "BOX5_STATE_TAX_WITHHELD1": {
        "kind": "amount",
        "required": true,
        "label": "State Tax Withheld 1"
      },
      "BOX5_STATE_TAX_WITHHELD2": {
        "kind": "amount",
        "label": "State Tax Withheld 2"
      },
      "BOX6_PAYER_STATE_NUMBER1": {
        "kind": "state",
        "required": true,
        "label": "Payer State Number 1"
      },
      "BOX6_PAYER_STATE_NUMBER2": {
        "kind": "state",
        "required": true,
        "label": "Payer State Number 2"
      },
      "BOX7_STATE_INCOME1": {
        "kind": "amount",
        "label": "State Income 1"
      },
      "BOX7_STATE_INCOME2": {
        "kind": "amount",
        "label": "State Income 2"
      },
      "BOX_2ND_TIN_NOT_IND": {
        "kind": "checkbox",
        "label": "2nd TIN not Indicator"
      },
      "FATCA_REQUIREMENT": {
        "kind": "checkbox",
        "label": "FATCA Requirement"
      },
      "FILER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Filer Address 1"
      },
      "FILER_ADDRESS2": {
        "kind": "text",
        "label": "Filer Address 2"
      },
      "FILER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Filer City"
      },
      "FILER_FEDERAL_ID_NUMBER": {
        "kind": "tin",
        "required": true,
        "label": "Filer Federal ID Number"
      },
      "FILER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Filer Name"
      },
      "FILER_PHONE": {
        "kind": "text",
        "required": true,
        "label": "Filer Phone"
      },
      "FILER_STATE": {
        "kind": "text",
        "required": true,
        "label": "Filer State"
      },
      "FILER_ZIP": {
        "kind": "text",
        "required": true,
        "label": "Filer ZIP"
      },
      "FOREIGN_COUNTRY": {
        "kind": "text",
        "label": "Foreign Country"
      },
      "FOREIGN_COUNTRY_INDICATOR": {
        "kind": "checkbox",
        "label": "Foreign Country Indicator"
      },
      "RECIPIENT_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Recipient Address 1"
      },
      "RECIPIENT_ADDRESS2": {
        "kind": "text",
        "label": "Recipient Address 2"
      },
      "RECIPIENT_ADDRESS3": {
        "kind": "text",
        "label": "Recipient Address 3"
      },
      "RECIPIENT_CITY": {
        "kind": "text",
        "required": true,
        "label": "Recipient City"
      },
      "RECIPIENT_EMAIL": {
        "kind": "text",
        "label": "Recipient Email"
      },
      "RECIPIENT_ID_NUMBER": {
        "kind": "text",
        "required": true,
        "label": "Recipient ID Number"
      },
      "RECIPIENT_NAME": {
        "kind": "text",
        "required": true,
        "label": "Recipient Name"
      },
      "RECIPIENT_NAME2": {
        "kind": "text",
        "label": "Recipient Name 2"
      },
      "RECIPIENT_STATE": {
        "kind": "text",
        "label": "Recipient State"
      },
      "RECIPIENT_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Recipient ZIP"
      },
      "SITE_ID": {
        "kind": "amount",
        "required": true,
        "label": "Site ID"
      },
      "SSN/TIN": {
        "kind": "text",
        "required": true,
        "label": "SSN/TIN"
      },
      "createdBy": {
        "kind": "text"
      },
      "userId": {
        "kind": "text"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "1099-R": {
      "JOB_NUMBER": {
        "kind": "text",
        "label": "Job Number"
      },
      "SITE_ID": {
        "kind": "text",
        "required": true,
        "label": "Site ID"
      },
      "FILER_FEDERAL_ID_NUMBER": {
        "kind": "tin",
        "required": true,
        "label": "Filer Federal ID Number"
      },
      "FILER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Filer Name"
      },
      "FILER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Filer Address 1"
      },
      "FILER_ADDRESS2": {
        "kind": "text",
        "label": "Filer Address 2"
      },
      "FILER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Filer City"
      },
      "FILER_STATE": {
        "kind": "text",
        "required": true,
        "label": "Filer State"
      },
      "FILER_ZIP": {
        "kind": "text",
        "required": true,
        "label": "Filer ZIP"
      },
      "FILER_PHONE": {
        "kind": "text",
        "required": true,
        "label": "Filer Phone"
      },
      "RECIPIENT_ID_NUMBER": {
        "kind": "text",
        "required": true,
        "label": "Recipient ID Number"
      },
      "RECIPIENT_NAME": {
        "kind": "text",
        "required": true,
        "label": "Recipient Name"
      },
      "RECIPIENT_NAME2": {
        "kind": "text",
        "required": true,
        "label": "Recipient Name 2"
      },
      "RECIPIENT_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Recipient Address 1"
      },
      "RECIPIENT_ADDRESS2": {
        "kind": "text",
        "label": "Recipient Address 2"
      },
      "RECIPIENT_CITY": {
        "kind": "text",
        "required": true,
        "label": "Recipient City"
      },
      "RECIPIENT_STATE": {
        "kind": "text",
        "required": true,
        "label": "Recipient State"
      },
      "RECIPIENT_ZIP": {
        "kind": "text",
        "required": true,
        "label": "Recipient ZIP"
      },
      "RECIPIENT_EMAIL": {
        "kind": "text",
        "label": "Recipient Email"
      },
      "ACCOUNT_NUMBER": {
        "kind": "text",
        "label": "Account Number"
      },
      "BOX1_GROSS_DISTRIBUTION": {
        "kind": "amount",
        "label": "Gross Distribution"
      },
      "BOX2A_TAXABLE_AMOUNT": {
        "kind": "amount",
        "label": "Taxable Amount"
      },
      "BOX2B_TAXABLE_AMOUNT_IND": {
        "kind": "checkbox",
        "label": "Taxable Amount Indicator"
      },
      "BOX2B_TOTAL_DISTRIBUTION_IND": {
        "kind": "checkbox",
        "label": "Total Distribution Indicator"
      },
      "BOX3_CAPITAL_GAIN": {
        "kind": "amount",
        "label": "Capital Gain"
      },
      "BOX4_FEDERAL_INCOME_TAX_WITHHELD": {
        "kind": "amount",
        "label": "Federal Income Tax Withheld"
      },
      "BOX5_EMPLOYEE_CONTRIBUTIONS": {
        "kind": "amount",
        "label": "Employee Contributions"
      },
      "BOX6_UNREALIZED_APPRECIATION": {
        "kind": "amount",
        "label": "Unrealized Appreciation"
      },
      "BOX7_DISTRIBUTION_CODES": {
        "kind": "text",
        "required": true,
        "label": "Distribution Codes"
      },
      "BOX7_IRA_SEP_SIMPLE_IND": {
        "kind": "checkbox",
        "label": "IRA SEP SIMPLE Indicator"
      },
      "BOX8_OTHER": {
        "kind": "amount",
        "label": "Other"
      },
      "BOX8_PERCENT": {
        "kind": "amount",
        "label": "Percent"
      },
      "BOX9A_PERCENT_OF_DISTRIBUTION": {
        "kind": "amount",
        "label": "Percent of Distribution"
      },
      "BOX9B_TOT_EMPLOYEE_CONTRIBUTIONS": {
        "kind": "amount",
        "label": "Total Employee Contributions"
      },
      "BOX10_AMOUNT_ALLOCABLE": {
        "kind": "amount",
        "label": "Amount Allocable"
      },
      "BOX11_ROTH_CONTRIBUTION": {
        "kind": "text",
        "label": "Roth Contribution"
      },
      "BOX12_FATCA_REQUIREMENT": {
        "kind": "checkbox",
        "label": "FATCA Requirement"
      },
      "BOX13_DATE_OF_PAYMENT": {
        "kind": "text",
        "label": "Date of Payment"
      },
      "BOX14_STATE_TAX_WITHHELD1": {
        "kind": "amount",
        "label": "State Tax Withheld 1"
      },
      "BOX14_STATE_TAX_WITHHELD2": {
        "kind": "amount",
        "label": "State Tax Withheld 2"
      },
      "BOX15_PAYERS_STATE_NO1": {
        "kind": "text",
        "label": "Payer's State Number 1"
      },
      "BOX15_PAYERS_STATE_NO2": {
        "kind": "text",
        "label": "Payer's State Number 2"
      },
      "BOX16_STATE_DISTRIBUTION1": {
        "kind": "amount",
        "label": "State Distribution 1"
      },
      "BOX16_STATE_DISTRIBUTION2": {
        "kind": "amount",
        "label": "State Distribution 2"
      },
      "BOX17_LOCAL_TAX_WITHHELD1": {
        "kind": "amount",
        "label": "Local Tax Withheld 1"
      },
      "BOX17_LOCAL_TAX_WITHHELD2": {
        "kind": "amount",
        "label": "Local Tax Withheld 2"
      },
      "BOX18_NAME_OF_LOCALITY1": {
        "kind": "text",
        "label": "Name of Locality 1"
      },
      "BOX18_NAME_OF_LOCALITY2": {
        "kind": "text",
        "label": "Name of Locality 2"
      },
      "BOX19_LOCAL_DISTRIBUTION1": {
        "kind": "amount",
        "label": "Local Distribution 1"
      },
      "BOX19_LOCAL_DISTRIBUTION2": {
        "kind": "amount",
        "label": "Local Distribution 2"
      },
      "1099_YEAR": {
        "kind": "text",
        "required": true,
        "label": "Year"
      },
      "SSN_TIN": {
        "kind": "text",
        "label": "SSN/TIN"
      },
      "FOREIGN_COUNTRY": {
        "kind": "text",
        "label": "Foreign Country"
      },
      "FOREIGN_COUNTRY_INDICATOR": {
        "kind": "checkbox",
        "label": "Foreign Country indicator"
      },
      "createdBy": {
        "kind": "text"
      },
      "userId": {
        "kind": "text"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "1099-SA": {
      "SITE_ID": {
        "kind": "text",
        "required": true,
        "label": "Site ID"
      },
      "FILER_FEDERAL_ID_NUMBER": {
        "kind": "text",
        "required": true,
        "label": "Filer Federal ID Number"
      },
      "FILER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Filer Name"
      },
      "FILER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Filer Address 1"
      },
      "FILER_ADDRESS2": {
        "kind": "text",
        "label": "Filer Address 2"
      },
      "FILER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Filer City"
      },
      "FILER_STATE": {
        "kind": "text",
        "required": true,
        "label": "Filer State"
      },
      "FILER_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Filer ZIP"
      },
      "JOB_NUMBER": {
        "kind": "text",
        "label": "Job Number"
      },
      "FILER_PHONE": {
        "kind": "text",
        "label": "Filer Phone"
      },
      "BOX_RECIPIENT_ID_NUMBER": {
        "kind": "tin",
        "required": true,
        "label": "Recipient ID Number"
      },
      "RECIPIENT_NAME": {
        "kind": "text",
        "required": true,
        "label": "Recipient Name"
      },
      "RECIPIENT_NAME2": {
        "kind": "text",
        "required": true,
        "label": "Recipient Name 2"
      },
      "RECIPIENT_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Recipient Address"
      },
      "RECIPIENT_ADDRESS2": {
        "kind": "text",
        "label": "Recipient Address 1"
      },
      "RECIPIENT_ADDRESS3": {
        "kind": "text",
        "label": "Recipient Address 3"
      },
      "RECIPIENT_CITY": {
        "kind": "text",
        "required": true,
        "label": "Recipient City"
      },
      "RECIPIENT_STATE": {
        "kind": "text",
        "required": true,
        "label": "Recipient State"
      },
      "RECIPIENT_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Recipient ZIP"
      },
      "EMAIL": {
        "kind": "text",
        "label": "Email"
      },
      "BOX_ACCOUNT_NUMBER": {
        "kind": "text",
        "label": "Account Number"
      },
      "BOX1_GROSS_DISTRIBUTION": {
        "kind": "text",
        "label": "Gross Distribution"
      },
      "BOX2_EARNING_ON_EXCESS": {
        "kind": "amount",
        "label": "Earnings on Excess Contributions"
      },
      "BOX3_DISTRIBUTION_CODE": {
        "kind": "text",
        "label": "Distribution Code"
      },
      "BOX4_FMV_ON_DATE_OF_DEATH": {
        "kind": "amount",
        "label": "FMV on Date of Death"
      },
      "BOX5_HSA": {
        "kind": "checkbox",
        "label": "HSA"
      },
      "BOX5_ARCHER_MSA": {
        "kind": "checkbox",
        "label": "Archer MSA"
      },
      "FOREIGN_COUNTRY": {
        "kind": "text",
        "label": "Foreign Country"
      },
      "FOREIGN_COUNTRY_INDICATOR": {
        "kind": "checkbox",
        "label": "Foreign Country Indicator"
      },
      "BOX5_MA_MSA": {
        "kind": "checkbox",
        "label": "MA MSA"
      },
      "1099_YEAR": {
        "kind": "text",
        "required": true,
        "label": "1099 Year"
      },
      "createdBy": {
        "kind": "text"
      },
      "userId": {
        "kind": "text"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "5498-SA": {
      "SITE_ID": {
        "kind": "text",
        "required": true,
        "label": "Site ID"
      },
      "FILER_FEDERAL_ID_NUMBER": {
        "kind": "text",
        "required": true,
        "label": "Filer Federal ID Number"
      },
      "FILER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Filer Name"
      },
      "FILER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Filer Address 1"
      },
      "FILER_ADDRESS2": {
        "kind": "text",
        "label": "Filer Address 2"
      },
      "FILER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Filer City"
      },
      "FILER_STATE": {
        "kind": "text",
        "required": true,
        "label": "Filer State"
      },
      "FILER_ZIP": {
        "kind": "text",
        "required": true,
        "label": "Filer ZIP"
      },
      "JOB_NUMBER": {
        "kind": "text",
        "label": "Job Number"
      },
      "FILER_PHONE": {
        "kind": "text",
        "label": "Filer Phone"
      },
      "RECIPIENT_ID_NUMBER": {
        "kind": "text",
        "required": true,
        "label": "Recipient ID Number"
      },
      "RECIPIENT_NAME": {
        "kind": "text",
        "required": true,
        "label": "Recipient Name"
      },
      "RECIPIENT_NAME2": {
        "kind": "text",
        "label": "Recipient Name 2"
      },
      "RECIPIENT_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Recipient Address 1"
      },
      "RECIPIENT_ADDRESS2": {
        "kind": "text",
        "label": "Recipient Address 2"
      },
      "RECIPIENT_ADDRESS3": {
        "kind": "text",
        "label": "Recipient Address 3"
      },
      "RECIPIENT_CITY": {
        "kind": "text",
        "required": true,
        "label": "Recipient City"
      },
      "RECIPIENT_STATE": {
        "kind": "text",
        "required": true,
        "label": "Recipient State"
      },
      "RECIPIENT_ZIP": {
        "kind": "text",
        "required": true,
        "label": "Recipient ZIP"
      },
      "ACCOUNT_NUMBER": {
        "kind": "text",
        "label": "Account Number"
      },
      "BOX1_ARCHER_MSA_CONTRIBUTIONS": {
        "kind": "amount",
        "label": "Archer MSA Contributions"
      },
      "BOX2_TOTAL_CONTRIBUTIONS": {
        "kind": "amount",
        "label": "Total Contributions"
      },
      "BOX3_TOTAL_HSA_ARCHER_CONTRIBUTIONS": {
        "kind": "amount",
        "label": "Total HSA/Archer Contributions"
      },
      "BOX4_ROLLOVER_CONTRIBUTIONS": {
        "kind": "amount",
        "label": "Rollover Contributions"
      },
      "BOX5_FAIR_MARKET_VALUE_HSA_ARCHER_MSA_MA_MSA": {
        "kind": "amount",
        "label": "Fair Market Value HSA/Archer MSA/MA MSA"
      },
      "BOX6_HSA": {
        "kind": "checkbox",
        "label": "HSA"
      },
      "BOX6_ARCHER_MSA": {
        "kind": "checkbox",
        "label": "Archer MSA"
      },
      "BOX6_MA_MSA": {
        "kind": "checkbox",
        "label": "MA MSA"
      },
      "FOREIGN_COUNTRY": {
        "kind": "text",
        "label": "Foreign Country"
      },
      "FOREIGN_COUNTRY_INDICATOR": {
        "kind": "checkbox",
        "label": "Foreign Country Indicator"
      },
      "1099_YEAR": {
        "kind": "text",
        "required": true,
        "label": "1099 Year"
      },
      "createdBy": {
        "kind": "text"
      },
      "userId": {
        "kind": "text"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    }
  },
  "2026": {
    "1098": {
      "SITE_ID": {
        "kind": "text",
        "required": true,
        "label": "Site ID"
      },
      "1098_YEAR": {
        "kind": "amount",
        "required": true,
        "label": "1098 Year"
      },
      "LENDER_FEDERAL_ID_NUMBER": {
        "kind": "text",
        "required": true,
        "label": "Lender Federal ID Number"
      },
      "LENDER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Lender Name"
      },
      "LENDER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Lender Address 1"
      },
      "LENDER_SUITE_NO": {
        "kind": "text",
        "label": "Lender Suite No"
      },
      "LENDER_ADDRESS2": {
        "kind": "text",
        "label": "Lender Address 2"
      },
      "LENDER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Lender City"
      },
      "LENDER_STATE": {
        "kind": "state",
        "required": true,
        "label": "Lender State"
      },
      "LENDER_ZIP": {
        "kind": "text",
        "required": true,
        "label": "Lender ZIP"
      },
      "LENDER_COUNTRY": {
        "kind": "text",
        "label": "Lender Country"
      },
      "LENDER_PHONE": {
        "kind": "text",
        "required": true,
        "label": "Lender Phone"
      },
      "BORROWER_ID_TYPE": {
        "kind": "idType",
        "label": "Borrower ID Type"
      },
      "BORROWER_ID_NUMBER": {
        "kind": "tin",
        "label": "Borrower ID Number"
      },
      "BORROWER_FIRST_NAME": {
        "kind": "text",
        "label": "Borrower First Name"
      },
      "BORROWER_MIDDLE_NAME": {
        "kind": "text",
        "label": "Borrower Middle Name"
      },
      "BORROWER_LAST_NAME": {
        "kind": "text",
        "label": "Borrower Last Name"
      },
      "BORROWER_SUFFIX": {
        "kind": "text",
        "label": "Borrower Suffix"
      },
      "BORROWER_COMPANY_NAME": {
        "kind": "text",
        "label": "Borrower Company Name"
      },
      "RECIPIENT_BUSINESS_NAME2": {
        "kind": "text",
        "label": "Borrower Business Name 2 (DBA)"
      },
      "BORROWER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Borrower Address 1"
      },
      "BORROWER_APT_NO": {
        "kind": "text",
        "label": "Borrower Apt No"
      },
      "BORROWER_ADDRESS2": {
        "kind": "text",
        "label": "Borrower Address 2"
      },
      "BORROWER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Borrower City"
      },
      "BORROWER_STATE": {
        "kind": "state",
        "required": true,
        "label": "Borrower State"
      },
      "BORROWER_ZIP": {
        "kind": "text",
        "label": "Borrower ZIP"
      },
      "FOREIGN_COUNTRY_INDICATOR": {
        "kind": "checkbox",
        "label": "Foreign Country Indicator"
      },
      "BORROWER_COUNTRY": {
        "kind": "text",
        "label": "Borrower Country"
      },
      "BORROWER_ACCOUNT_NUMBER": {
        "kind": "text",
        "label": "Borrower Account Number"
      },
      "AMORTIZATION": {
        "kind": "amount",
        "decimal": true,
        "label": "Amortization"
      },
      "REAL_ESTATE_TAXES": {
        "kind": "amount",
        "decimal": true,
        "label": "Real Estate Taxes"
      },
      "CAPITAL_RESERVE": {
        "kind": "amount",
        "decimal": true,
        "label": "Capital Reserve"
      },
      "BOX1_MORTGAGE_INTEREST": {
        "kind": "amount",
        "decimal": true,
        "label": "Mortgage Interest Received"
      },
      "BOX2_OUTSTANDING_MORTGAGE_PRINCIPAL": {
        "kind": "amount",
        "decimal": true,
        "label": "Outstanding Mortgage Principal"
      },
      "BOX3_MORTGAGE_ORIGNATION_DATE": {
        "kind": "date",
        "label": "Mortgage Origination Date"
      },
      "BOX4_REFUND_OF_OVERPAID_INTEREST": {
        "kind": "amount",
        "decimal": true,
        "label": "Refund of Overpaid Interest"
      },
      "BOX_5_MORTGAGE_INSURANCE_PREMIUM": {
        "kind": "amount",
        "decimal": true,
        "label": "Mortgage Insurance Premiums"
      },
      "BOX6_POINTS_PAID": {
        "kind": "amount",
        "decimal": true,
        "label": "Points Paid on Purchase of Principal Residence"
      },
      "BOX7_ADDRESS_OF_PROPERTY_SECURING_MORTGAGE_IS_THE_SAME": {
        "kind": "checkbox",
        "label": "Property Address Same as Borrower Address"
      },
      "BOX8_ADDRESS_OR_DESCRIPTION_OF_PROPERTY_LINE_1": {
        "kind": "text",
        "label": "Property Address or Description Line 1"
      },
      "BOX8_ADDRESS_OR_DESCRIPTION_OF_PROPERTY_LINE_2": {
        "kind": "text",
        "label": "Property Address or Description Line 2"
      },
      "BOX8_ADDRESS_OR_DESCRIPTION_OF_PROPERTY_LINE_3": {
        "kind": "text",
        "label": "Property Address or Description Line 3"
      },
      "BOX9_NUMBER_OF_PROPERTIES": {
        "kind": "amount",
        "label": "Number of Properties Securing the Mortgage"
      },
      "BOX10_OTHER": {
        "kind": "text",
        "label": "Other"
      },
      "BOX11_MORTGAGE_ACQUISITION_DATE": {
        "kind": "date",
        "label": "Mortgage Acquisition Date"
      },
      "BORROWER_FULL_NAME": {
        "kind": "text",
        "label": "Borrower Full Name (combined)"
      },
      "createdBy": {
        "kind": "text"
      },
      "userId": {
        "kind": "text"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "3921": {
      "EMPLOYEE_ID_TYPE": {
        "kind": "idType",
        "label": "Employee ID Type"
      },
      "EMPLOYEE_FIRST_NAME": {
        "kind": "text",
        "label": "Employee First Name"
      },
      "EMPLOYEE_MIDDLE_NAME": {
        "kind": "text",
        "label": "Employee Middle Name"
      },
      "EMPLOYEE_LAST_NAME": {
        "kind": "text",
        "label": "Employee Last Name"
      },
      "EMPLOYEE_SUFFIX": {
        "kind": "text",
        "label": "Employee Suffix"
      },
      "EMPLOYEE_BUSINESS_NAME1": {
        "kind": "text",
        "label": "Employee Business Name 1"
      },
      "EMPLOYEE_BUSINESS_NAME2": {
        "kind": "text",
        "label": "Employee Business Name 2"
      },
      "RECIPIENT_ID_NUMBER": {
        "kind": "tin",
        "label": "Recipient ID Number"
      },
      "EMPLOYEE_SSN": {
        "kind": "tin",
        "required": true,
        "label": "Employee SSN"
      },
      "EMPLOYEE_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Employee Address 1"
      },
      "EMPLOYEE_ADDRESS2": {
        "kind": "text",
        "label": "Employee Address 2"
      },
      "EMPLOYEE_APT_NO": {
        "kind": "text",
        "label": "Employee Apt No"
      },
      "EMPLOYEE_ADDRESS3": {
        "kind": "text",
        "label": "Employee Address 3"
      },
      "EMPLOYEE_CITY": {
        "kind": "text",
        "required": true,
        "label": "Employee City"
      },
      "EMPLOYEE_STATE": {
        "kind": "text",
        "required": true,
        "label": "Employee State"
      },
      "EMPLOYEE_ZIP": {
        "kind": "text",
        "required": true,
        "label": "Employee ZIP"
      },
      "ACCOUNT_NUMBER": {
        "kind": "text",
        "label": "Account Number"
      },
      "transectionId": {
        "kind": "text",
        "label": "Transaction ID"
      },
      "BOX1_DATE_GRANTED": {
        "kind": "date",
        "label": "Date Granted"
      },
      "BOX2_DATE_EXERCISED": {
        "kind": "date",
        "label": "Date Exercised"
      },
      "BOX3_EXE_PRICE_PER_SHARE": {
        "kind": "text",
        "decimal": true,
        "label": "Exercise Price per Share"
      },
      "BOX4_FAIR_MARKET_VALUE_ON_SHARE": {
        "kind": "text",
        "decimal": true,
        "label": "Fair Market Value per Share"
      },
      "BOX5_SHARES_TRANSFERRED": {
        "kind": "amount",
        "label": "Shares Transferred"
      },
      "BOX6_EIN": {
        "kind": "text",
        "label": "Employer Identification Number"
      },
      "BOX6_COMPANY": {
        "kind": "text",
        "label": "Company"
      },
      "BOX6_ADDRESS": {
        "kind": "text",
        "label": "Company Address"
      },
      "BOX_6D": {
        "kind": "text",
        "label": "Box 6D"
      },
      "1099_YEAR": {
        "kind": "amount",
        "required": true,
        "label": "1099 Year"
      },
      "SITE_ID": {
        "kind": "text",
        "required": true,
        "label": "Site ID"
      },
      "FILER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Filer Name"
      },
      "FILER_NAME2": {
        "kind": "text",
        "label": "Filer Name 2"
      },
      "FILER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Filer Address 1"
      },
      "FILER_ADDRESS2": {
        "kind": "text",
        "label": "Filer Address 2"
      },
      "FILER_COUNTRY": {
        "kind": "text",
        "label": "Filer Country"
      },
      "FILER_SUITE_NO": {
        "kind": "text",
        "label": "Filer Suite No"
      },
      "FILER_ADDRESS3": {
        "kind": "text",
        "label": "Filer Address 3"
      },
      "FILER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Filer City"
      },
      "FILER_STATE": {
        "kind": "text",
        "required": true,
        "label": "Filer State"
      },
      "FILER_ZIP": {
        "kind": "text",
        "required": true,
        "label": "Filer ZIP"
      },
      "FILER_PHONE": {
        "kind": "text",
        "label": "Filer Phone"
      },
      "FILER_FEDERAL_ID_NUMBER": {
        "kind": "text",
        "required": true,
        "label": "Filer Federal ID Number"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "3922": {
      "EMPLOYEE_ID_TYPE": {
        "kind": "idType",
        "label": "Employee ID Type"
      },
      "EMPLOYEE_FIRST_NAME": {
        "kind": "text",
        "label": "Employee First Name"
      },
      "EMPLOYEE_MIDDLE_NAME": {
        "kind": "text",
        "label": "Employee Middle Name"
      },
      "EMPLOYEE_LAST_NAME": {
        "kind": "text",
        "label": "Employee Last Name"
      },
      "EMPLOYEE_SUFFIX": {
        "kind": "text",
        "label": "Employee Suffix"
      },
      "EMPLOYEE_BUSINESS_NAME1": {
        "kind": "text",
        "label": "Employee Business Name 1"
      },
      "EMPLOYEE_BUSINESS_NAME2": {
        "kind": "text",
        "label": "Employee Business Name 2"
      },
      "RECIPIENT_ID_NUMBER": {
        "kind": "tin",
        "label": "Recipient ID Number"
      },
      "SITE_ID": {
        "kind": "text",
        "required": true,
        "label": "Site ID"
      },
      "1099_YEAR": {
        "kind": "amount",
        "required": true,
        "label": "Tax Year"
      },
      "CORPORATION_NAME_1": {
        "kind": "text",
        "required": true,
        "label": "Corporation Name 1"
      },
      "CORPORATION_NAME_2": {
        "kind": "text",
        "label": "Corporation Name 2"
      },
      "CORPORATION_ADDRESS": {
        "kind": "text",
        "required": true,
        "label": "Corporation Address"
      },
      "CORPORATION_ADDRESS2": {
        "kind": "text",
        "label": "Corporation Address 2"
      },
      "CORPORATION_COUNTRY": {
        "kind": "text",
        "label": "Corporation Country"
      },
      "CORPORATION_SUITE_NO": {
        "kind": "text",
        "label": "Corporation Suite No"
      },
      "CORPORATION_CITY": {
        "kind": "text",
        "required": true,
        "label": "Corporation City"
      },
      "CORPORATION_STATE": {
        "kind": "state",
        "required": true,
        "label": "Corporation State"
      },
      "CORPORATION_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Corporation ZIP"
      },
      "CORPORATION_PHONE": {
        "kind": "text",
        "label": "Corporation Phone"
      },
      "CORPORATION_EIN": {
        "kind": "text",
        "label": "Corporation EIN"
      },
      "EMPLOYEE_SSN": {
        "kind": "tin",
        "required": true,
        "label": "Employee SSN"
      },
      "EMPLOYEE_ADDRESS_1": {
        "kind": "text",
        "required": true,
        "label": "Employee Address 1"
      },
      "EMPLOYEE_ADDRESS_2": {
        "kind": "text",
        "label": "Employee Address 2"
      },
      "EMPLOYEE_APT_NO": {
        "kind": "text",
        "label": "Employee Apt No"
      },
      "EMPLOYEE_ADDRESS_3": {
        "kind": "text",
        "label": "Employee Address 3"
      },
      "EMPLOYEE_CITY": {
        "kind": "text",
        "required": true,
        "label": "Employee City"
      },
      "EMPLOYEE_STATE": {
        "kind": "state",
        "required": true,
        "label": "Employee State"
      },
      "EMPLOYEE_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Employee ZIP"
      },
      "EMPLOYEE_ACCOUNT_NO": {
        "kind": "text",
        "label": "Employee Account Number"
      },
      "BOX1_DATE_GRANTED": {
        "kind": "text",
        "label": "Box 1"
      },
      "BOX2_DATE_EXERCISED": {
        "kind": "text",
        "label": "Box 2"
      },
      "BOX3_FAIR_MARKET_VALUE_GRANT_DATE": {
        "kind": "amount",
        "decimal": true,
        "label": "Box 3"
      },
      "BOX4_FAIR_MARKET_VALUE_EXE_DATE": {
        "kind": "amount",
        "decimal": true,
        "label": "Box 4"
      },
      "BOX5_EXERCISE_PRICE_PAID": {
        "kind": "amount",
        "decimal": true,
        "label": "Box 5"
      },
      "BOX6_NO_SHARES_TRANSFERRED": {
        "kind": "amount",
        "label": "Box 6"
      },
      "BOX7_DATE_TITLE_TRANSFERRED": {
        "kind": "text",
        "label": "Box 7"
      },
      "BOX8_EXERCISE_PRICE_PAID_ON_GRANT_DATE": {
        "kind": "amount",
        "decimal": true,
        "label": "Box 8"
      },
      "ROW_INDEX": {
        "kind": "text",
        "label": "Row Index"
      },
      "transectionId": {
        "kind": "text",
        "label": "Transaction ID"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "1042-S": {
      "createdBy": {
        "kind": "text"
      },
      "userId": {
        "kind": "text"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "1098-E": {
      "SITE_ID": {
        "kind": "text",
        "required": true,
        "label": "Site ID"
      },
      "1098_YEAR": {
        "kind": "amount",
        "required": true,
        "label": "1098 Year"
      },
      "BORROWER_ID_TYPE": {
        "kind": "idType",
        "label": "Borrower ID Type"
      },
      "BORROWER_FIRST_NAME": {
        "kind": "text",
        "label": "Borrower First Name"
      },
      "BORROWER_MIDDLE_NAME": {
        "kind": "text",
        "label": "Borrower Middle Name"
      },
      "BORROWER_LAST_NAME": {
        "kind": "text",
        "label": "Borrower Last Name"
      },
      "BORROWER_SUFFIX": {
        "kind": "text",
        "label": "Borrower Suffix"
      },
      "BORROWER_BUSINESS_NAME1": {
        "kind": "text",
        "label": "Borrower Business Name 1"
      },
      "BORROWER_BUSINESS_NAME2": {
        "kind": "text",
        "label": "Borrower Business Name 2"
      },
      "BORROWER_SSN": {
        "kind": "tin",
        "label": "Borrower SSN"
      },
      "JOB_NUMBER": {
        "kind": "text",
        "label": "Job Number"
      },
      "BORROWER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Borrower Address 1"
      },
      "BORROWER_ADDRESS2": {
        "kind": "text",
        "label": "Borrower Address 2"
      },
      "BORROWER_APT_NO": {
        "kind": "text",
        "label": "Borrower Apt No"
      },
      "BORROWER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Borrower City"
      },
      "BORROWER_STATE": {
        "kind": "state",
        "required": true,
        "label": "Borrower State"
      },
      "BORROWER_ZIP": {
        "kind": "text",
        "required": true,
        "label": "Borrower ZIP"
      },
      "FOREIGN_COUNTRY_INDICATOR": {
        "kind": "checkbox",
        "label": "Foreign Country Indicator"
      },
      "FOREIGN_COUNTRY": {
        "kind": "text",
        "label": "Foreign Country"
      },
      "BOX1_STUDENT_LOAN_INTEREST": {
        "kind": "amount",
        "label": "Student Loan Interest"
      },
      "BOX2_PRE_2004_ORIGINATION_FEES": {
        "kind": "checkbox",
        "label": "Pre-2004 Origination Fees"
      },
      "BORROWER_ACCOUNT_NUMBER": {
        "kind": "text",
        "label": "Borrower Account Number"
      },
      "FILER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Filer Name"
      },
      "FILER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Filer Address 1"
      },
      "FILER_ADDRESS2": {
        "kind": "text",
        "label": "Filer Address 2"
      },
      "FILER_COUNTRY": {
        "kind": "text",
        "label": "Filer Country"
      },
      "FILER_SUITE_NO": {
        "kind": "text",
        "label": "Filer Suite No"
      },
      "FILER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Filer City"
      },
      "FILER_STATE": {
        "kind": "text",
        "required": true,
        "label": "Filer State"
      },
      "FILER_ZIP": {
        "kind": "text",
        "required": true,
        "label": "Filer ZIP"
      },
      "FILER_PHONE": {
        "kind": "text",
        "required": true,
        "label": "Filer Phone"
      },
      "FILER_FEDERAL_ID_NUMBER": {
        "kind": "text",
        "required": true,
        "label": "Filer Federal ID Number"
      },
      "createdBy": {
        "kind": "text"
      },
      "userId": {
        "kind": "text"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "1098-T": {
      "SITE_ID": {
        "kind": "text",
        "required": true,
        "label": "Site ID"
      },
      "FILER_FEDERAL_ID_NUMBER": {
        "kind": "text",
        "required": true,
        "label": "Filer Federal ID Number"
      },
      "FILER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Filer Name"
      },
      "FILER_NAME2": {
        "kind": "text",
        "label": "Filer Name 2"
      },
      "FILER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Filer Address 1"
      },
      "FILER_ADDRESS2": {
        "kind": "text",
        "label": "Filer Address 2"
      },
      "FILER_COUNTRY": {
        "kind": "text",
        "label": "Filer Country"
      },
      "FILER_SUITE_NO": {
        "kind": "text",
        "label": "Filer Suite No"
      },
      "FILER_ADDRESS3": {
        "kind": "text",
        "label": "Filer Address 3"
      },
      "FILER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Filer City"
      },
      "FILER_STATE": {
        "kind": "state",
        "required": true,
        "label": "Filer State"
      },
      "FILER_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Filer ZIP"
      },
      "JOB_NUMBER": {
        "kind": "text",
        "label": "Job Number"
      },
      "FILER_PHONE": {
        "kind": "text",
        "required": true,
        "label": "Filer Phone"
      },
      "STUDENT_SSN": {
        "kind": "tin",
        "required": true,
        "label": "Student SSN"
      },
      "STUDENT_FIRST_NAME": {
        "kind": "text",
        "required": true,
        "label": "Student First Name"
      },
      "STUDENT_MIDDLE_NAME": {
        "kind": "text",
        "label": "Student Middle Name"
      },
      "STUDENT_LAST_NAME": {
        "kind": "text",
        "required": true,
        "label": "Student Last Name"
      },
      "STUDENT_SUFFIX": {
        "kind": "text",
        "label": "Student Suffix"
      },
      "STUDENT_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Student Address 1"
      },
      "STUDENT_ADDRESS2": {
        "kind": "text",
        "label": "Student Address 2"
      },
      "STUDENT_APT_NO": {
        "kind": "text",
        "label": "Student Apt No"
      },
      "STUDENT_CITY": {
        "kind": "text",
        "required": true,
        "label": "Student City"
      },
      "STUDENT_STATE": {
        "kind": "state",
        "required": true,
        "label": "Student State"
      },
      "STUDENT_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Student ZIP"
      },
      "FOREIGN_COUNTRY_INDICATOR": {
        "kind": "checkbox",
        "label": "Foreign Country Indicator"
      },
      "FOREIGN_COUNTRY": {
        "kind": "text",
        "label": "Foreign Country"
      },
      "EMAIL": {
        "kind": "text",
        "label": "Email"
      },
      "SCHOOL_STUDENT_ID": {
        "kind": "text",
        "label": "School Student ID"
      },
      "BOX1_PAYMENTS_RECEIVED": {
        "kind": "amount",
        "label": "Payments Received"
      },
      "BOX4_ADJ_MADE_PRIOR_YR": {
        "kind": "amount",
        "label": "Adjustments Made Prior Year"
      },
      "BOX5_SCHOLARSHIP_GRANTS": {
        "kind": "amount",
        "label": "Scholarships or Grants"
      },
      "BOX6_ADJ_SCHOLARSHIP_PRIOR_YR": {
        "kind": "amount",
        "label": "Adjustments to Scholarships or Grants for Prior Year"
      },
      "BOX7_ACADEMIC_PERIOD_IND": {
        "kind": "checkbox",
        "label": "Academic Period Indicator"
      },
      "BOX8_HALF_TIME_STUDENT_IND": {
        "kind": "checkbox",
        "label": "Half-time Student Indicator"
      },
      "BOX9_GRADUATE_STUDENT_IND": {
        "kind": "checkbox",
        "label": "Graduate Student Indicator"
      },
      "BOX10_REIMBURSEMENT_OR_REFUND": {
        "kind": "amount",
        "label": "Reimbursement or Refund"
      },
      "SSN_SOLICITED": {
        "kind": "text",
        "label": "SSN Solicited"
      },
      "1098_YEAR": {
        "kind": "amount",
        "required": true,
        "label": "1098 Year"
      },
      "SSN/TIN": {
        "kind": "tin",
        "required": true,
        "label": "SSN/TIN"
      },
      "TOKEN": {
        "kind": "text",
        "label": "Token"
      },
      "STUDENT_CONSENT_DATE": {
        "kind": "text",
        "label": "Student Consent Date"
      },
      "createdBy": {
        "kind": "text"
      },
      "userId": {
        "kind": "text"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "1099-B": {
      "SITE_ID": {
        "kind": "text",
        "required": true,
        "label": "Site ID"
      },
      "FILER_FEDERAL_ID_NUMBER": {
        "kind": "tin",
        "required": true,
        "label": "Filer Federal ID Number"
      },
      "FILER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Filer Name"
      },
      "FILER_NAME2": {
        "kind": "text",
        "label": "Filer Name 2"
      },
      "FILER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Filer Address 1"
      },
      "FILER_SUITE_NO": {
        "kind": "text",
        "label": "Filer Suite No"
      },
      "FILER_ADDRESS2": {
        "kind": "text",
        "label": "Filer Address 2"
      },
      "FILER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Filer City"
      },
      "FILER_STATE": {
        "kind": "state",
        "required": true,
        "label": "Filer State"
      },
      "FILER_COUNTRY": {
        "kind": "text",
        "label": "Filer Country"
      },
      "FILER_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Filer ZIP"
      },
      "FILER_PHONE": {
        "kind": "text",
        "required": true,
        "label": "Filer Phone"
      },
      "RECIPIENT_ID_TYPE": {
        "kind": "idType",
        "label": "Recipient ID Type"
      },
      "RECIPIENT_FIRST_NAME": {
        "kind": "text",
        "label": "Recipient First Name"
      },
      "RECIPIENT_MIDDLE_NAME": {
        "kind": "text",
        "label": "Recipient Middle Name"
      },
      "RECIPIENT_LAST_NAME": {
        "kind": "text",
        "label": "Recipient Last Name"
      },
      "RECIPIENT_SUFFIX": {
        "kind": "text",
        "label": "Recipient Suffix"
      },
      "RECIPIENT_BUSINESS_NAME1": {
        "kind": "text",
        "label": "Recipient Business Name 1"
      },
      "RECIPIENT_BUSINESS_NAME2": {
        "kind": "text",
        "label": "Recipient Business Name 2"
      },
      "RECIPIENT_ID_NUMBER": {
        "kind": "tin",
        "label": "Recipient ID Number"
      },
      "RECIPIENT_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Recipient Address 1"
      },
      "RECIPIENT_APT_NO": {
        "kind": "text",
        "label": "Recipient Apt No"
      },
      "RECIPIENT_ADDRESS2": {
        "kind": "text",
        "label": "Recipient Address 2"
      },
      "RECIPIENT_CITY": {
        "kind": "text",
        "required": true,
        "label": "Recipient City"
      },
      "RECIPIENT_STATE": {
        "kind": "state",
        "required": true,
        "label": "Recipient State"
      },
      "RECIPIENT_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Recipient ZIP"
      },
      "FOREIGN_ADDRESS_INDICATOR": {
        "kind": "checkbox",
        "label": "Foreign Address Indicator"
      },
      "FOREIGN_CITY_PROVINCE_POSTAL": {
        "kind": "text",
        "label": "Foreign City"
      },
      "FOREIGN_COUNTRY": {
        "kind": "text",
        "label": "Foreign Country"
      },
      "ACCOUNT_NUMBER": {
        "kind": "text",
        "label": "Account Number"
      },
      "TIN_NOTICE_INDICATOR": {
        "kind": "checkbox",
        "label": "TIN Notice Indicator"
      },
      "CUSIP_NUMBER": {
        "kind": "text",
        "label": "CUSIP Number"
      },
      "FATCA_REQUIREMENT": {
        "kind": "checkbox",
        "label": "FATCA Requirement"
      },
      "FORM_8949_CHECKBOX": {
        "kind": "text",
        "label": "Form 8949 Checkbox"
      },
      "BOX_1A_DESCRIPTION": {
        "kind": "text",
        "label": "Description of property (Example: 100 sh. XYZ Co.)"
      },
      "BOX_1B_DATE_ACQUIRED": {
        "kind": "text",
        "label": "Date Acquired"
      },
      "BOX_1C_DATE_SOLD": {
        "kind": "text",
        "label": "Date Sold or Disposed"
      },
      "BOX_1D_PROCEEDS": {
        "kind": "amount",
        "decimal": true,
        "label": "Proceeds"
      },
      "BOX_1E_COST_BASIS": {
        "kind": "amount",
        "decimal": true,
        "label": "Cost or Other Basis"
      },
      "BOX_1F_MARKET_DISCOUNT": {
        "kind": "amount",
        "decimal": true,
        "label": "Market Discount"
      },
      "BOX_1G_WASH_SALE_LOSS": {
        "kind": "amount",
        "decimal": true,
        "label": "Wash Sale Loss Disallowed"
      },
      "BOX_2_SHORT_TERM_INDICATOR": {
        "kind": "checkbox",
        "label": "Short-term transaction indicator"
      },
      "BOX_2_LONG_TERM_INDICATOR": {
        "kind": "checkbox",
        "label": "Long-term transaction indicator"
      },
      "BOX_2_ORDINARY_INDICATOR": {
        "kind": "checkbox",
        "label": "Ordinary transaction indicator"
      },
      "BOX_3_PROCEEDS_COLLECTIBLES": {
        "kind": "checkbox",
        "decimal": true,
        "label": "Proceeds from Collectibles"
      },
      "BOX_3_PROCEEDS_QOF": {
        "kind": "checkbox",
        "decimal": true,
        "label": "Proceeds from QOF"
      },
      "BOX_4_FEDERAL_TAX_WITHHELD": {
        "kind": "amount",
        "decimal": true,
        "label": "Federal Income Tax Withheld"
      },
      "BOX_5_NONCOVERED_SECURITY_INDICATOR": {
        "kind": "checkbox",
        "label": "Noncovered Security"
      },
      "BOX_6_GROSS_PROCEEDS_INDICATOR": {
        "kind": "checkbox",
        "label": "Gross Proceeds"
      },
      "BOX_6_NET_PROCEEDS_INDICATOR": {
        "kind": "checkbox",
        "label": "Net Proceeds"
      },
      "BOX_7_LOSS_NOT_ALLOWED_INDICATOR": {
        "kind": "checkbox",
        "label": "Loss is not allowed based on amount in 1d"
      },
      "BOX_8_PROFIT_LOSS_2019": {
        "kind": "amount",
        "decimal": true,
        "label": "Profit or (loss) realized in 2019"
      },
      "BOX_9_UNREALIZED_PL_2018": {
        "kind": "amount",
        "decimal": true,
        "label": "Unrealized profit or (loss) on open contracts—12/31/2018"
      },
      "BOX_10_UNREALIZED_PL_2019": {
        "kind": "amount",
        "decimal": true,
        "label": "Unrealized profit or (loss) on open contracts—12/31/2019"
      },
      "BOX_11_AGGREGATE_PL": {
        "kind": "amount",
        "decimal": true,
        "label": "Aggregate profit or (loss) on contracts"
      },
      "BOX_12_PROCEEDS_COLLECTIBLES_INDICATOR": {
        "kind": "checkbox",
        "label": "If checked"
      },
      "BOX_13_BARTERING": {
        "kind": "amount",
        "decimal": true,
        "label": "Bartering"
      },
      "BOX_14_STATE_NAME1": {
        "kind": "text",
        "label": "State Name 1"
      },
      "BOX_15_STATE_ID1": {
        "kind": "text",
        "label": "State ID Number 1"
      },
      "BOX_16_STATE_TAX_WITHHELD1": {
        "kind": "amount",
        "decimal": true,
        "label": "State Tax Withheld 1"
      },
      "BOX_14_STATE_NAME2": {
        "kind": "text",
        "label": "State Name 2"
      },
      "BOX_15_STATE_ID2": {
        "kind": "text",
        "label": "State ID Number 2"
      },
      "BOX_16_STATE_TAX_WITHHELD2": {
        "kind": "amount",
        "decimal": true,
        "label": "State Tax Withheld 2"
      },
      "1099_YEAR": {
        "kind": "text",
        "required": true,
        "label": "1099 Year"
      }
    },
    "1099-C": {
      "JOB_NUMBER": {
        "kind": "text",
        "label": "Job Number"
      },
      "SITE_ID": {
        "kind": "text",
        "required": true,
        "label": "Site ID"
      },
      "FILER_FEDERAL_ID_NUMBER": {
        "kind": "text",
        "required": true,
        "label": "Filer Federal ID Number"
      },
      "FILER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Filer Name"
      },
      "FILER_NAME2": {
        "kind": "text",
        "label": "Filer Name 2"
      },
      "FILER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Filer Address 1"
      },
      "FILER_SUITE_NO": {
        "kind": "text",
        "label": "Filer Suite No"
      },
      "FILER_ADDRESS2": {
        "kind": "text",
        "label": "Filer Address 2"
      },
      "FILER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Filer City"
      },
      "FILER_STATE": {
        "kind": "state",
        "required": true,
        "label": "Filer State"
      },
      "FILER_COUNTRY": {
        "kind": "text",
        "label": "Filer Country"
      },
      "FILER_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Filer ZIP"
      },
      "FILER_PHONE": {
        "kind": "text",
        "required": true,
        "label": "Filer Phone"
      },
      "RECIPIENT_ID_TYPE": {
        "kind": "idType",
        "label": "Recipient ID Type"
      },
      "RECIPIENT_FIRST_NAME": {
        "kind": "text",
        "label": "Recipient First Name"
      },
      "RECIPIENT_MIDDLE_NAME": {
        "kind": "text",
        "label": "Recipient Middle Name"
      },
      "RECIPIENT_LAST_NAME": {
        "kind": "text",
        "label": "Recipient Last Name"
      },
      "RECIPIENT_SUFFIX": {
        "kind": "text",
        "label": "Recipient Suffix"
      },
      "RECIPIENT_BUSINESS_NAME1": {
        "kind": "text",
        "label": "Recipient Business Name 1"
      },
      "RECIPIENT_BUSINESS_NAME2": {
        "kind": "text",
        "label": "Recipient Business Name 2"
      },
      "RECIPIENT_ID_NUMBER": {
        "kind": "tin",
        "label": "Recipient ID Number"
      },
      "RECIPIENT_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Recipient Address 1"
      },
      "RECIPIENT_APT_NO": {
        "kind": "text",
        "label": "Recipient Apt No"
      },
      "RECIPIENT_ADDRESS2": {
        "kind": "text",
        "label": "Recipient Address 2"
      },
      "RECIPIENT_ADDRESS3": {
        "kind": "text",
        "label": "Recipient Address 3"
      },
      "RECIPIENT_CITY": {
        "kind": "text",
        "required": true,
        "label": "Recipient City"
      },
      "RECIPIENT_STATE": {
        "kind": "state",
        "required": true,
        "label": "Recipient State"
      },
      "RECIPIENT_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Recipient ZIP"
      },
      "RECIPIENT_EMAIL": {
        "kind": "text",
        "label": "Recipient Email"
      },
      "ACCOUNT_NUMBER": {
        "kind": "text",
        "label": "Account Number"
      },
      "BOX1_DATE_OF_IDENTIFIABLE_EVENT": {
        "kind": "date",
        "label": "Date of Identifiable Event"
      },
      "BOX2_AMT_OF_DEBT_DISCHARGED": {
        "kind": "amount",
        "label": "Amount of Debt Discharged"
      },
      "BOX3_INTEREST_INCL_IN_BOX2": {
        "kind": "amount",
        "label": "Interest Included in Box 2"
      },
      "BOX4_DEBT_DESCRIPTION": {
        "kind": "text",
        "label": "Debt Description"
      },
      "BOX5_BORROWER_LIABLE_YES_IND": {
        "kind": "checkbox",
        "label": "Borrower Liable Yes Indicator"
      },
      "BOX5_BORROWER_LIABLE_NO_IND": {
        "kind": "checkbox",
        "label": "Borrower Liable No Indicator"
      },
      "BOX6_IDENTIFIABLE_EVENT_CODE": {
        "kind": "text",
        "label": "Identifiable Event Code"
      },
      "BOX7_FAIR_MARKET_VALUE": {
        "kind": "amount",
        "label": "Fair Market Value"
      },
      "1099_YEAR": {
        "kind": "text",
        "required": true,
        "label": "1099 Year"
      },
      "FOREIGN_COUNTRY_INDICATOR": {
        "kind": "checkbox",
        "label": "Foreign Country Indicator"
      },
      "FOREIGN_COUNTRY": {
        "kind": "text",
        "label": "Foreign Country"
      },
      "createdBy": {
        "kind": "text"
      },
      "userId": {
        "kind": "text"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "1099-DIV": {
      "JOB_NUMBER": {
        "kind": "text",
        "label": "Job Number"
      },
      "SITE_ID": {
        "kind": "text",
        "required": true,
        "label": "Site ID"
      },
      "FILER_FEDERAL_ID_NUMBER": {
        "kind": "text",
        "required": true,
        "label": "Filer Federal ID Number"
      },
      "FILER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Filer Name"
      },
      "FILER_NAME2": {
        "kind": "text",
        "label": "Filer Name 2"
      },
      "FILER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Filer Address 1"
      },
      "FILER_SUITE_NO": {
        "kind": "text",
        "label": "Filer Suite No"
      },
      "FILER_ADDRESS2": {
        "kind": "text",
        "label": "Filer Address 2"
      },
      "FILER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Filer City"
      },
      "FILER_STATE": {
        "kind": "state",
        "required": true,
        "label": "Filer State"
      },
      "FILER_COUNTRY": {
        "kind": "text",
        "label": "Filer Country"
      },
      "FILER_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Filer ZIP"
      },
      "FILER_PHONE": {
        "kind": "text",
        "required": true,
        "label": "Filer Phone"
      },
      "RECIPIENT_ID_TYPE": {
        "kind": "idType",
        "label": "Recipient ID Type"
      },
      "RECIPIENT_FIRST_NAME": {
        "kind": "text",
        "label": "Recipient First Name"
      },
      "RECIPIENT_MIDDLE_NAME": {
        "kind": "text",
        "label": "Recipient Middle Name"
      },
      "RECIPIENT_LAST_NAME": {
        "kind": "text",
        "label": "Recipient Last Name"
      },
      "RECIPIENT_SUFFIX": {
        "kind": "text",
        "label": "Recipient Suffix"
      },
      "RECIPIENT_BUSINESS_NAME1": {
        "kind": "text",
        "label": "Recipient Business Name 1"
      },
      "RECIPIENT_BUSINESS_NAME2": {
        "kind": "text",
        "label": "Recipient Business Name 2"
      },
      "RECIPIENT_ID_NUMBER": {
        "kind": "tin",
        "label": "Recipient ID Number"
      },
      "RECIPIENT_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Recipient Address 1"
      },
      "RECIPIENT_APT_NO": {
        "kind": "text",
        "label": "Recipient Apt No"
      },
      "RECIPIENT_ADDRESS2": {
        "kind": "text",
        "label": "Recipient Address 2"
      },
      "RECIPIENT_CITY": {
        "kind": "text",
        "required": true,
        "label": "Recipient City"
      },
      "RECIPIENT_STATE": {
        "kind": "state",
        "required": true,
        "label": "Recipient State"
      },
      "RECIPIENT_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Recipient ZIP"
      },
      "RECIPIENT_EMAIL": {
        "kind": "text",
        "label": "Recipient Email"
      },
      "FOREIGN_COUNTRY_INDICATOR": {
        "kind": "checkbox",
        "label": "Foreign Country Indicator"
      },
      "FOREIGN_COUNTRY": {
        "kind": "text",
        "label": "Foreign Country"
      },
      "ACCOUNT_NUMBER": {
        "kind": "text",
        "label": "Account Number"
      },
      "BOX1A_TOTAL_ORDINARY_DIVIDENDS": {
        "kind": "amount",
        "label": "Total Ordinary Dividends"
      },
      "BOX1B_QUALIFIED_DIVIDENDS": {
        "kind": "amount",
        "label": "Qualified Dividends"
      },
      "BOX2A_TOTAL_CAPITAL_GAIN_DISTR": {
        "kind": "amount",
        "label": "Total Capital Gain Distributions"
      },
      "BOX2B_UNRECAP_SEC_1250_GAIN": {
        "kind": "amount",
        "label": "Unrecaptured Section 1250 Gain"
      },
      "BOX2C_SECTION_1202_GAIN": {
        "kind": "amount",
        "label": "Section 1202 Gain"
      },
      "BOX2D_COLLECTIBLES_GAIN": {
        "kind": "amount",
        "label": "Collectibles Gain"
      },
      "BOX2E_SECTION_897_ORD_DIV": {
        "kind": "amount",
        "label": "Section 897 Ordinary Dividends"
      },
      "BOX2F_SECTION_897_CAP_GAIN": {
        "kind": "amount",
        "label": "Section 897 Capital Gain"
      },
      "BOX3_NONDIVIDEND_DISTRIBUTIONS": {
        "kind": "amount",
        "label": "Nondividend Distributions"
      },
      "BOX4_FEDERAL_INCOME_TAX_WITHHELD": {
        "kind": "amount",
        "label": "Federal Income Tax Withheld"
      },
      "BOX5_SECTION_199A_DIVIDENDS": {
        "kind": "amount",
        "label": "Section 199A Dividends"
      },
      "BOX6_INVESTMENT_EXPENSES": {
        "kind": "amount",
        "label": "Investment Expenses"
      },
      "BOX7_FOREIGN_TAX_PAID": {
        "kind": "amount",
        "label": "Foreign Tax Paid"
      },
      "BOX8_FOREIGN_COUNTRY": {
        "kind": "text",
        "label": "Foreign Country"
      },
      "BOX9_CASH_LIQUIDATION_DISTRIBUTIONS": {
        "kind": "amount",
        "label": "Cash Liquidation Distributions"
      },
      "BOX10_NONCASH_LIQUIDATION_DISTRIBUTIONS": {
        "kind": "amount",
        "label": "Noncash Liquidation Distributions"
      },
      "BOX12_EXEMPT_INTEREST_DIVIDENDS": {
        "kind": "amount",
        "label": "Exempt Interest Dividends"
      },
      "BOX13_SPECIFIED_PRIVATE_ACTIVITY_BOND": {
        "kind": "amount",
        "label": "Specified Private Activity Bond"
      },
      "BOX14_STATE1": {
        "kind": "text",
        "label": "State 1"
      },
      "BOX14_STATE2": {
        "kind": "text",
        "label": "State 2"
      },
      "BOX15_STATE_IDENTIFICATION_NO1": {
        "kind": "text",
        "label": "State Identification No 1"
      },
      "BOX15_STATE_IDENTIFICATION_NO2": {
        "kind": "text",
        "label": "State Identification No 2"
      },
      "BOX16_STATE_TAX_WITHHELD1": {
        "kind": "amount",
        "label": "State Tax Withheld 1"
      },
      "BOX16_STATE_TAX_WITHHELD2": {
        "kind": "amount",
        "label": "State Tax Withheld 2"
      },
      "BOX11_FATCA_REQUIREMENT": {
        "kind": "checkbox",
        "label": "FATCA Requirement"
      },
      "BOX_2ND_TIN_NOT_IND": {
        "kind": "checkbox",
        "label": "2nd TIN Not Indicator"
      },
      "1099_YEAR": {
        "kind": "text",
        "required": true,
        "label": "1099 Year"
      },
      "createdBy": {
        "kind": "text"
      },
      "userId": {
        "kind": "text"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "1099-INT": {
      "RECIPIENT_ID_TYPE": {
        "kind": "idType",
        "label": "Recipient ID Type"
      },
      "RECIPIENT_FIRST_NAME": {
        "kind": "text",
        "label": "Recipient First Name"
      },
      "RECIPIENT_MIDDLE_NAME": {
        "kind": "text",
        "label": "Recipient Middle Name"
      },
      "RECIPIENT_LAST_NAME": {
        "kind": "text",
        "label": "Recipient Last Name"
      },
      "RECIPIENT_SUFFIX": {
        "kind": "text",
        "label": "Recipient Suffix"
      },
      "RECIPIENT_BUSINESS_NAME1": {
        "kind": "text",
        "label": "Recipient Business Name 1"
      },
      "RECIPIENT_BUSINESS_NAME2": {
        "kind": "text",
        "label": "Recipient Business Name 2"
      },
      "RECIPIENT_ID_NUMBER": {
        "kind": "tin",
        "label": "Recipient ID Number"
      },
      "RECIPIENT_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Recipient Address 1"
      },
      "RECIPIENT_APT_NO": {
        "kind": "text",
        "label": "Recipient Apt No"
      },
      "RECIPIENT_ADDRESS2": {
        "kind": "text",
        "label": "Recipient Address 2"
      },
      "RECIPIENT_ADDRESS3": {
        "kind": "text",
        "label": "Recipient Address 3"
      },
      "RECIPIENT_CITY": {
        "kind": "text",
        "required": true,
        "label": "Recipient City"
      },
      "RECIPIENT_STATE": {
        "kind": "state",
        "required": true,
        "label": "Recipient State"
      },
      "RECIPIENT_ZIP": {
        "kind": "text",
        "required": true,
        "label": "Recipient ZIP"
      },
      "FOREIGN_COUNTRY_INDICATOR": {
        "kind": "checkbox",
        "label": "Foreign Country Indicator"
      },
      "FOREIGN_COUNTRY": {
        "kind": "text",
        "label": "Foreign Country"
      },
      "ACCOUNT_NUMBER": {
        "kind": "text",
        "label": "Account Number"
      },
      "BOX1_INTEREST_INCOME_NOT_IN_BOX3": {
        "kind": "amount",
        "label": "Interest Income Not in Box 3"
      },
      "BOX2_EARLY_WITHDRAWAL_PENALTY": {
        "kind": "amount",
        "label": "Early Withdrawal Penalty"
      },
      "BOX3_INTEREST_ON_US_SAVINGS": {
        "kind": "amount",
        "label": "Interest on US Savings"
      },
      "BOX4_FED_INCOME_TAX_WITHHELD": {
        "kind": "amount",
        "label": "Federal Income Tax Withheld"
      },
      "BOX5_INVESTMENT_EXPENSES": {
        "kind": "amount",
        "label": "Investment Expenses"
      },
      "BOX6_FOREIGN_TAX_PAID": {
        "kind": "amount",
        "label": "Foreign Tax Paid"
      },
      "BOX7_FOREIGN_COUNTRY_US_POSSESSION": {
        "kind": "text",
        "label": "Foreign Country US Possession"
      },
      "BOX8_TAX_EXEMPT_INTEREST": {
        "kind": "amount",
        "label": "Tax-Exempt Interest"
      },
      "BOX9_SPEC_PRIVATE_ACT_BOND_INT": {
        "kind": "amount",
        "label": "Specified Private Activity Bond Interest"
      },
      "BOX10_MARKET_DISCOUNT_BOND": {
        "kind": "amount",
        "label": "Market Discount Bond"
      },
      "BOX11_BOND_PREMIUM": {
        "kind": "amount",
        "label": "Bond Premium"
      },
      "BOX12_BOND_PREMIUM_ON_TREASURY": {
        "kind": "amount",
        "label": "Bond Premium on Treasury"
      },
      "BOX13_TAX_EXEMPT_BOND": {
        "kind": "amount",
        "label": "Tax-Exempt Bond"
      },
      "BOX14_TAX_EXEMPT_BOND_CUSIP": {
        "kind": "text",
        "label": "Tax-Exempt Bond CUSIP"
      },
      "BOX15_STATE": {
        "kind": "text",
        "label": "State"
      },
      "BOX15_STATE2": {
        "kind": "text",
        "label": "State 2"
      },
      "BOX16_STATE_ID": {
        "kind": "text",
        "label": "State ID"
      },
      "BOX16_STATE_ID2": {
        "kind": "text",
        "label": "State ID 2"
      },
      "BOX17_STATE_TAX_WITHHELD": {
        "kind": "amount",
        "decimal": true,
        "label": "State Tax Withheld"
      },
      "BOX17_STATE_TAX_WITHHELD2": {
        "kind": "amount",
        "label": "State Tax Withheld 2"
      },
      "FATCA_REQUIREMENT": {
        "kind": "checkbox",
        "label": "FATCA Requirement"
      },
      "1099_YEAR": {
        "kind": "text",
        "label": "1099 Year"
      },
      "SITE_ID": {
        "kind": "text",
        "label": "Site ID"
      },
      "FILER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Filer Name"
      },
      "FILER_NAME2": {
        "kind": "text",
        "label": "Filer Name 2"
      },
      "FILER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Filer Address 1"
      },
      "FILER_SUITE_NO": {
        "kind": "text",
        "label": "Filer Suite No"
      },
      "FILER_ADDRESS2": {
        "kind": "text",
        "label": "Filer Address 2"
      },
      "JOB_NUMBER": {
        "kind": "text",
        "label": "Job Number"
      },
      "FILER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Filer City"
      },
      "FILER_STATE": {
        "kind": "state",
        "required": true,
        "label": "Filer State"
      },
      "FILER_COUNTRY": {
        "kind": "text",
        "label": "Filer Country"
      },
      "FILER_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Filer ZIP"
      },
      "FILER_PHONE": {
        "kind": "text",
        "required": true,
        "label": "Filer Phone"
      },
      "FILER_FEDERAL_ID_NUMBER": {
        "kind": "text",
        "label": "Filer Federal ID Number"
      },
      "BOX_2ND_TIN_NOT_IND": {
        "kind": "checkbox",
        "label": "2nd TIN Not IND"
      },
      "createdBy": {
        "kind": "text"
      },
      "userId": {
        "kind": "text"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "1099-K": {
      "SITE_ID": {
        "kind": "text",
        "label": "Site ID"
      },
      "1099_YEAR": {
        "kind": "text",
        "label": "1099 Year"
      },
      "JOB_NUMBER": {
        "kind": "text",
        "label": "Job Number"
      },
      "FILER_FEDERAL_ID_NUMBER": {
        "kind": "text",
        "required": true,
        "label": "Filer Federal ID Number"
      },
      "FILER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Filer Name"
      },
      "FILER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Filer Address 1"
      },
      "FILER_SUITE_NO": {
        "kind": "text",
        "label": "Filer Room / Suite No"
      },
      "FILER_ADDRESS2": {
        "kind": "text",
        "label": "Filer Address 2"
      },
      "FILER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Filer City"
      },
      "FILER_STATE": {
        "kind": "state",
        "required": true,
        "label": "Filer State"
      },
      "FILER_COUNTRY": {
        "kind": "text",
        "label": "Filer Country"
      },
      "FILER_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Filer ZIP Code"
      },
      "FILER_PHONE": {
        "kind": "text",
        "required": true,
        "label": "Filer Phone"
      },
      "FILER_NAME2": {
        "kind": "text",
        "label": "Filer Name 2"
      },
      "RECIPIENT_ID_TYPE": {
        "kind": "idType",
        "label": "Recipient ID Type"
      },
      "RECIPIENT_FIRST_NAME": {
        "kind": "text",
        "label": "Recipient First Name"
      },
      "RECIPIENT_MIDDLE_NAME": {
        "kind": "text",
        "label": "Recipient Middle Name"
      },
      "RECIPIENT_LAST_NAME": {
        "kind": "text",
        "label": "Recipient Last Name"
      },
      "RECIPIENT_SUFFIX": {
        "kind": "text",
        "label": "Recipient Suffix"
      },
      "RECIPIENT_BUSINESS_NAME1": {
        "kind": "text",
        "label": "Recipient Business Name 1"
      },
      "RECIPIENT_BUSINESS_NAME2": {
        "kind": "text",
        "label": "Recipient Business Name 2"
      },
      "PAYEE_TIN": {
        "kind": "tin",
        "label": "Payee TIN"
      },
      "PAYEE_NAME": {
        "kind": "text",
        "label": "Payee Name"
      },
      "PAYEE_NAME2": {
        "kind": "text",
        "label": "Payee Name 2"
      },
      "PAYEE_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Payee Address 1"
      },
      "PAYEE_APT_NO": {
        "kind": "text",
        "label": "Payee Apt No"
      },
      "PAYEE_ADDRESS2": {
        "kind": "text",
        "label": "Payee Address 2"
      },
      "PAYEE_CITY": {
        "kind": "text",
        "required": true,
        "label": "Payee City"
      },
      "PAYEE_STATE": {
        "kind": "state",
        "required": true,
        "label": "Payee State"
      },
      "PAYEE_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Payee ZIP Code"
      },
      "PAYEE_EMAIL": {
        "kind": "text",
        "label": "Payee Email"
      },
      "FOREIGN_COUNTRY": {
        "kind": "text",
        "label": "Foreign Country"
      },
      "FOREIGN_COUNTRY_INDICATOR": {
        "kind": "checkbox",
        "label": "Foreign Country Indicator"
      },
      "TYPE_OF_PAYMENT_INDICATOR_PAYMENT_CARD": {
        "kind": "checkbox",
        "label": "Type of Payment Indicator (Payment Card)"
      },
      "TYPE_OF_PAYMENT_INDICATOR_THIRD_PARTY": {
        "kind": "checkbox",
        "label": "Type of Payment Indicator (Third Party)"
      },
      "TYPE_OF_FILER_INDICATOR_PSE": {
        "kind": "checkbox",
        "label": "Type of Filer Indicator (PSE)"
      },
      "TYPE_OF_FILER_INDICATOR_3RD_PARTY": {
        "kind": "checkbox",
        "label": "Type of Filer Indicator (3rd Party)"
      },
      "BOX1A_GROSS_AMT_PAYMENT_CARD_TPN_TRANS": {
        "kind": "amount",
        "label": "Box 1a Gross Amount Payment Card/TPN Transactions"
      },
      "BOX1B_CARD_NOT_PRESENT_TRANS": {
        "kind": "amount",
        "label": "Box 1b Card Not Present Transactions"
      },
      "BOX1C_CASH_TIPS": {
        "kind": "amount",
        "label": "Cash Tips (Box 1c)"
      },
      "BOX1D_TTOC1": {
        "kind": "text",
        "label": "TTOC Code 1 (Box 1d)"
      },
      "BOX1D_TTOC2": {
        "kind": "text",
        "label": "TTOC Code 2 (Box 1d)"
      },
      "BOX2_MERCHANT_CAT_CODE": {
        "kind": "text",
        "label": "Box 2 Merchant Category Code"
      },
      "BOX3_NO_PAYMENT_TRANSACTIONS": {
        "kind": "amount",
        "label": "Box 3 Number of Payment Transactions"
      },
      "BOX4_FED_INCOME_TAX_WTHLD": {
        "kind": "amount",
        "label": "Box 4 Federal Income Tax Withheld"
      },
      "BOX5A_JANUARY": {
        "kind": "amount",
        "label": "Box 5a January"
      },
      "BOX5B_FEBRUARY": {
        "kind": "amount",
        "label": "Box 5b February"
      },
      "BOX5C_MARCH": {
        "kind": "amount",
        "label": "Box 5c March"
      },
      "BOX5D_APRIL": {
        "kind": "amount",
        "label": "Box 5d April"
      },
      "BOX5E_MAY": {
        "kind": "amount",
        "label": "Box 5e May"
      },
      "BOX5F_JUNE": {
        "kind": "amount",
        "label": "Box 5f June"
      },
      "BOX5G_JULY": {
        "kind": "amount",
        "label": "Box 5g July"
      },
      "BOX5H_AUGUST": {
        "kind": "amount",
        "label": "Box 5h August"
      },
      "BOX5I_SEPTEMBER": {
        "kind": "amount",
        "label": "Box 5i September"
      },
      "BOX5J_OCTOBER": {
        "kind": "amount",
        "label": "Box 5j October"
      },
      "BOX5K_NOVEMBER": {
        "kind": "amount",
        "label": "Box 5k November"
      },
      "BOX5L_DECEMBER": {
        "kind": "amount",
        "label": "Box 5l December"
      },
      "BOX6A_STATE": {
        "kind": "text",
        "label": "Box 6A State"
      },
      "BOX6B_STATE": {
        "kind": "text",
        "label": "Box 6B State"
      },
      "BOX7A_STATE_ID_NO": {
        "kind": "text",
        "label": "Box 7A State ID Number"
      },
      "BOX7B_STATE_ID_NO": {
        "kind": "text",
        "label": "Box 7B State ID Number"
      },
      "BOX8A_STATE_TAX": {
        "kind": "amount",
        "label": "Box 8A State Income Tax Withheld"
      },
      "BOX8B_STATE_TAX": {
        "kind": "amount",
        "label": "Box 8B State Income Tax Withheld"
      },
      "PSE_NAME": {
        "kind": "text",
        "required": true,
        "label": "PSE Name"
      },
      "PSE_TELEPHONE": {
        "kind": "text",
        "required": true,
        "label": "PSE Telephone"
      },
      "PAYEE_INTERNAL_ACCT_NO": {
        "kind": "text",
        "label": "Payee Internal Account Number"
      },
      "createdBy": {
        "kind": "text"
      },
      "userId": {
        "kind": "text"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "1099-LTC": {
      "SITE_ID": {
        "kind": "text",
        "label": "Site ID"
      },
      "POLICYHOLDER_ID_TYPE": {
        "kind": "idType",
        "label": "Policyholder ID Type"
      },
      "POLICYHOLDER_FIRST_NAME": {
        "kind": "text",
        "label": "Policyholder First Name"
      },
      "POLICYHOLDER_MIDDLE_NAME": {
        "kind": "text",
        "label": "Policyholder Middle Name"
      },
      "POLICYHOLDER_LAST_NAME": {
        "kind": "text",
        "label": "Policyholder Last Name"
      },
      "POLICYHOLDER_SUFFIX": {
        "kind": "text",
        "label": "Policyholder Suffix"
      },
      "POLICYHOLDER_BUSINESS_NAME1": {
        "kind": "text",
        "label": "Policyholder Business Name 1"
      },
      "POLICYHOLDER_BUSINESS_NAME2": {
        "kind": "text",
        "label": "Policyholder Business Name 2"
      },
      "POLICYHOLDER_TAX_ID": {
        "kind": "tin",
        "label": "Policyholder Tax ID"
      },
      "POLICYHOLDER_STREET_ADDRESS_1": {
        "kind": "text",
        "required": true,
        "label": "Policyholder Street Address 1"
      },
      "POLICYHOLDER_STREET_ADDRESS_2": {
        "kind": "text",
        "label": "Policyholder Street Address 2"
      },
      "POLICYHOLDER_APT_NO": {
        "kind": "text",
        "label": "Policyholder Apt No"
      },
      "POLICYHOLDER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Policyholder City"
      },
      "POLICYHOLDER_STATE": {
        "kind": "state",
        "required": true,
        "label": "Policyholder State"
      },
      "POLICYHOLDER_ZIP_CODE": {
        "kind": "zip",
        "required": true,
        "label": "Policyholder Zip Code"
      },
      "INSURED_SSN": {
        "kind": "tin",
        "required": true,
        "label": "Insured SSN"
      },
      "INSURED_NAME": {
        "kind": "text",
        "required": true,
        "label": "Insured Name"
      },
      "INSURED_STREET_ADDRESS_1": {
        "kind": "text",
        "required": true,
        "label": "Insured Street Address 1"
      },
      "INSURED_STREET_ADDRESS_2": {
        "kind": "text",
        "label": "Insured Street Address 2"
      },
      "INSURED_CITY": {
        "kind": "text",
        "required": true,
        "label": "Insured City"
      },
      "INSURED_STATE": {
        "kind": "state",
        "required": true,
        "label": "Insured State"
      },
      "INSURED_ZIP_CODE": {
        "kind": "zip",
        "required": true,
        "label": "Insured Zip Code"
      },
      "INSURED_ACCOUNT_NUMBER": {
        "kind": "text",
        "label": "Insured Account Number"
      },
      "MAILING_ADDRESS_INDICATOR": {
        "kind": "text",
        "label": "Mailing Address Indicator"
      },
      "BOX1_GROSS_BENEFITS_PAID": {
        "kind": "amount",
        "label": "Box 1 Gross Benefits Paid"
      },
      "BOX2_ACCELERATED_DEATH_BENEFITS_PAID": {
        "kind": "amount",
        "label": "Box 2 Accelerated Death Benefits Paid"
      },
      "PAYMENT_TYPE": {
        "kind": "amount",
        "label": "Payment Type"
      },
      "QUALIFIED_CONTRACT": {
        "kind": "checkbox",
        "label": "Qualified Contract"
      },
      "CHRONICALLY_ILL": {
        "kind": "checkbox",
        "label": "Chronically Ill or Terminal"
      },
      "PAYER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Payer Name"
      },
      "PAYER_STREET_ADDRESS_1": {
        "kind": "text",
        "required": true,
        "label": "Payer Street Address 1"
      },
      "PAYER_STREET_ADDRESS_2": {
        "kind": "text",
        "label": "Payer Street Address 2"
      },
      "PAYER_COUNTRY": {
        "kind": "text",
        "label": "Payer Country"
      },
      "PAYER_SUITE_NO": {
        "kind": "text",
        "label": "Payer Suite No"
      },
      "FOREIGN_COUNTRY": {
        "kind": "text",
        "label": "Foreign Country"
      },
      "FOREIGN_COUNTRY_INDICATOR": {
        "kind": "checkbox",
        "label": "Foreign Country Indicator"
      },
      "PAYER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Payer City"
      },
      "PAYER_STATE": {
        "kind": "state",
        "required": true,
        "label": "Payer State"
      },
      "PAYER_ZIP_CODE": {
        "kind": "zip",
        "required": true,
        "label": "Payer Zip Code"
      },
      "JOB_NUMBER": {
        "kind": "text",
        "label": "Job Number"
      },
      "PAYER_PHONE": {
        "kind": "text",
        "label": "Payer Phone"
      },
      "PAYERS_TIN": {
        "kind": "tin",
        "required": true,
        "label": "Payer's TIN"
      },
      "1099_YEAR": {
        "kind": "text",
        "label": "1099 Year"
      },
      "DATE_CERTIFIED": {
        "kind": "text",
        "label": "Date Certified"
      },
      "REIMBURSED_AMOUNT": {
        "kind": "checkbox",
        "decimal": true,
        "label": "Reimbursed Amount"
      },
      "TERMINAL_ILL": {
        "kind": "checkbox",
        "label": "Terminally Ill"
      },
      "PER_DIEM": {
        "kind": "checkbox",
        "decimal": true,
        "label": "Per Diem"
      },
      "createdBy": {
        "kind": "text"
      },
      "userId": {
        "kind": "text"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "1099-MISC": {
      "SITE_ID": {
        "kind": "text",
        "required": true,
        "label": "Site ID"
      },
      "JOB_NUMBER": {
        "kind": "text",
        "label": "Job Number"
      },
      "1099_YEAR": {
        "kind": "text",
        "required": true,
        "label": "1099 Year"
      },
      "FILER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Filer Name"
      },
      "FILER_NAME2": {
        "kind": "text",
        "label": "Filer Name 2"
      },
      "FILER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Filer Address 1"
      },
      "FILER_SUITE_NO": {
        "kind": "text",
        "label": "Filer Suite No"
      },
      "FILER_ADDRESS2": {
        "kind": "text",
        "label": "Filer Address 2"
      },
      "FILER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Filer City"
      },
      "FILER_STATE": {
        "kind": "state",
        "required": true,
        "label": "Filer State"
      },
      "FILER_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Filer ZIP"
      },
      "FILER_COUNTRY": {
        "kind": "text",
        "label": "Filer Country"
      },
      "FILER_PHONE": {
        "kind": "text",
        "required": true,
        "label": "Filer Phone"
      },
      "FILER_FEDERAL_ID_NUMBER": {
        "kind": "text",
        "required": true,
        "label": "Filer Federal ID Number"
      },
      "BOX_2ND_TIN_NOT_IND": {
        "kind": "checkbox",
        "label": "2nd TIN Not Indicator"
      },
      "BOX1_RENTS": {
        "kind": "amount",
        "label": "Rents"
      },
      "BOX2_ROYALTIES": {
        "kind": "amount",
        "label": "Royalties"
      },
      "BOX3_OTHER_INCOME": {
        "kind": "amount",
        "label": "Other Income"
      },
      "BOX4_FED_INCOME_TAX_WITHHELD": {
        "kind": "amount",
        "label": "Federal Income Tax Withheld"
      },
      "BOX5_FISHING_BOAT_PROCEEDS": {
        "kind": "amount",
        "label": "Fishing Boat Proceeds"
      },
      "BOX6_MEDICAL_HEALTHCARE_PAYMENTS": {
        "kind": "amount",
        "label": "Medical Healthcare Payments"
      },
      "BOX7_DIRECT_SALES_IND": {
        "kind": "checkbox",
        "label": "Direct Sales Indicator"
      },
      "BOX8_SUBSTITUTE_PAYMENTS": {
        "kind": "amount",
        "label": "Substitute Payments"
      },
      "BOX9_CROP_INSURANCE_PROCEEDS": {
        "kind": "amount",
        "label": "Crop Insurance Proceeds"
      },
      "BOX10_GROSS_PROCEEDS_ATTORNEY": {
        "kind": "amount",
        "label": "Gross Proceeds Paid to Attorney"
      },
      "BOX11_FISH_PURCHASED_RESALE": {
        "kind": "amount",
        "label": "Fish Purchased for Resale"
      },
      "BOX12_SECTION_409A_DEFERRALS": {
        "kind": "amount",
        "label": "Section 409A Deferral"
      },
      "BOX13A_CASH_TIPS": {
        "kind": "amount",
        "label": "Cash Tips"
      },
      "BOX13B_TTOC": {
        "kind": "text",
        "label": "TTOC Code 1"
      },
      "BOX13B_TTOC2": {
        "kind": "text",
        "label": "TTOC Code 2"
      },
      "BOX14_OVERTIME_COMPENSATION": {
        "kind": "amount",
        "label": "Overtime Compensation"
      },
      "BOX13_FATCA_REQUIREMENT": {
        "kind": "checkbox",
        "label": "FATCA Requirement"
      },
      "BOX15_NONQUALIFIED_COMPENSATION": {
        "kind": "amount",
        "label": "Nonqualified Compensation"
      },
      "BOX16_STATE_TAX_WITHHELD1": {
        "kind": "amount",
        "label": "State Tax Withheld 1"
      },
      "BOX16_STATE_TAX_WITHHELD2": {
        "kind": "amount",
        "label": "State Tax Withheld 2"
      },
      "BOX17_PAYER_STATE_NUMBER1": {
        "kind": "text",
        "label": "Payer State Number 1"
      },
      "BOX17_PAYER_STATE_NUMBER2": {
        "kind": "text",
        "label": "Payer State Number 2"
      },
      "BOX18_STATE_INCOME1": {
        "kind": "amount",
        "label": "State Income 1"
      },
      "BOX18_STATE_INCOME2": {
        "kind": "amount",
        "label": "State Income 2"
      },
      "FOREIGN_COUNTRY": {
        "kind": "text",
        "label": "Foreign Country"
      },
      "FOREIGN_COUNTRY_INDICATOR": {
        "kind": "checkbox",
        "label": "Foreign Country Indicator"
      },
      "ACCOUNT_NUMBER": {
        "kind": "text",
        "label": "Account Number"
      },
      "RECIPIENT_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Recipient Address 1"
      },
      "RECIPIENT_APT_NO": {
        "kind": "text",
        "label": "Recipient Apt No"
      },
      "RECIPIENT_ADDRESS2": {
        "kind": "text",
        "label": "Recipient Address 2"
      },
      "RECIPIENT_ADDRESS3": {
        "kind": "text",
        "label": "Recipient Address 3"
      },
      "RECIPIENT_CITY": {
        "kind": "text",
        "required": true,
        "label": "Recipient City"
      },
      "RECIPIENT_STATE": {
        "kind": "state",
        "required": true,
        "label": "Recipient State"
      },
      "RECIPIENT_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Recipient ZIP"
      },
      "RECIPIENT_EMAIL": {
        "kind": "text",
        "label": "Recipient Email"
      },
      "RECIPIENT_ID_TYPE": {
        "kind": "idType",
        "label": "Recipient ID Type"
      },
      "RECIPIENT_FIRST_NAME": {
        "kind": "text",
        "label": "Recipient First Name"
      },
      "RECIPIENT_MIDDLE_NAME": {
        "kind": "text",
        "label": "Recipient Middle Name"
      },
      "RECIPIENT_LAST_NAME": {
        "kind": "text",
        "label": "Recipient Last Name"
      },
      "RECIPIENT_SUFFIX": {
        "kind": "text",
        "label": "Recipient Suffix"
      },
      "RECIPIENT_BUSINESS_NAME1": {
        "kind": "text",
        "label": "Recipient Business Name 1"
      },
      "RECIPIENT_BUSINESS_NAME2": {
        "kind": "text",
        "label": "Recipient Business Name 2"
      },
      "RECIPIENT_ID_NUMBER": {
        "kind": "tin",
        "label": "Recipient ID Number"
      },
      "createdBy": {
        "kind": "text"
      },
      "userId": {
        "kind": "text"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "1099-NEC": {
      "1099_YEAR": {
        "kind": "amount",
        "required": true,
        "label": "1099 Year"
      },
      "JOB_NUMBER": {
        "kind": "text",
        "label": "Job Number"
      },
      "ACCOUNT_NUMBER": {
        "kind": "text",
        "label": "Account Number"
      },
      "BOX1A_NONEMPLOYEE_COMPENSATION": {
        "kind": "amount",
        "label": "Nonemployee Compensation (Box 1a)"
      },
      "BOX1B_CASH_TIPS": {
        "kind": "amount",
        "label": "Cash Tips (Box 1b)"
      },
      "BOX1C_TTOC1": {
        "kind": "text",
        "label": "TTOC Code 1 (Box 1c)"
      },
      "BOX1C_TTOC2": {
        "kind": "text",
        "label": "TTOC Code 2 (Box 1c)"
      },
      "BOX1D_OVERTIME_COMPENSATION": {
        "kind": "amount",
        "label": "Overtime Compensation (Box 1d)"
      },
      "BOX2_DIRECT_SALES": {
        "kind": "checkbox",
        "label": "Direct Sales"
      },
      "BOX3_GOLDEN_PARACHUTE": {
        "kind": "amount",
        "label": "Golden Parachute"
      },
      "BOX4_FED_INCOME_TAX_WITHHELD": {
        "kind": "amount",
        "label": "Federal Income Tax Withheld"
      },
      "BOX5_STATE_TAX_WITHHELD1": {
        "kind": "amount",
        "label": "State Tax Withheld 1"
      },
      "BOX5_STATE_TAX_WITHHELD2": {
        "kind": "amount",
        "label": "State Tax Withheld 2"
      },
      "BOX6_PAYER_STATE_NUMBER1": {
        "kind": "text",
        "label": "Payer State Number 1"
      },
      "BOX6_PAYER_STATE_NUMBER2": {
        "kind": "text",
        "label": "Payer State Number 2"
      },
      "BOX7_STATE_INCOME1": {
        "kind": "amount",
        "label": "State Income 1"
      },
      "BOX7_STATE_INCOME2": {
        "kind": "amount",
        "label": "State Income 2"
      },
      "BOX_2ND_TIN_NOT_IND": {
        "kind": "checkbox",
        "label": "2nd TIN not Indicator"
      },
      "FILER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Filer Address 1"
      },
      "FILER_SUITE_NO": {
        "kind": "text",
        "label": "Filer Suite No"
      },
      "FILER_ADDRESS2": {
        "kind": "text",
        "label": "Filer Address 2"
      },
      "FILER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Filer City"
      },
      "FILER_FEDERAL_ID_NUMBER": {
        "kind": "tin",
        "required": true,
        "label": "Filer Federal ID Number"
      },
      "FILER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Filer Name"
      },
      "FILER_NAME2": {
        "kind": "text",
        "label": "Filer Name 2"
      },
      "FILER_PHONE": {
        "kind": "text",
        "required": true,
        "label": "Filer Phone"
      },
      "FILER_STATE": {
        "kind": "text",
        "label": "Filer State"
      },
      "FILER_COUNTRY": {
        "kind": "text",
        "label": "Filer Country"
      },
      "FILER_ZIP": {
        "kind": "text",
        "required": true,
        "label": "Filer ZIP"
      },
      "FOREIGN_COUNTRY": {
        "kind": "text",
        "label": "Foreign Country"
      },
      "FOREIGN_COUNTRY_INDICATOR": {
        "kind": "checkbox",
        "label": "Foreign Country Indicator"
      },
      "RECIPIENT_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Recipient Address 1"
      },
      "RECIPIENT_APT_NO": {
        "kind": "text",
        "label": "Recipient Apt No"
      },
      "RECIPIENT_ADDRESS2": {
        "kind": "text",
        "label": "Recipient Address 2"
      },
      "RECIPIENT_ADDRESS3": {
        "kind": "text",
        "label": "Recipient Address 3"
      },
      "RECIPIENT_CITY": {
        "kind": "text",
        "required": true,
        "label": "Recipient City"
      },
      "RECIPIENT_EMAIL": {
        "kind": "text",
        "label": "Recipient Email"
      },
      "RECIPIENT_ID_TYPE": {
        "kind": "idType",
        "label": "Recipient ID Type"
      },
      "RECIPIENT_FIRST_NAME": {
        "kind": "text",
        "label": "Recipient First Name"
      },
      "RECIPIENT_MIDDLE_NAME": {
        "kind": "text",
        "label": "Recipient Middle Name"
      },
      "RECIPIENT_LAST_NAME": {
        "kind": "text",
        "label": "Recipient Last Name"
      },
      "RECIPIENT_SUFFIX": {
        "kind": "text",
        "label": "Recipient Suffix"
      },
      "RECIPIENT_BUSINESS_NAME1": {
        "kind": "text",
        "label": "Recipient Business Name 1"
      },
      "RECIPIENT_BUSINESS_NAME2": {
        "kind": "text",
        "label": "Recipient Business Name 2"
      },
      "RECIPIENT_NAME": {
        "kind": "text",
        "label": "Recipient Name 1 (combined)"
      },
      "RECIPIENT_NAME2": {
        "kind": "text",
        "label": "Recipient Name 2 (combined)"
      },
      "RECIPIENT_ID_NUMBER": {
        "kind": "tin",
        "label": "Recipient ID Number"
      },
      "RECIPIENT_STATE": {
        "kind": "text",
        "label": "Recipient State"
      },
      "RECIPIENT_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Recipient ZIP"
      },
      "SITE_ID": {
        "kind": "amount",
        "required": true,
        "label": "Site ID"
      },
      "createdBy": {
        "kind": "text"
      },
      "userId": {
        "kind": "text"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "1099-R": {
      "JOB_NUMBER": {
        "kind": "text",
        "label": "Job Number"
      },
      "SITE_ID": {
        "kind": "text",
        "required": true,
        "label": "Site ID"
      },
      "Filer_Name2": {
        "kind": "text",
        "label": "Filer Name 2"
      },
      "Filer_Room_Suite_No": {
        "kind": "text",
        "label": "Filer Room / Suite No"
      },
      "Filer_Country": {
        "kind": "text",
        "label": "Filer Country"
      },
      "RECIPIENT_APT_NO": {
        "kind": "text",
        "label": "Recipient Apt No"
      },
      "BOX7_TRUMP_ACCOUNT": {
        "kind": "checkbox",
        "label": "Trump account"
      },
      "BOX7_EARNINGS_EXCESS_CONTRIBUTIONS": {
        "kind": "text",
        "label": "Box 7 Earnings Excess Contributions"
      },
      "RECIPIENT_ID_TYPE": {
        "kind": "idType",
        "label": "Recipient ID Type"
      },
      "RECIPIENT_FIRST_NAME": {
        "kind": "text",
        "label": "Recipient First Name"
      },
      "RECIPIENT_MIDDLE_NAME": {
        "kind": "text",
        "label": "Recipient Middle Name"
      },
      "RECIPIENT_LAST_NAME": {
        "kind": "text",
        "label": "Recipient Last Name"
      },
      "RECIPIENT_SUFFIX": {
        "kind": "text",
        "label": "Recipient Suffix"
      },
      "RECIPIENT_BUSINESS_NAME1": {
        "kind": "text",
        "label": "Recipient Business Name 1"
      },
      "RECIPIENT_BUSINESS_NAME2": {
        "kind": "text",
        "label": "Recipient Business Name 2"
      },
      "FILER_FEDERAL_ID_NUMBER": {
        "kind": "tin",
        "required": true,
        "label": "Filer Federal ID Number"
      },
      "Filer_Name": {
        "kind": "text",
        "required": true,
        "label": "Filer Name"
      },
      "Filer_Address1": {
        "kind": "text",
        "required": true,
        "label": "Filer Address 1"
      },
      "Filer_Address2": {
        "kind": "text",
        "label": "Filer Address 2"
      },
      "Filer_City": {
        "kind": "text",
        "required": true,
        "label": "Filer City"
      },
      "Filer_State": {
        "kind": "text",
        "required": true,
        "label": "Filer State"
      },
      "Filer_Zip": {
        "kind": "text",
        "required": true,
        "label": "Filer ZIP"
      },
      "Filer_Phone": {
        "kind": "text",
        "required": true,
        "label": "Filer Phone"
      },
      "RECIPIENT_ID_NUMBER": {
        "kind": "tin",
        "label": "Recipient ID Number"
      },
      "RECIPIENT_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Recipient Address 1"
      },
      "RECIPIENT_ADDRESS2": {
        "kind": "text",
        "label": "Recipient Address 2"
      },
      "RECIPIENT_CITY": {
        "kind": "text",
        "required": true,
        "label": "Recipient City"
      },
      "RECIPIENT_STATE": {
        "kind": "text",
        "label": "Recipient State"
      },
      "RECIPIENT_ZIP": {
        "kind": "text",
        "required": true,
        "label": "Recipient ZIP"
      },
      "RECIPIENT_EMAIL": {
        "kind": "text",
        "label": "Recipient Email"
      },
      "ACCOUNT_NUMBER": {
        "kind": "text",
        "label": "Account Number"
      },
      "BOX1_GROSS_DISTRIBUTION": {
        "kind": "amount",
        "label": "Gross Distribution"
      },
      "BOX2A_TAXABLE_AMOUNT": {
        "kind": "amount",
        "label": "Taxable Amount"
      },
      "BOX2B_TAXABLE_AMOUNT_IND": {
        "kind": "checkbox",
        "label": "Taxable Amount Indicator"
      },
      "BOX2B_TOTAL_DISTRIBUTION_IND": {
        "kind": "checkbox",
        "label": "Total Distribution Indicator"
      },
      "BOX3_CAPITAL_GAIN": {
        "kind": "amount",
        "label": "Capital Gain"
      },
      "BOX4_FEDERAL_INCOME_TAX_WITHHELD": {
        "kind": "amount",
        "label": "Federal Income Tax Withheld"
      },
      "BOX5_EMPLOYEE_CONTRIBUTIONS": {
        "kind": "amount",
        "label": "Employee Contributions"
      },
      "BOX6_UNREALIZED_APPRECIATION": {
        "kind": "amount",
        "label": "Unrealized Appreciation"
      },
      "BOX7_DISTRIBUTION_CODES": {
        "kind": "text",
        "required": true,
        "label": "Distribution Codes"
      },
      "BOX7_IRA_SEP_SIMPLE_IND": {
        "kind": "checkbox",
        "label": "IRA SEP SIMPLE Indicator"
      },
      "BOX8_OTHER": {
        "kind": "amount",
        "label": "Other"
      },
      "BOX8_PERCENT": {
        "kind": "amount",
        "label": "Percent"
      },
      "BOX9A_PERCENT_OF_DISTRIBUTION": {
        "kind": "amount",
        "label": "Percent of Distribution"
      },
      "BOX9B_TOT_EMPLOYEE_CONTRIBUTIONS": {
        "kind": "amount",
        "label": "Total Employee Contributions"
      },
      "BOX10_AMOUNT_ALLOCABLE": {
        "kind": "amount",
        "label": "Amount Allocable"
      },
      "BOX11_ROTH_CONTRIBUTION": {
        "kind": "text",
        "label": "Roth Contribution"
      },
      "BOX12_FATCA_REQUIREMENT": {
        "kind": "checkbox",
        "label": "FATCA Requirement"
      },
      "BOX13_DATE_OF_PAYMENT": {
        "kind": "text",
        "label": "Date of Payment"
      },
      "BOX14_STATE_TAX_WITHHELD1": {
        "kind": "amount",
        "label": "State Tax Withheld 1"
      },
      "BOX14_STATE_TAX_WITHHELD2": {
        "kind": "amount",
        "label": "State Tax Withheld 2"
      },
      "BOX15_PAYERS_STATE_NO1": {
        "kind": "text",
        "label": "Payer's State Number 1"
      },
      "BOX15_PAYERS_STATE_NO2": {
        "kind": "text",
        "label": "Payer's State Number 2"
      },
      "BOX16_STATE_DISTRIBUTION1": {
        "kind": "amount",
        "label": "State Distribution 1"
      },
      "BOX16_STATE_DISTRIBUTION2": {
        "kind": "amount",
        "label": "State Distribution 2"
      },
      "BOX17_LOCAL_TAX_WITHHELD1": {
        "kind": "amount",
        "label": "Local Tax Withheld 1"
      },
      "BOX17_LOCAL_TAX_WITHHELD2": {
        "kind": "amount",
        "label": "Local Tax Withheld 2"
      },
      "BOX18_NAME_OF_LOCALITY1": {
        "kind": "text",
        "label": "Name of Locality 1"
      },
      "BOX18_NAME_OF_LOCALITY2": {
        "kind": "text",
        "label": "Name of Locality 2"
      },
      "BOX19_LOCAL_DISTRIBUTION1": {
        "kind": "amount",
        "label": "Local Distribution 1"
      },
      "BOX19_LOCAL_DISTRIBUTION2": {
        "kind": "amount",
        "label": "Local Distribution 2"
      },
      "1099_YEAR": {
        "kind": "text",
        "required": true,
        "label": "Year"
      },
      "FOREIGN_COUNTRY": {
        "kind": "text",
        "label": "Foreign Country"
      },
      "FOREIGN_COUNTRY_INDICATOR": {
        "kind": "checkbox",
        "label": "Foreign Country indicator"
      },
      "createdBy": {
        "kind": "text"
      },
      "userId": {
        "kind": "text"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "1099-SA": {
      "SITE_ID": {
        "kind": "text",
        "required": true,
        "label": "Site ID"
      },
      "FILER_FEDERAL_ID_NUMBER": {
        "kind": "text",
        "required": true,
        "label": "Filer Federal ID Number"
      },
      "FILER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Filer Name"
      },
      "FILER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Filer Address 1"
      },
      "FILER_ADDRESS2": {
        "kind": "text",
        "label": "Filer Address 2"
      },
      "FILER_COUNTRY": {
        "kind": "text",
        "label": "Filer Country"
      },
      "FILER_SUITE_NO": {
        "kind": "text",
        "label": "Filer Suite No"
      },
      "FILER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Filer City"
      },
      "FILER_STATE": {
        "kind": "text",
        "required": true,
        "label": "Filer State"
      },
      "FILER_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Filer ZIP"
      },
      "JOB_NUMBER": {
        "kind": "text",
        "label": "Job Number"
      },
      "FILER_PHONE": {
        "kind": "text",
        "label": "Filer Phone"
      },
      "RECIPIENT_ID_TYPE": {
        "kind": "idType",
        "label": "Recipient ID Type"
      },
      "RECIPIENT_FIRST_NAME": {
        "kind": "text",
        "label": "Recipient First Name"
      },
      "RECIPIENT_MIDDLE_NAME": {
        "kind": "text",
        "label": "Recipient Middle Name"
      },
      "RECIPIENT_LAST_NAME": {
        "kind": "text",
        "label": "Recipient Last Name"
      },
      "RECIPIENT_SUFFIX": {
        "kind": "text",
        "label": "Recipient Suffix"
      },
      "RECIPIENT_BUSINESS_NAME1": {
        "kind": "text",
        "label": "Recipient Business Name 1"
      },
      "RECIPIENT_BUSINESS_NAME2": {
        "kind": "text",
        "label": "Recipient Business Name 2"
      },
      "BOX_RECIPIENT_ID_NUMBER": {
        "kind": "tin",
        "label": "Recipient ID Number"
      },
      "RECIPIENT_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Recipient Address"
      },
      "RECIPIENT_ADDRESS2": {
        "kind": "text",
        "label": "Recipient Address 1"
      },
      "RECIPIENT_APT_NO": {
        "kind": "text",
        "label": "Recipient Apt No"
      },
      "RECIPIENT_ADDRESS3": {
        "kind": "text",
        "label": "Recipient Address 3"
      },
      "RECIPIENT_CITY": {
        "kind": "text",
        "required": true,
        "label": "Recipient City"
      },
      "RECIPIENT_STATE": {
        "kind": "state",
        "required": true,
        "label": "Recipient State"
      },
      "RECIPIENT_ZIP": {
        "kind": "zip",
        "required": true,
        "label": "Recipient ZIP"
      },
      "EMAIL": {
        "kind": "text",
        "label": "Email"
      },
      "BOX_ACCOUNT_NUMBER": {
        "kind": "text",
        "label": "Account Number"
      },
      "BOX1_GROSS_DISTRIBUTION": {
        "kind": "text",
        "label": "Gross Distribution"
      },
      "BOX2_EARNING_ON_EXCESS": {
        "kind": "amount",
        "label": "Earnings on Excess Contributions"
      },
      "BOX3_DISTRIBUTION_CODE": {
        "kind": "text",
        "label": "Distribution Code"
      },
      "BOX4_FMV_ON_DATE_OF_DEATH": {
        "kind": "amount",
        "label": "FMV on Date of Death"
      },
      "BOX5_HSA": {
        "kind": "checkbox",
        "label": "HSA"
      },
      "BOX5_ARCHER_MSA": {
        "kind": "checkbox",
        "label": "Archer MSA"
      },
      "FOREIGN_COUNTRY": {
        "kind": "text",
        "label": "Foreign Country"
      },
      "FOREIGN_COUNTRY_INDICATOR": {
        "kind": "checkbox",
        "label": "Foreign Country Indicator"
      },
      "BOX5_MA_MSA": {
        "kind": "checkbox",
        "label": "MA MSA"
      },
      "1099_YEAR": {
        "kind": "text",
        "required": true,
        "label": "1099 Year"
      },
      "createdBy": {
        "kind": "text"
      },
      "userId": {
        "kind": "text"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    },
    "5498-SA": {
      "SITE_ID": {
        "kind": "text",
        "required": true,
        "label": "Site ID"
      },
      "FILER_FEDERAL_ID_NUMBER": {
        "kind": "text",
        "required": true,
        "label": "Filer Federal ID Number"
      },
      "FILER_NAME": {
        "kind": "text",
        "required": true,
        "label": "Filer Name"
      },
      "FILER_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Filer Address 1"
      },
      "FILER_ADDRESS2": {
        "kind": "text",
        "label": "Filer Address 2"
      },
      "FILER_COUNTRY": {
        "kind": "text",
        "label": "Filer Country"
      },
      "FILER_SUITE_NO": {
        "kind": "text",
        "label": "Filer Suite No"
      },
      "FILER_CITY": {
        "kind": "text",
        "required": true,
        "label": "Filer City"
      },
      "FILER_STATE": {
        "kind": "text",
        "required": true,
        "label": "Filer State"
      },
      "FILER_ZIP": {
        "kind": "text",
        "required": true,
        "label": "Filer ZIP"
      },
      "JOB_NUMBER": {
        "kind": "text",
        "label": "Job Number"
      },
      "FILER_PHONE": {
        "kind": "text",
        "label": "Filer Phone"
      },
      "RECIPIENT_ID_TYPE": {
        "kind": "idType",
        "label": "Recipient ID Type"
      },
      "RECIPIENT_FIRST_NAME": {
        "kind": "text",
        "label": "Recipient First Name"
      },
      "RECIPIENT_MIDDLE_NAME": {
        "kind": "text",
        "label": "Recipient Middle Name"
      },
      "RECIPIENT_LAST_NAME": {
        "kind": "text",
        "label": "Recipient Last Name"
      },
      "RECIPIENT_SUFFIX": {
        "kind": "text",
        "label": "Recipient Suffix"
      },
      "RECIPIENT_BUSINESS_NAME1": {
        "kind": "text",
        "label": "Recipient Business Name 1"
      },
      "RECIPIENT_BUSINESS_NAME2": {
        "kind": "text",
        "label": "Recipient Business Name 2"
      },
      "RECIPIENT_ID_NUMBER": {
        "kind": "tin",
        "label": "Recipient ID Number"
      },
      "RECIPIENT_ADDRESS1": {
        "kind": "text",
        "required": true,
        "label": "Recipient Address 1"
      },
      "RECIPIENT_ADDRESS2": {
        "kind": "text",
        "label": "Recipient Address 2"
      },
      "RECIPIENT_APT_NO": {
        "kind": "text",
        "label": "Recipient Apt No"
      },
      "RECIPIENT_ADDRESS3": {
        "kind": "text",
        "label": "Recipient Address 3"
      },
      "RECIPIENT_CITY": {
        "kind": "text",
        "required": true,
        "label": "Recipient City"
      },
      "RECIPIENT_STATE": {
        "kind": "state",
        "required": true,
        "label": "Recipient State"
      },
      "RECIPIENT_ZIP": {
        "kind": "text",
        "required": true,
        "label": "Recipient ZIP"
      },
      "ACCOUNT_NUMBER": {
        "kind": "text",
        "label": "Account Number"
      },
      "BOX1_ARCHER_MSA_CONTRIBUTIONS": {
        "kind": "amount",
        "label": "Archer MSA Contributions"
      },
      "BOX2_TOTAL_CONTRIBUTIONS": {
        "kind": "amount",
        "label": "Total Contributions"
      },
      "BOX3_TOTAL_HSA_ARCHER_CONTRIBUTIONS": {
        "kind": "amount",
        "label": "Total HSA/Archer Contributions"
      },
      "BOX4_ROLLOVER_CONTRIBUTIONS": {
        "kind": "amount",
        "label": "Rollover Contributions"
      },
      "BOX5_FAIR_MARKET_VALUE_HSA_ARCHER_MSA_MA_MSA": {
        "kind": "amount",
        "label": "Fair Market Value HSA/Archer MSA/MA MSA"
      },
      "BOX6_HSA": {
        "kind": "checkbox",
        "label": "HSA"
      },
      "BOX6_ARCHER_MSA": {
        "kind": "checkbox",
        "label": "Archer MSA"
      },
      "BOX6_MA_MSA": {
        "kind": "checkbox",
        "label": "MA MSA"
      },
      "FOREIGN_COUNTRY": {
        "kind": "text",
        "label": "Foreign Country"
      },
      "FOREIGN_COUNTRY_INDICATOR": {
        "kind": "checkbox",
        "label": "Foreign Country Indicator"
      },
      "1099_YEAR": {
        "kind": "text",
        "required": true,
        "label": "1099 Year"
      },
      "createdBy": {
        "kind": "text"
      },
      "userId": {
        "kind": "text"
      },
      "corrected": {
        "kind": "text"
      },
      "createdAt": {
        "kind": "text"
      }
    }
  }
};

/** Forms whose definition core-api does not version by year. */
export const FIELD_RULES_BASE: Record<string, FormFieldRules> = {
  "3921": {
    "RECIPIENT_ID_NUMBER": {
      "kind": "text",
      "required": true,
      "label": "Recipient ID Number"
    },
    "EMPLOYEE_NAME": {
      "kind": "text",
      "required": true,
      "label": "Employee Name"
    },
    "EMPLOYEE_SSN": {
      "kind": "tin",
      "required": true,
      "label": "Employee SSN"
    },
    "EMPLOYEE_NAME2": {
      "kind": "text",
      "label": "Employee Name 2"
    },
    "EMPLOYEE_ADDRESS1": {
      "kind": "text",
      "required": true,
      "label": "Employee Address 1"
    },
    "EMPLOYEE_ADDRESS2": {
      "kind": "text",
      "label": "Employee Address 2"
    },
    "EMPLOYEE_ADDRESS3": {
      "kind": "text",
      "label": "Employee Address 3"
    },
    "EMPLOYEE_CITY": {
      "kind": "text",
      "required": true,
      "label": "Employee City"
    },
    "EMPLOYEE_STATE": {
      "kind": "text",
      "required": true,
      "label": "Employee State"
    },
    "EMPLOYEE_ZIP": {
      "kind": "text",
      "required": true,
      "label": "Employee ZIP"
    },
    "ACCOUNT_NUMBER": {
      "kind": "text",
      "label": "Account Number"
    },
    "transectionId": {
      "kind": "text",
      "label": "Transaction ID"
    },
    "BOX1_DATE_GRANTED": {
      "kind": "date",
      "label": "Date Granted"
    },
    "BOX2_DATE_EXERCISED": {
      "kind": "date",
      "label": "Date Exercised"
    },
    "BOX3_EXE_PRICE_PER_SHARE": {
      "kind": "text",
      "decimal": true,
      "label": "Exercise Price per Share"
    },
    "BOX4_FAIR_MARKET_VALUE_ON_SHARE": {
      "kind": "text",
      "decimal": true,
      "label": "Fair Market Value per Share"
    },
    "BOX5_SHARES_TRANSFERRED": {
      "kind": "amount",
      "label": "Shares Transferred"
    },
    "BOX6_EIN": {
      "kind": "text",
      "label": "Employer Identification Number"
    },
    "BOX6_COMPANY": {
      "kind": "text",
      "label": "Company"
    },
    "BOX6_ADDRESS": {
      "kind": "text",
      "label": "Company Address"
    },
    "BOX_6D": {
      "kind": "text",
      "label": "Box 6D"
    },
    "1099_YEAR": {
      "kind": "amount",
      "required": true,
      "label": "1099 Year"
    },
    "SITE_ID": {
      "kind": "text",
      "required": true,
      "label": "Site ID"
    },
    "FILER_NAME": {
      "kind": "text",
      "required": true,
      "label": "Filer Name"
    },
    "FILER_NAME2": {
      "kind": "text",
      "label": "Filer Name 2"
    },
    "FILER_ADDRESS1": {
      "kind": "text",
      "required": true,
      "label": "Filer Address 1"
    },
    "FILER_ADDRESS2": {
      "kind": "text",
      "label": "Filer Address 2"
    },
    "FILER_ADDRESS3": {
      "kind": "text",
      "label": "Filer Address 3"
    },
    "FILER_CITY": {
      "kind": "text",
      "required": true,
      "label": "Filer City"
    },
    "FILER_STATE": {
      "kind": "text",
      "required": true,
      "label": "Filer State"
    },
    "FILER_ZIP": {
      "kind": "text",
      "required": true,
      "label": "Filer ZIP"
    },
    "FILER_PHONE": {
      "kind": "text",
      "label": "Filer Phone"
    },
    "FILER_FEDERAL_ID_NUMBER": {
      "kind": "text",
      "required": true,
      "label": "Filer Federal ID Number"
    },
    "corrected": {
      "kind": "text"
    },
    "createdAt": {
      "kind": "text"
    }
  },
  "3922": {
    "RECIPIENT_ID_NUMBER": {
      "kind": "tin",
      "required": true,
      "label": "Recipient ID Number"
    },
    "SITE_ID": {
      "kind": "text",
      "required": true,
      "label": "Site ID"
    },
    "1099_YEAR": {
      "kind": "amount",
      "required": true,
      "label": "Tax Year"
    },
    "CORPORATION_NAME_1": {
      "kind": "text",
      "required": true,
      "label": "Corporation Name 1"
    },
    "CORPORATION_NAME_2": {
      "kind": "text",
      "label": "Corporation Name 2"
    },
    "CORPORATION_ADDRESS": {
      "kind": "text",
      "required": true,
      "label": "Corporation Address"
    },
    "CORPORATION_ADDRESS2": {
      "kind": "text",
      "label": "Corporation Address 2"
    },
    "CORPORATION_CITY": {
      "kind": "text",
      "required": true,
      "label": "Corporation City"
    },
    "CORPORATION_STATE": {
      "kind": "state",
      "required": true,
      "label": "Corporation State"
    },
    "CORPORATION_ZIP": {
      "kind": "zip",
      "required": true,
      "label": "Corporation ZIP"
    },
    "CORPORATION_PHONE": {
      "kind": "text",
      "label": "Corporation Phone"
    },
    "CORPORATION_EIN": {
      "kind": "text",
      "label": "Corporation EIN"
    },
    "EMPLOYEE_SSN": {
      "kind": "tin",
      "required": true,
      "label": "Employee SSN"
    },
    "EMPLOYEE_NAME": {
      "kind": "text",
      "required": true,
      "label": "Employee Name"
    },
    "EMPLOYEE_ADDRESS_1": {
      "kind": "text",
      "required": true,
      "label": "Employee Address 1"
    },
    "EMPLOYEE_ADDRESS_2": {
      "kind": "text",
      "label": "Employee Address 2"
    },
    "EMPLOYEE_ADDRESS_3": {
      "kind": "text",
      "label": "Employee Address 3"
    },
    "EMPLOYEE_CITY": {
      "kind": "text",
      "required": true,
      "label": "Employee City"
    },
    "EMPLOYEE_STATE": {
      "kind": "state",
      "required": true,
      "label": "Employee State"
    },
    "EMPLOYEE_ZIP": {
      "kind": "zip",
      "required": true,
      "label": "Employee ZIP"
    },
    "EMPLOYEE_ACCOUNT_NO": {
      "kind": "text",
      "label": "Employee Account Number"
    },
    "BOX1_DATE_GRANTED": {
      "kind": "text",
      "label": "Box 1"
    },
    "BOX2_DATE_EXERCISED": {
      "kind": "text",
      "label": "Box 2"
    },
    "BOX3_FAIR_MARKET_VALUE_GRANT_DATE": {
      "kind": "amount",
      "decimal": true,
      "label": "Box 3"
    },
    "BOX4_FAIR_MARKET_VALUE_EXE_DATE": {
      "kind": "amount",
      "decimal": true,
      "label": "Box 4"
    },
    "BOX5_EXERCISE_PRICE_PAID": {
      "kind": "amount",
      "decimal": true,
      "label": "Box 5"
    },
    "BOX6_NO_SHARES_TRANSFERRED": {
      "kind": "amount",
      "label": "Box 6"
    },
    "BOX7_DATE_TITLE_TRANSFERRED": {
      "kind": "text",
      "label": "Box 7"
    },
    "BOX8_EXERCISE_PRICE_PAID_ON_GRANT_DATE": {
      "kind": "amount",
      "decimal": true,
      "label": "Box 8"
    },
    "ROW_INDEX": {
      "kind": "text",
      "label": "Row Index"
    },
    "transectionId": {
      "kind": "text",
      "label": "Transaction ID"
    },
    "corrected": {
      "kind": "text"
    },
    "createdAt": {
      "kind": "text"
    }
  },
  "1099-MISC": {
    "SITE_ID": {
      "kind": "text",
      "required": true,
      "label": "Site ID"
    },
    "JOB_NUMBER": {
      "kind": "text",
      "label": "Job Number"
    },
    "1099_YEAR": {
      "kind": "text",
      "required": true,
      "label": "1099 Year"
    },
    "SSN/TIN": {
      "kind": "text",
      "label": "SSN/TIN"
    },
    "FILER_NAME": {
      "kind": "text",
      "required": true,
      "label": "Filer Name"
    },
    "FILER_ADDRESS1": {
      "kind": "text",
      "required": true,
      "label": "Filer Address 1"
    },
    "FILER_ADDRESS2": {
      "kind": "text",
      "label": "Filer Address 2"
    },
    "FILER_CITY": {
      "kind": "text",
      "required": true,
      "label": "Filer City"
    },
    "FILER_STATE": {
      "kind": "state",
      "required": true,
      "label": "Filer State"
    },
    "FILER_ZIP": {
      "kind": "zip",
      "required": true,
      "label": "Filer ZIP"
    },
    "FILER_PHONE": {
      "kind": "text",
      "required": true,
      "label": "Filer Phone"
    },
    "FILER_FEDERAL_ID_NUMBER": {
      "kind": "text",
      "required": true,
      "label": "Filer Federal ID Number"
    },
    "BOX_2ND_TIN_NOT_IND": {
      "kind": "checkbox",
      "label": "2nd TIN Not Indicator"
    },
    "BOX1_RENTS": {
      "kind": "amount",
      "label": "Rents"
    },
    "BOX2_ROYALTIES": {
      "kind": "amount",
      "label": "Royalties"
    },
    "BOX3_OTHER_INCOME": {
      "kind": "amount",
      "label": "Other Income"
    },
    "BOX4_FED_INCOME_TAX_WITHHELD": {
      "kind": "amount",
      "label": "Federal Income Tax Withheld"
    },
    "BOX5_FISHING_BOAT_PROCEEDS": {
      "kind": "amount",
      "label": "Fishing Boat Proceeds"
    },
    "BOX6_MEDICAL_HEALTHCARE_PAYMENTS": {
      "kind": "amount",
      "label": "Medical Healthcare Payments"
    },
    "BOX7_DIRECT_SALES_IND": {
      "kind": "checkbox",
      "label": "Direct Sales Indicator"
    },
    "BOX8_SUBSTITUTE_PAYMENTS": {
      "kind": "amount",
      "label": "Substitute Payments"
    },
    "BOX9_CROP_INSURANCE_PROCEEDS": {
      "kind": "amount",
      "label": "Crop Insurance Proceeds"
    },
    "BOX10_GROSS_PROCEEDS_ATTORNEY": {
      "kind": "amount",
      "label": "Gross Proceeds Paid to Attorney"
    },
    "BOX11_FISH_PURCHASED_RESALE": {
      "kind": "amount",
      "label": "Fish Purchased for Resale"
    },
    "BOX12_SECTION_409A_DEFERRAL": {
      "kind": "amount",
      "label": "Section 409A Deferral"
    },
    "BOX13_FATCA_REQUIREMENT": {
      "kind": "checkbox",
      "label": "FATCA Requirement"
    },
    "BOX15_NONQUALIFIED_COMPENSATION": {
      "kind": "amount",
      "label": "Nonqualified Compensation"
    },
    "BOX16_STATE_TAX_WITHHELD1": {
      "kind": "amount",
      "label": "State Tax Withheld 1"
    },
    "BOX16_STATE_TAX_WITHHELD2": {
      "kind": "amount",
      "label": "State Tax Withheld 2"
    },
    "BOX17_PAYER_STATE_NUMBER1": {
      "kind": "text",
      "label": "Payer State Number 1"
    },
    "BOX17_PAYER_STATE_NUMBER2": {
      "kind": "text",
      "label": "Payer State Number 2"
    },
    "BOX18_STATE_INCOME1": {
      "kind": "amount",
      "label": "State Income 1"
    },
    "BOX18_STATE_INCOME2": {
      "kind": "amount",
      "label": "State Income 2"
    },
    "FOREIGN_COUNTRY": {
      "kind": "text",
      "label": "Foreign Country"
    },
    "FOREIGN_COUNTRY_INDICATOR": {
      "kind": "checkbox",
      "label": "Foreign Country Indicator"
    },
    "ACCOUNT_NUMBER": {
      "kind": "text",
      "label": "Account Number"
    },
    "RECIPIENT_NAME": {
      "kind": "text",
      "required": true,
      "label": "Recipient Name"
    },
    "RECIPIENT_NAME2": {
      "kind": "text",
      "label": "Recipient Name 2"
    },
    "RECIPIENT_ADDRESS1": {
      "kind": "text",
      "required": true,
      "label": "Recipient Address 1"
    },
    "RECIPIENT_ADDRESS2": {
      "kind": "text",
      "label": "Recipient Address 2"
    },
    "RECIPIENT_ADDRESS3": {
      "kind": "text",
      "label": "Recipient Address 3"
    },
    "RECIPIENT_CITY": {
      "kind": "text",
      "required": true,
      "label": "Recipient City"
    },
    "RECIPIENT_STATE": {
      "kind": "state",
      "required": true,
      "label": "Recipient State"
    },
    "RECIPIENT_ZIP": {
      "kind": "zip",
      "required": true,
      "label": "Recipient ZIP"
    },
    "RECIPIENT_EMAIL": {
      "kind": "text",
      "label": "Recipient Email"
    },
    "RECIPIENT_ID_NUMBER": {
      "kind": "tin",
      "required": true,
      "label": "RECIPIENT_ID_NUMBER"
    },
    "createdBy": {
      "kind": "text"
    },
    "userId": {
      "kind": "text"
    },
    "corrected": {
      "kind": "text"
    },
    "createdAt": {
      "kind": "text"
    }
  },
  "1042-S": {
    "RECIPIENT_KEY": {
      "kind": "text",
      "required": true,
      "label": "RECIPIENT_KEY"
    },
    "DATABASE_LOAD_ID": {
      "kind": "text",
      "label": "DATABASE_LOAD_ID"
    },
    "SITE_ID": {
      "kind": "text",
      "label": "SITE_ID"
    },
    "STATUS": {
      "kind": "text",
      "label": "STATUS"
    },
    "TYPE": {
      "kind": "text",
      "label": "TYPE"
    },
    "ONLINE_UPDATE_IND": {
      "kind": "text",
      "label": "ONLINE_UPDATE_IND"
    },
    "TAX_YEAR": {
      "kind": "text",
      "label": "TAX_YEAR"
    },
    "UID": {
      "kind": "text",
      "label": "UID"
    },
    "WITHHOLDING_AGENTS_EIN_INDICATOR": {
      "kind": "text",
      "label": "WITHHOLDING_AGENTS_EIN_INDICATOR"
    },
    "BOX12A_WITHHOLDING_AGENT_EIN": {
      "kind": "text",
      "label": "BOX12A_WITHHOLDING_AGENT_EIN"
    },
    "BOX12B_WITHHOLDING_AGENT_CH3_STATUS_CODE": {
      "kind": "text",
      "label": "BOX12B_WITHHOLDING_AGENT_CH3_STATUS_CODE"
    },
    "BOX12C_WITHHOLDING_AGENT_CH4_STATUS_CODE": {
      "kind": "text",
      "label": "BOX12C_WITHHOLDING_AGENT_CH4_STATUS_CODE"
    },
    "BOX12D_WITHHOLDING_AGENT_NAME1": {
      "kind": "text",
      "label": "BOX12D_WITHHOLDING_AGENT_NAME1"
    },
    "BOX12D_WITHHOLDING_AGENT_NAME2": {
      "kind": "text",
      "label": "BOX12D_WITHHOLDING_AGENT_NAME2"
    },
    "BOX12D_WITHHOLDING_AGENT_NAME3": {
      "kind": "text",
      "label": "BOX12D_WITHHOLDING_AGENT_NAME3"
    },
    "BOX12E_WITHHOLDING_AGENTS_GIIN": {
      "kind": "text",
      "label": "BOX12E_WITHHOLDING_AGENTS_GIIN"
    },
    "BOX12F_WITHHOLDING_AGENT_COUNTRY_CODE": {
      "kind": "text",
      "label": "BOX12F_WITHHOLDING_AGENT_COUNTRY_CODE"
    },
    "BOX12G_WITHHOLDING_AGENT_FOREIGN_TIN": {
      "kind": "text",
      "label": "BOX12G_WITHHOLDING_AGENT_FOREIGN_TIN"
    },
    "BOX12H_WITHHOLDING_AGENT_ADDRESS1": {
      "kind": "text",
      "label": "BOX12H_WITHHOLDING_AGENT_ADDRESS1"
    },
    "BOX12H_WITHHOLDING_AGENT_ADDRESS2": {
      "kind": "text",
      "label": "BOX12H_WITHHOLDING_AGENT_ADDRESS2"
    },
    "BOX12I_WITHHOLDING_AGENT_CITY": {
      "kind": "text",
      "label": "BOX12I_WITHHOLDING_AGENT_CITY"
    },
    "BOX12I_WITHHOLDING_AGENT_STATE_CODE": {
      "kind": "text",
      "label": "BOX12I_WITHHOLDING_AGENT_STATE_CODE"
    },
    "BOX12I_WITHHOLDING_AGENT_PROVINCE_CODE": {
      "kind": "text",
      "label": "BOX12I_WITHHOLDING_AGENT_PROVINCE_CODE"
    },
    "BOX12I_WITHHOLDING_AGENT_ZIP": {
      "kind": "text",
      "label": "BOX12I_WITHHOLDING_AGENT_ZIP"
    },
    "BOX12I_WITHHOLDING_AGENT_COUNTRY": {
      "kind": "text",
      "label": "BOX12I_WITHHOLDING_AGENT_COUNTRY"
    },
    "BOX12I_WITHHOLDING_AGENT_FOREIGN_INDICATOR": {
      "kind": "text",
      "label": "BOX12I_WITHHOLDING_AGENT_FOREIGN_INDICATOR"
    },
    "WITHHOLDING_AGENTS_CONTACT_NAME": {
      "kind": "text",
      "label": "WITHHOLDING_AGENTS_CONTACT_NAME"
    },
    "WITHHOLDING_AGENTS_DEPARTMENT_TITLE": {
      "kind": "text",
      "label": "WITHHOLDING_AGENTS_DEPARTMENT_TITLE"
    },
    "CONTACT_TELEPHONE_NUMBER_AND_EXTENSION": {
      "kind": "text",
      "label": "CONTACT_TELEPHONE_NUMBER_AND_EXTENSION"
    },
    "BOX3_CHAPTER_INDICATOR": {
      "kind": "text",
      "label": "BOX3_CHAPTER_INDICATOR"
    },
    "BOX1_INCOME_CODE": {
      "kind": "text",
      "label": "BOX1_INCOME_CODE"
    },
    "BOX2_GROSS_INCOME": {
      "kind": "amount",
      "decimal": true,
      "label": "Gross Income"
    },
    "BOX3A_EXEMPTION_CODE": {
      "kind": "text",
      "label": "BOX3A_EXEMPTION_CODE"
    },
    "BOX4A_EXEMPTION_CODE": {
      "kind": "text",
      "label": "BOX4A_EXEMPTION_CODE"
    },
    "BOX3B_TAX_RATE": {
      "kind": "text",
      "label": "BOX3B_TAX_RATE"
    },
    "BOX4B_TAX_RATE": {
      "kind": "text",
      "label": "BOX4B_TAX_RATE"
    },
    "BOX5_WITHHOLDING_ALLOWANCE": {
      "kind": "amount",
      "decimal": true,
      "label": "Withholding Allowance"
    },
    "BOX6_NET_INCOME": {
      "kind": "amount",
      "decimal": true,
      "label": "Net Income"
    },
    "BOX7A_FEDERAL_TAX_WITHHELD": {
      "kind": "amount",
      "decimal": true,
      "label": "Federal Tax Withheld"
    },
    "BOX7B_TAX_NOT_DEP_W_IRS": {
      "kind": "text",
      "label": "BOX7B_TAX_NOT_DEP_W_IRS"
    },
    "BOX7C_WITHHOLD_RES_PARTNERSHIP_INRST": {
      "kind": "text",
      "label": "BOX7C_WITHHOLD_RES_PARTNERSHIP_INRST"
    },
    "BOX7D_QUALIFIED_INTERMEDIARY_IND": {
      "kind": "checkbox",
      "label": "BOX7D_QUALIFIED_INTERMEDIARY_IND"
    },
    "BOX8_TAX_WITHHELD_OTHER_AGENTS": {
      "kind": "amount",
      "decimal": true,
      "label": "Tax Withheld by Other Agents"
    },
    "BOX9_AMOUNT_REPAID_RECIPIENT": {
      "kind": "amount",
      "decimal": true,
      "label": "Amount Repaid to Recipient"
    },
    "BOX10_TOTAL_WITHOLDING_CREDIT": {
      "kind": "amount",
      "decimal": true,
      "label": "Total Withholding Credit"
    },
    "BOX11_TAX_PAID_WITHHOLDING_AGENT": {
      "kind": "amount",
      "decimal": true,
      "label": "Tax Paid by Withholding Agent"
    },
    "BOX13A_RECIPIENT_NAME1": {
      "kind": "text",
      "label": "BOX13A_RECIPIENT_NAME1"
    },
    "BOX13A_RECIPIENT_NAME2": {
      "kind": "text",
      "label": "BOX13A_RECIPIENT_NAME2"
    },
    "BOX13A_RECIPIENT_NAME3": {
      "kind": "text",
      "label": "BOX13A_RECIPIENT_NAME3"
    },
    "BOX13B_RECIPIENT_COUNTRY_CODE": {
      "kind": "text",
      "label": "BOX13B_RECIPIENT_COUNTRY_CODE"
    },
    "BOX13C_RECIPIENT_ADDRESS1": {
      "kind": "text",
      "label": "BOX13C_RECIPIENT_ADDRESS1"
    },
    "BOX13C_RECIPIENT_ADDRESS2": {
      "kind": "text",
      "label": "BOX13C_RECIPIENT_ADDRESS2"
    },
    "BOX13D_RECIPIENT_CITY": {
      "kind": "text",
      "label": "BOX13D_RECIPIENT_CITY"
    },
    "BOX13D_RECIPIENT_STATE": {
      "kind": "text",
      "label": "BOX13D_RECIPIENT_STATE"
    },
    "BOX13D_RECIPIENT_PROVINCE": {
      "kind": "text",
      "label": "BOX13D_RECIPIENT_PROVINCE"
    },
    "BOX13D_RECIPIENT_ZIP": {
      "kind": "text",
      "label": "BOX13D_RECIPIENT_ZIP"
    },
    "BOX13D_RECIPIENT_COUNTRY": {
      "kind": "text",
      "label": "BOX13D_RECIPIENT_COUNTRY"
    },
    "BOX13D_RECIPIENT_FOREIGN_INDICATOR": {
      "kind": "text",
      "label": "BOX13D_RECIPIENT_FOREIGN_INDICATOR"
    },
    "BOX13E_RECIPIENT_US_TIN": {
      "kind": "text",
      "label": "BOX13E_RECIPIENT_US_TIN"
    },
    "BOX13F_RECIPIENT_CH3_STATUS_CODE": {
      "kind": "text",
      "label": "BOX13F_RECIPIENT_CH3_STATUS_CODE"
    },
    "BOX13G_RECIPIENT_CH4_STATUS_CODE": {
      "kind": "text",
      "label": "BOX13G_RECIPIENT_CH4_STATUS_CODE"
    },
    "BOX13H_RECIPIENTS_GIIN": {
      "kind": "text",
      "label": "BOX13H_RECIPIENTS_GIIN"
    },
    "BOX13I_RECIPIENTS_FOREIGN_TIN": {
      "kind": "text",
      "label": "BOX13I_RECIPIENTS_FOREIGN_TIN"
    },
    "BOX13J_RECIPIENT_LOB_CODE": {
      "kind": "text",
      "label": "BOX13J_RECIPIENT_LOB_CODE"
    },
    "BOX13K_RECIPIENTS_ACCT_NO": {
      "kind": "text",
      "label": "BOX13K_RECIPIENTS_ACCT_NO"
    },
    "BOX13L_RECIPIENT_DOB": {
      "kind": "text",
      "label": "BOX13L_RECIPIENT_DOB"
    },
    "BOX14A_PRIMARY_WITHHOLDING_AGENT_NAME1": {
      "kind": "text",
      "label": "BOX14A_PRIMARY_WITHHOLDING_AGENT_NAME1"
    },
    "BOX14A_PRIMARY_WITHHOLDING_AGENT_NAME2": {
      "kind": "text",
      "label": "BOX14A_PRIMARY_WITHHOLDING_AGENT_NAME2"
    },
    "BOX14B_PRIMARY_WITHHOLDING_AGENT_EIN": {
      "kind": "text",
      "label": "BOX14B_PRIMARY_WITHHOLDING_AGENT_EIN"
    },
    "BOX15_PRO_RATA_BASIS_REPORTING_INDICATOR": {
      "kind": "text",
      "label": "BOX15_PRO_RATA_BASIS_REPORTING_INDICATOR"
    },
    "BOX15A_INTERMEDIARY_EIN": {
      "kind": "text",
      "label": "BOX15A_INTERMEDIARY_EIN"
    },
    "BOX15B_INTERMEDIARY_CH3_STATUS_CODE": {
      "kind": "text",
      "label": "BOX15B_INTERMEDIARY_CH3_STATUS_CODE"
    },
    "BOX15C_INTERMEDIARY_CH4_STATUS_CODE": {
      "kind": "text",
      "label": "BOX15C_INTERMEDIARY_CH4_STATUS_CODE"
    },
    "BOX15D_INTERMEDIARY_ENTITY_NAME1": {
      "kind": "text",
      "label": "BOX15D_INTERMEDIARY_ENTITY_NAME1"
    },
    "BOX15D_INTERMEDIARY_ENTITY_NAME2": {
      "kind": "text",
      "label": "BOX15D_INTERMEDIARY_ENTITY_NAME2"
    },
    "BOX15D_INTERMEDIARY_ENTITY_NAME3": {
      "kind": "text",
      "label": "BOX15D_INTERMEDIARY_ENTITY_NAME3"
    },
    "BOX15E_INTERMEDIARY_ENTITY_GIIN": {
      "kind": "text",
      "label": "BOX15E_INTERMEDIARY_ENTITY_GIIN"
    },
    "BOX15F_INTERMEDIARY_COUNTRY_CODE": {
      "kind": "text",
      "label": "BOX15F_INTERMEDIARY_COUNTRY_CODE"
    },
    "BOX15G_INTERMEDIARY_FOREIGN_TIN": {
      "kind": "text",
      "label": "BOX15G_INTERMEDIARY_FOREIGN_TIN"
    },
    "BOX15H_INTERMEDIARY_ADDRESS1": {
      "kind": "text",
      "label": "BOX15H_INTERMEDIARY_ADDRESS1"
    },
    "BOX15H_INTERMEDIARY_ADDRESS2": {
      "kind": "text",
      "label": "BOX15H_INTERMEDIARY_ADDRESS2"
    },
    "BOX15F_INTERMEDIARY_FOREIGN_INDICATOR": {
      "kind": "text",
      "label": "BOX15F_INTERMEDIARY_FOREIGN_INDICATOR"
    },
    "BOX15I_INTERMEDIARY_CITY": {
      "kind": "text",
      "label": "BOX15I_INTERMEDIARY_CITY"
    },
    "BOX15I_INTERMEDIARY_STATE_CODE": {
      "kind": "text",
      "label": "BOX15I_INTERMEDIARY_STATE_CODE"
    },
    "BOX15I_INTERMEDIARY_PROVINCE_CODE": {
      "kind": "text",
      "label": "BOX15I_INTERMEDIARY_PROVINCE_CODE"
    },
    "BOX15I_INTERMEDIARY_COUNTRY": {
      "kind": "text",
      "label": "BOX15I_INTERMEDIARY_COUNTRY"
    },
    "BOX15I_INTERMEDIARY_ZIP": {
      "kind": "text",
      "label": "BOX15I_INTERMEDIARY_ZIP"
    },
    "BOX16A_PAYERS_NAME": {
      "kind": "text",
      "label": "BOX16A_PAYERS_NAME"
    },
    "BOX16B_PAYERS_TIN": {
      "kind": "text",
      "label": "BOX16B_PAYERS_TIN"
    },
    "BOX16C_PAYERS_GIIN": {
      "kind": "text",
      "label": "BOX16C_PAYERS_GIIN"
    },
    "BOX16D_PAYERS_CH3_STATUS_CODE": {
      "kind": "text",
      "label": "BOX16D_PAYERS_CH3_STATUS_CODE"
    },
    "BOX16E_PAYERS_CH4_STATUS_CODE": {
      "kind": "text",
      "label": "BOX16E_PAYERS_CH4_STATUS_CODE"
    },
    "BOX17A_STATE_INCOME_TAX_WITHHELD": {
      "kind": "text",
      "decimal": true,
      "label": "STATE INCOME TAX WITHHELD"
    },
    "BOX17B_PAYERS_STATE_TAX_NO": {
      "kind": "text",
      "label": "BOX17B_PAYERS_STATE_TAX_NO"
    },
    "BOX17C_NAME_OF_STATE": {
      "kind": "text",
      "label": "BOX17C_NAME_OF_STATE"
    },
    "US_FEDERAL_TAX_WITHHELD_INDICATOR": {
      "kind": "text",
      "label": "US_FEDERAL_TAX_WITHHELD_INDICATOR"
    },
    "EFILED_NUMBER": {
      "kind": "text",
      "label": "EFILED_NUMBER"
    },
    "PROOF_JOB_NUMBER": {
      "kind": "text",
      "label": "PROOF_JOB_NUMBER"
    },
    "PROOF_SEQ_ID": {
      "kind": "text",
      "label": "PROOF_SEQ_ID"
    },
    "PROOF_APPROVED_IND": {
      "kind": "text",
      "label": "PROOF_APPROVED_IND"
    },
    "1099_VIEWED_INDICATOR": {
      "kind": "text",
      "label": "1099_VIEWED_INDICATOR"
    },
    "1099_PRINTED_INDICATOR": {
      "kind": "text",
      "label": "1099_PRINTED_INDICATOR"
    },
    "1099_EFILED_INDICATOR": {
      "kind": "text",
      "label": "1099_EFILED_INDICATOR"
    },
    "PROC_TMPFLAG": {
      "kind": "text",
      "label": "PROC_TMPFLAG"
    },
    "Memo": {
      "kind": "text",
      "label": "Memo"
    },
    "Ammendment": {
      "kind": "text",
      "label": "Ammendment"
    },
    "Ammendment_Number": {
      "kind": "text",
      "label": "Ammendment_Number"
    },
    "VERSION": {
      "kind": "amount",
      "label": "VERSION"
    },
    "Last_Change_Date": {
      "kind": "text",
      "label": "Last_Change_Date"
    },
    "FORM_RELEASE_DATE": {
      "kind": "text",
      "label": "FORM_RELEASE_DATE"
    },
    "ONLINE_ACCESS": {
      "kind": "text",
      "label": "ONLINE_ACCESS"
    },
    "CREATED_BY": {
      "kind": "text",
      "label": "CREATED_BY"
    },
    "CREATED_DATE": {
      "kind": "text",
      "label": "CREATED_DATE"
    },
    "IPADDRESS": {
      "kind": "text",
      "label": "IPADDRESS"
    },
    "Comments": {
      "kind": "text",
      "label": "Comments"
    },
    "MODIFIED_BY": {
      "kind": "text",
      "label": "MODIFIED_BY"
    },
    "UPLOAD_FILE_ID": {
      "kind": "text",
      "label": "UPLOAD_FILE_ID"
    },
    "isDeleted": {
      "kind": "text",
      "label": "isDeleted"
    },
    "eFiled": {
      "kind": "text",
      "label": "eFiled"
    },
    "updated": {
      "kind": "text",
      "label": "updated"
    },
    "manual": {
      "kind": "text",
      "label": "manual"
    },
    "manualRecordId": {
      "kind": "amount",
      "label": "manualRecordId"
    },
    "sequence_id": {
      "kind": "text",
      "label": "sequence_id"
    },
    "refId": {
      "kind": "amount",
      "label": "refId"
    },
    "customerFileId": {
      "kind": "amount",
      "label": "customerFileId"
    },
    "id": {
      "kind": "amount",
      "label": "id"
    },
    "createdAt": {
      "kind": "text"
    },
    "UNIQUE_FORM_IDENTIFIER": {
      "kind": "text",
      "label": "UNIQUE_FORM_IDENTIFIER"
    }
  },
  "1098-E": {
    "SITE_ID": {
      "kind": "text",
      "required": true,
      "label": "Site ID"
    },
    "1098_YEAR": {
      "kind": "amount",
      "required": true,
      "label": "1098 Year"
    },
    "BORROWER_SSN": {
      "kind": "text",
      "required": true,
      "label": "Borrower SSN"
    },
    "BORROWER_NAME": {
      "kind": "text",
      "required": true,
      "label": "Borrower Name"
    },
    "BORROWER_NAME2": {
      "kind": "text",
      "label": "Borrower Name 2"
    },
    "JOB_NUMBER": {
      "kind": "text",
      "label": "Job Number"
    },
    "BORROWER_ADDRESS1": {
      "kind": "text",
      "required": true,
      "label": "Borrower Address 1"
    },
    "BORROWER_ADDRESS2": {
      "kind": "text",
      "label": "Borrower Address 2"
    },
    "BORROWER_CITY": {
      "kind": "text",
      "required": true,
      "label": "Borrower City"
    },
    "BORROWER_STATE": {
      "kind": "text",
      "required": true,
      "label": "Borrower State"
    },
    "BORROWER_ZIP": {
      "kind": "text",
      "required": true,
      "label": "Borrower ZIP"
    },
    "FOREIGN_COUNTRY_INDICATOR": {
      "kind": "checkbox",
      "label": "Foreign Country Indicator"
    },
    "FOREIGN_COUNTRY": {
      "kind": "text",
      "label": "Foreign Country"
    },
    "BOX1_STUDENT_LOAN_INTEREST": {
      "kind": "amount",
      "label": "Student Loan Interest"
    },
    "BOX2_PRE_2004_ORIGINATION_FEES": {
      "kind": "checkbox",
      "label": "Pre-2004 Origination Fees"
    },
    "BORROWER_ACCOUNT_NUMBER": {
      "kind": "text",
      "label": "Borrower Account Number"
    },
    "FILER_NAME": {
      "kind": "text",
      "required": true,
      "label": "Filer Name"
    },
    "FILER_ADDRESS1": {
      "kind": "text",
      "required": true,
      "label": "Filer Address 1"
    },
    "FILER_ADDRESS2": {
      "kind": "text",
      "label": "Filer Address 2"
    },
    "FILER_CITY": {
      "kind": "text",
      "required": true,
      "label": "Filer City"
    },
    "FILER_STATE": {
      "kind": "text",
      "required": true,
      "label": "Filer State"
    },
    "FILER_ZIP": {
      "kind": "text",
      "required": true,
      "label": "Filer ZIP"
    },
    "FILER_PHONE": {
      "kind": "text",
      "required": true,
      "label": "Filer Phone"
    },
    "FILER_FEDERAL_ID_NUMBER": {
      "kind": "text",
      "required": true,
      "label": "Filer Federal ID Number"
    },
    "createdBy": {
      "kind": "text"
    },
    "userId": {
      "kind": "text"
    },
    "corrected": {
      "kind": "text"
    },
    "createdAt": {
      "kind": "text"
    }
  },
  "1098-T": {
    "SITE_ID": {
      "kind": "text",
      "required": true,
      "label": "Site ID"
    },
    "FILER_FEDERAL_ID_NUMBER": {
      "kind": "text",
      "required": true,
      "label": "Filer Federal ID Number"
    },
    "FILER_NAME": {
      "kind": "text",
      "required": true,
      "label": "Filer Name"
    },
    "FILER_ADDRESS1": {
      "kind": "text",
      "required": true,
      "label": "Filer Address 1"
    },
    "FILER_ADDRESS2": {
      "kind": "text",
      "label": "Filer Address 2"
    },
    "FILER_ADDRESS3": {
      "kind": "text",
      "label": "Filer Address 3"
    },
    "FILER_CITY": {
      "kind": "text",
      "required": true,
      "label": "Filer City"
    },
    "FILER_STATE": {
      "kind": "state",
      "required": true,
      "label": "Filer State"
    },
    "FILER_ZIP": {
      "kind": "zip",
      "required": true,
      "label": "Filer ZIP"
    },
    "JOB_NUMBER": {
      "kind": "text",
      "label": "Job Number"
    },
    "FILER_PHONE": {
      "kind": "text",
      "required": true,
      "label": "Filer Phone"
    },
    "STUDENT_SSN": {
      "kind": "tin",
      "required": true,
      "label": "Student SSN"
    },
    "STUDENT_FIRST_NAME": {
      "kind": "text",
      "required": true,
      "label": "Student First Name"
    },
    "STUDENT_MIDDLE_NAME": {
      "kind": "text",
      "label": "Student Middle Name"
    },
    "STUDENT_LAST_NAME": {
      "kind": "text",
      "required": true,
      "label": "Student Last Name"
    },
    "STUDENT_ADDRESS1": {
      "kind": "text",
      "required": true,
      "label": "Student Address 1"
    },
    "STUDENT_ADDRESS2": {
      "kind": "text",
      "label": "Student Address 2"
    },
    "STUDENT_CITY": {
      "kind": "text",
      "required": true,
      "label": "Student City"
    },
    "STUDENT_STATE": {
      "kind": "state",
      "required": true,
      "label": "Student State"
    },
    "STUDENT_ZIP": {
      "kind": "zip",
      "required": true,
      "label": "Student ZIP"
    },
    "FOREIGN_COUNTRY_INDICATOR": {
      "kind": "checkbox",
      "label": "Foreign Country Indicator"
    },
    "FOREIGN_COUNTRY": {
      "kind": "text",
      "label": "Foreign Country"
    },
    "EMAIL": {
      "kind": "text",
      "label": "Email"
    },
    "SCHOOL_STUDENT_ID": {
      "kind": "text",
      "label": "School Student ID"
    },
    "BOX1_PAYMENTS_RECEIVED": {
      "kind": "amount",
      "label": "Payments Received"
    },
    "BOX4_ADJ_MADE_PRIOR_YR": {
      "kind": "amount",
      "label": "Adjustments Made Prior Year"
    },
    "BOX5_SCHOLARSHIP_GRANTS": {
      "kind": "amount",
      "label": "Scholarships or Grants"
    },
    "BOX6_ADJ_SCHOLARSHIP_PRIOR_YR": {
      "kind": "amount",
      "label": "Adjustments to Scholarships or Grants for Prior Year"
    },
    "BOX7_ACADEMIC_PERIOD_IND": {
      "kind": "checkbox",
      "label": "Academic Period Indicator"
    },
    "BOX8_HALF_TIME_STUDENT_IND": {
      "kind": "checkbox",
      "label": "Half-time Student Indicator"
    },
    "BOX9_GRADUATE_STUDENT_IND": {
      "kind": "checkbox",
      "label": "Graduate Student Indicator"
    },
    "BOX10_REIMBURSEMENT_OR_REFUND": {
      "kind": "amount",
      "label": "Reimbursement or Refund"
    },
    "SSN_SOLICITED": {
      "kind": "text",
      "label": "SSN Solicited"
    },
    "1098_YEAR": {
      "kind": "amount",
      "required": true,
      "label": "1098 Year"
    },
    "SSN/TIN": {
      "kind": "tin",
      "required": true,
      "label": "SSN/TIN"
    },
    "TOKEN": {
      "kind": "text",
      "label": "Token"
    },
    "STUDENT_CONSENT_DATE": {
      "kind": "text",
      "label": "Student Consent Date"
    },
    "createdBy": {
      "kind": "text"
    },
    "userId": {
      "kind": "text"
    },
    "corrected": {
      "kind": "text"
    },
    "createdAt": {
      "kind": "text"
    }
  },
  "1099-B": {
    "SITE_ID": {
      "kind": "text",
      "required": true,
      "label": "Site ID"
    },
    "FILER_FEDERAL_ID_NUMBER": {
      "kind": "tin",
      "required": true,
      "label": "Filer Federal ID Number"
    },
    "FILER_NAME": {
      "kind": "text",
      "required": true,
      "label": "Filer Name"
    },
    "FILER_ADDRESS1": {
      "kind": "text",
      "required": true,
      "label": "Filer Address 1"
    },
    "FILER_ADDRESS2": {
      "kind": "text",
      "label": "Filer Address 2"
    },
    "FILER_CITY": {
      "kind": "text",
      "required": true,
      "label": "Filer City"
    },
    "FILER_STATE": {
      "kind": "state",
      "required": true,
      "label": "Filer State"
    },
    "FILER_ZIP": {
      "kind": "zip",
      "required": true,
      "label": "Filer ZIP"
    },
    "FILER_PHONE": {
      "kind": "text",
      "required": true,
      "label": "Filer Phone"
    },
    "RECIPIENT_ID_NUMBER": {
      "kind": "tin",
      "required": true,
      "label": "Recipient ID Number"
    },
    "RECIPIENT_NAME1": {
      "kind": "text",
      "required": true,
      "label": "Recipient Name 1"
    },
    "RECIPIENT_NAME2": {
      "kind": "text",
      "required": true,
      "label": "Recipient Name 2"
    },
    "RECIPIENT_ADDRESS1": {
      "kind": "text",
      "required": true,
      "label": "Recipient Address 1"
    },
    "RECIPIENT_ADDRESS2": {
      "kind": "text",
      "label": "Recipient Address 2"
    },
    "RECIPIENT_CITY": {
      "kind": "text",
      "required": true,
      "label": "Recipient City"
    },
    "RECIPIENT_STATE": {
      "kind": "state",
      "required": true,
      "label": "Recipient State"
    },
    "RECIPIENT_ZIP": {
      "kind": "zip",
      "required": true,
      "label": "Recipient ZIP"
    },
    "FOREIGN_ADDRESS_INDICATOR": {
      "kind": "checkbox",
      "label": "Foreign Address Indicator"
    },
    "FOREIGN_CITY_PROVINCE_POSTAL": {
      "kind": "text",
      "label": "Foreign City"
    },
    "ACCOUNT_NUMBER": {
      "kind": "text",
      "label": "Account Number"
    },
    "TIN_NOTICE_INDICATOR": {
      "kind": "checkbox",
      "label": "TIN Notice Indicator"
    },
    "CUSIP_NUMBER": {
      "kind": "text",
      "label": "CUSIP Number"
    },
    "FATCA_REQUIREMENT": {
      "kind": "text",
      "label": "FATCA Requirement"
    },
    "FORM_8949_CHECKBOX": {
      "kind": "text",
      "label": "Form 8949 Checkbox"
    },
    "BOX_1A_DESCRIPTION": {
      "kind": "text",
      "label": "Description of property (Example: 100 sh. XYZ Co.)"
    },
    "BOX_1B_DATE_ACQUIRED": {
      "kind": "text",
      "label": "Date Acquired"
    },
    "BOX_1C_DATE_SOLD": {
      "kind": "text",
      "label": "Date Sold or Disposed"
    },
    "BOX_1D_PROCEEDS": {
      "kind": "amount",
      "decimal": true,
      "label": "Proceeds"
    },
    "BOX_1E_COST_BASIS": {
      "kind": "amount",
      "decimal": true,
      "label": "Cost or Other Basis"
    },
    "BOX_1F_MARKET_DISCOUNT": {
      "kind": "amount",
      "decimal": true,
      "label": "Market Discount"
    },
    "BOX_1G_WASH_SALE_LOSS": {
      "kind": "amount",
      "decimal": true,
      "label": "Wash Sale Loss Disallowed"
    },
    "BOX_2_SHORT_TERM_INDICATOR": {
      "kind": "checkbox",
      "label": "Short-term transaction indicator"
    },
    "BOX_2_LONG_TERM_INDICATOR": {
      "kind": "checkbox",
      "label": "Long-term transaction indicator"
    },
    "BOX_2_ORDINARY_INDICATOR": {
      "kind": "checkbox",
      "label": "Ordinary transaction indicator"
    },
    "BOX_3_PROCEEDS_COLLECTIBLES": {
      "kind": "amount",
      "decimal": true,
      "label": "Proceeds from Collectibles"
    },
    "BOX_3_PROCEEDS_QOF": {
      "kind": "amount",
      "decimal": true,
      "label": "Proceeds from QOF"
    },
    "BOX_4_FEDERAL_TAX_WITHHELD": {
      "kind": "amount",
      "decimal": true,
      "label": "Federal Income Tax Withheld"
    },
    "BOX_5_NONCOVERED_SECURITY_INDICATOR": {
      "kind": "checkbox",
      "label": "Noncovered Security"
    },
    "BOX_6_GROSS_PROCEEDS_INDICATOR": {
      "kind": "checkbox",
      "label": "Gross Proceeds"
    },
    "BOX_6_NET_PROCEEDS_INDICATOR": {
      "kind": "checkbox",
      "label": "Net Proceeds"
    },
    "BOX_7_LOSS_NOT_ALLOWED_INDICATOR": {
      "kind": "checkbox",
      "label": "Loss is not allowed based on amount in 1d"
    },
    "BOX_8_PROFIT_LOSS_2019": {
      "kind": "amount",
      "decimal": true,
      "label": "Profit or (loss) realized in 2019"
    },
    "BOX_9_UNREALIZED_PL_2018": {
      "kind": "amount",
      "decimal": true,
      "label": "Unrealized profit or (loss) on open contracts—12/31/2018"
    },
    "BOX_10_UNREALIZED_PL_2019": {
      "kind": "amount",
      "decimal": true,
      "label": "Unrealized profit or (loss) on open contracts—12/31/2019"
    },
    "BOX_11_AGGREGATE_PL": {
      "kind": "amount",
      "decimal": true,
      "label": "Aggregate profit or (loss) on contracts"
    },
    "BOX_12_PROCEEDS_COLLECTIBLES_INDICATOR": {
      "kind": "checkbox",
      "label": "If checked"
    },
    "BOX_13_BARTERING": {
      "kind": "amount",
      "decimal": true,
      "label": "Bartering"
    },
    "BOX_14_STATE_NAME1": {
      "kind": "state",
      "required": true,
      "label": "State Name 1"
    },
    "BOX_15_STATE_ID1": {
      "kind": "text",
      "label": "State ID Number 1"
    },
    "BOX_16_STATE_TAX_WITHHELD1": {
      "kind": "amount",
      "decimal": true,
      "label": "State Tax Withheld 1"
    },
    "BOX_14_STATE_NAME2": {
      "kind": "state",
      "required": true,
      "label": "State Name 2"
    },
    "BOX_15_STATE_ID2": {
      "kind": "text",
      "label": "State ID Number 2"
    },
    "BOX_16_STATE_TAX_WITHHELD2": {
      "kind": "amount",
      "decimal": true,
      "label": "State Tax Withheld 2"
    }
  },
  "1099-C": {
    "JOB_NUMBER": {
      "kind": "text",
      "label": "Job Number"
    },
    "SITE_ID": {
      "kind": "text",
      "required": true,
      "label": "Site ID"
    },
    "FILER_FEDERAL_ID_NUMBER": {
      "kind": "text",
      "required": true,
      "label": "Filer Federal ID Number"
    },
    "FILER_NAME": {
      "kind": "text",
      "required": true,
      "label": "Filer Name"
    },
    "FILER_ADDRESS1": {
      "kind": "text",
      "required": true,
      "label": "Filer Address 1"
    },
    "FILER_ADDRESS2": {
      "kind": "text",
      "label": "Filer Address 2"
    },
    "FILER_CITY": {
      "kind": "text",
      "required": true,
      "label": "Filer City"
    },
    "FILER_STATE": {
      "kind": "state",
      "required": true,
      "label": "Filer State"
    },
    "FILER_ZIP": {
      "kind": "zip",
      "required": true,
      "label": "Filer ZIP"
    },
    "FILER_PHONE": {
      "kind": "text",
      "required": true,
      "label": "Filer Phone"
    },
    "RECIPIENT_ID_NUMBER": {
      "kind": "text",
      "required": true,
      "label": "Recipient ID Number"
    },
    "RECIPIENT_NAME": {
      "kind": "text",
      "required": true,
      "label": "Recipient Name"
    },
    "RECIPIENT_NAME2": {
      "kind": "text",
      "label": "Recipient Name 2"
    },
    "RECIPIENT_ADDRESS1": {
      "kind": "text",
      "required": true,
      "label": "Recipient Address 1"
    },
    "RECIPIENT_ADDRESS2": {
      "kind": "text",
      "label": "Recipient Address 2"
    },
    "RECIPIENT_ADDRESS3": {
      "kind": "text",
      "label": "Recipient Address 3"
    },
    "RECIPIENT_CITY": {
      "kind": "text",
      "required": true,
      "label": "Recipient City"
    },
    "RECIPIENT_STATE": {
      "kind": "state",
      "required": true,
      "label": "Recipient State"
    },
    "RECIPIENT_ZIP": {
      "kind": "zip",
      "required": true,
      "label": "Recipient ZIP"
    },
    "RECIPIENT_EMAIL": {
      "kind": "text",
      "label": "Recipient Email"
    },
    "ACCOUNT_NUMBER": {
      "kind": "text",
      "label": "Account Number"
    },
    "BOX1_DATE_OF_IDENTIFIABLE_EVENT": {
      "kind": "date",
      "label": "Date of Identifiable Event"
    },
    "BOX2_AMT_OF_DEBT_DISCHARGED": {
      "kind": "amount",
      "label": "Amount of Debt Discharged"
    },
    "BOX3_INTEREST_INCL_IN_BOX2": {
      "kind": "amount",
      "label": "Interest Included in Box 2"
    },
    "BOX4_DEBT_DESCRIPTION": {
      "kind": "text",
      "label": "Debt Description"
    },
    "BOX5_BORROWER_LIABLE_YES_IND": {
      "kind": "checkbox",
      "label": "Borrower Liable Yes Indicator"
    },
    "BOX5_BORROWER_LIABLE_NO_IND": {
      "kind": "checkbox",
      "label": "Borrower Liable No Indicator"
    },
    "BOX6_IDENTIFIABLE_EVENT_CODE": {
      "kind": "text",
      "label": "Identifiable Event Code"
    },
    "BOX7_FAIR_MARKET_VALUE": {
      "kind": "amount",
      "label": "Fair Market Value"
    },
    "1099_YEAR": {
      "kind": "text",
      "required": true,
      "label": "1099 Year"
    },
    "SSN/TIN": {
      "kind": "tin",
      "required": true,
      "label": "SSN/TIN"
    },
    "FOREIGN_COUNTRY_INDICATOR": {
      "kind": "checkbox",
      "label": "Foreign Country Indicator"
    },
    "FOREIGN_COUNTRY": {
      "kind": "text",
      "label": "Foreign Country"
    },
    "createdBy": {
      "kind": "text"
    },
    "userId": {
      "kind": "text"
    },
    "corrected": {
      "kind": "text"
    },
    "createdAt": {
      "kind": "text"
    }
  },
  "1099-DIV": {
    "JOB_NUMBER": {
      "kind": "text",
      "label": "Job Number"
    },
    "SITE_ID": {
      "kind": "text",
      "required": true,
      "label": "Site ID"
    },
    "FILER_FEDERAL_ID_NUMBER": {
      "kind": "text",
      "required": true,
      "label": "Filer Federal ID Number"
    },
    "FILER_NAME": {
      "kind": "text",
      "required": true,
      "label": "Filer Name"
    },
    "FILER_ADDRESS1": {
      "kind": "text",
      "required": true,
      "label": "Filer Address 1"
    },
    "FILER_ADDRESS2": {
      "kind": "text",
      "label": "Filer Address 2"
    },
    "FILER_CITY": {
      "kind": "text",
      "required": true,
      "label": "Filer City"
    },
    "FILER_STATE": {
      "kind": "state",
      "required": true,
      "label": "Filer State"
    },
    "FILER_ZIP": {
      "kind": "zip",
      "required": true,
      "label": "Filer ZIP"
    },
    "FILER_PHONE": {
      "kind": "text",
      "required": true,
      "label": "Filer Phone"
    },
    "RECIPIENT_ID_NUMBER": {
      "kind": "text",
      "required": true,
      "label": "Recipient ID Number"
    },
    "RECIPIENT_NAME": {
      "kind": "text",
      "required": true,
      "label": "Recipient Name"
    },
    "RECIPIENT_NAME2": {
      "kind": "text",
      "label": "Recipient Name 2"
    },
    "RECIPIENT_ADDRESS1": {
      "kind": "text",
      "required": true,
      "label": "Recipient Address 1"
    },
    "RECIPIENT_ADDRESS2": {
      "kind": "text",
      "label": "Recipient Address 2"
    },
    "RECIPIENT_CITY": {
      "kind": "text",
      "required": true,
      "label": "Recipient City"
    },
    "RECIPIENT_STATE": {
      "kind": "state",
      "required": true,
      "label": "Recipient State"
    },
    "RECIPIENT_ZIP": {
      "kind": "zip",
      "required": true,
      "label": "Recipient ZIP"
    },
    "RECIPIENT_EMAIL": {
      "kind": "text",
      "label": "Recipient Email"
    },
    "FOREIGN_COUNTRY_INDICATOR": {
      "kind": "checkbox",
      "label": "Foreign Country Indicator"
    },
    "FOREIGN_COUNTRY": {
      "kind": "text",
      "label": "Foreign Country"
    },
    "ACCOUNT_NUMBER": {
      "kind": "text",
      "label": "Account Number"
    },
    "BOX1A_TOTAL_ORDINARY_DIVIDENDS": {
      "kind": "amount",
      "label": "Total Ordinary Dividends"
    },
    "BOX1B_QUALIFIED_DIVIDENDS": {
      "kind": "amount",
      "label": "Qualified Dividends"
    },
    "BOX2A_TOTAL_CAPITAL_GAIN_DISTR": {
      "kind": "amount",
      "label": "Total Capital Gain Distributions"
    },
    "BOX2B_UNRECAP_SEC_1250_GAIN": {
      "kind": "amount",
      "label": "Unrecaptured Section 1250 Gain"
    },
    "BOX2C_SECTION_1202_GAIN": {
      "kind": "amount",
      "label": "Section 1202 Gain"
    },
    "BOX2D_COLLECTIBLES_GAIN": {
      "kind": "amount",
      "label": "Collectibles Gain"
    },
    "BOX2E_SECTION_897_ORD_DIV": {
      "kind": "amount",
      "label": "Section 897 Ordinary Dividends"
    },
    "BOX2F_SECTION_897_CAP_GAIN": {
      "kind": "amount",
      "label": "Section 897 Capital Gain"
    },
    "BOX3_NONDIVIDEND_DISTRIBUTIONS": {
      "kind": "amount",
      "label": "Nondividend Distributions"
    },
    "BOX4_FEDERAL_INCOME_TAX_WITHHELD": {
      "kind": "amount",
      "label": "Federal Income Tax Withheld"
    },
    "BOX5_SECTION_199A_DIVIDENDS": {
      "kind": "amount",
      "label": "Section 199A Dividends"
    },
    "BOX6_INVESTMENT_EXPENSES": {
      "kind": "amount",
      "label": "Investment Expenses"
    },
    "BOX7_FOREIGN_TAX_PAID": {
      "kind": "amount",
      "label": "Foreign Tax Paid"
    },
    "BOX8_FOREIGN_COUNTRY": {
      "kind": "text",
      "label": "Foreign Country"
    },
    "BOX9_CASH_LIQUIDATION_DISTRIBUTIONS": {
      "kind": "amount",
      "label": "Cash Liquidation Distributions"
    },
    "BOX10_NONCASH_LIQUIDATION_DISTRIBUTIONS": {
      "kind": "amount",
      "label": "Noncash Liquidation Distributions"
    },
    "BOX12_EXEMPT_INTEREST_DIVIDENDS": {
      "kind": "amount",
      "label": "Exempt Interest Dividends"
    },
    "BOX13_SPECIFIED_PRIVATE_ACTIVITY_BOND": {
      "kind": "amount",
      "label": "Specified Private Activity Bond"
    },
    "BOX14_STATE1": {
      "kind": "text",
      "label": "State 1"
    },
    "BOX14_STATE2": {
      "kind": "text",
      "label": "State 2"
    },
    "BOX15_STATE_IDENTIFICATION_NO1": {
      "kind": "text",
      "label": "State Identification No 1"
    },
    "BOX15_STATE_IDENTIFICATION_NO2": {
      "kind": "text",
      "label": "State Identification No 2"
    },
    "BOX16_STATE_TAX_WITHHELD1": {
      "kind": "amount",
      "label": "State Tax Withheld 1"
    },
    "BOX16_STATE_TAX_WITHHELD2": {
      "kind": "amount",
      "label": "State Tax Withheld 2"
    },
    "BOX11_FATCA_REQUIREMENT": {
      "kind": "checkbox",
      "label": "FATCA Requirement"
    },
    "BOX_2ND_TIN_NOT_IND": {
      "kind": "checkbox",
      "label": "2nd TIN Not Indicator"
    },
    "1099_YEAR": {
      "kind": "text",
      "required": true,
      "label": "1099 Year"
    },
    "createdBy": {
      "kind": "text"
    },
    "userId": {
      "kind": "text"
    },
    "corrected": {
      "kind": "text"
    },
    "createdAt": {
      "kind": "text"
    }
  },
  "1099-INT": {
    "RECIPIENT_ID_NUMBER": {
      "kind": "tin",
      "required": true,
      "label": "Recipient ID Number"
    },
    "RECIPIENT_NAME": {
      "kind": "text",
      "required": true,
      "label": "Recipient Name"
    },
    "RECIPIENT_NAME2": {
      "kind": "text",
      "label": "Recipient Name 2"
    },
    "RECIPIENT_ADDRESS1": {
      "kind": "text",
      "required": true,
      "label": "Recipient Address 1"
    },
    "RECIPIENT_ADDRESS2": {
      "kind": "text",
      "label": "Recipient Address 2"
    },
    "RECIPIENT_ADDRESS3": {
      "kind": "text",
      "label": "Recipient Address 3"
    },
    "RECIPIENT_CITY": {
      "kind": "text",
      "required": true,
      "label": "Recipient City"
    },
    "RECIPIENT_STATE": {
      "kind": "state",
      "required": true,
      "label": "Recipient State"
    },
    "RECIPIENT_ZIP": {
      "kind": "text",
      "required": true,
      "label": "Recipient ZIP"
    },
    "FOREIGN_COUNTRY_INDICATOR": {
      "kind": "checkbox",
      "label": "Foreign Country Indicator"
    },
    "FOREIGN_COUNTRY": {
      "kind": "text",
      "label": "Foreign Country"
    },
    "ACCOUNT_NUMBER": {
      "kind": "text",
      "label": "Account Number"
    },
    "BOX1_INTEREST_INCOME_NOT_IN_BOX3": {
      "kind": "amount",
      "label": "Interest Income Not in Box 3"
    },
    "BOX2_EARLY_WITHDRAWAL_PENALTY": {
      "kind": "amount",
      "label": "Early Withdrawal Penalty"
    },
    "BOX3_INTEREST_ON_US_SAVINGS": {
      "kind": "amount",
      "label": "Interest on US Savings"
    },
    "BOX4_FED_INCOME_TAX_WITHHELD": {
      "kind": "amount",
      "label": "Federal Income Tax Withheld"
    },
    "BOX5_INVESTMENT_EXPENSES": {
      "kind": "amount",
      "label": "Investment Expenses"
    },
    "BOX6_FOREIGN_TAX_PAID": {
      "kind": "amount",
      "label": "Foreign Tax Paid"
    },
    "BOX7_FOREIGN_COUNTRY_US_POSSESSION": {
      "kind": "text",
      "label": "Foreign Country US Possession"
    },
    "BOX8_TAX_EXEMPT_INTEREST": {
      "kind": "amount",
      "label": "Tax-Exempt Interest"
    },
    "BOX9_SPEC_PRIVATE_ACT_BOND_INT": {
      "kind": "amount",
      "label": "Specified Private Activity Bond Interest"
    },
    "BOX10_MARKET_DISCOUNT_BOND": {
      "kind": "amount",
      "label": "Market Discount Bond"
    },
    "BOX11_BOND_PREMIUM": {
      "kind": "amount",
      "label": "Bond Premium"
    },
    "BOX12_BOND_PREMIUM_ON_TREASURY": {
      "kind": "amount",
      "label": "Bond Premium on Treasury"
    },
    "BOX13_TAX_EXEMPT_BOND": {
      "kind": "amount",
      "label": "Tax-Exempt Bond"
    },
    "BOX14_TAX_EXEMPT_BOND_CUSIP": {
      "kind": "text",
      "label": "Tax-Exempt Bond CUSIP"
    },
    "BOX15_STATE": {
      "kind": "text",
      "label": "State"
    },
    "BOX15_STATE2": {
      "kind": "text",
      "label": "State 2"
    },
    "BOX16_STATE_ID": {
      "kind": "text",
      "label": "State ID"
    },
    "BOX16_STATE_ID2": {
      "kind": "text",
      "label": "State ID 2"
    },
    "BOX17_STATE_TAX_WITHHELD": {
      "kind": "amount",
      "decimal": true,
      "label": "State Tax Withheld"
    },
    "BOX17_STATE_TAX_WITHHELD2": {
      "kind": "amount",
      "label": "State Tax Withheld 2"
    },
    "FATCA_REQUIREMENT": {
      "kind": "checkbox",
      "label": "FATCA Requirement"
    },
    "1099_YEAR": {
      "kind": "text",
      "label": "1099 Year"
    },
    "SITE_ID": {
      "kind": "text",
      "label": "Site ID"
    },
    "FILER_NAME": {
      "kind": "text",
      "required": true,
      "label": "Filer Name"
    },
    "FILER_ADDRESS1": {
      "kind": "text",
      "required": true,
      "label": "Filer Address 1"
    },
    "FILER_ADDRESS2": {
      "kind": "text",
      "label": "Filer Address 2"
    },
    "JOB_NUMBER": {
      "kind": "text",
      "label": "Job Number"
    },
    "FILER_CITY": {
      "kind": "text",
      "required": true,
      "label": "Filer City"
    },
    "FILER_STATE": {
      "kind": "state",
      "required": true,
      "label": "Filer State"
    },
    "FILER_ZIP": {
      "kind": "zip",
      "required": true,
      "label": "Filer ZIP"
    },
    "FILER_PHONE": {
      "kind": "text",
      "required": true,
      "label": "Filer Phone"
    },
    "FILER_FEDERAL_ID_NUMBER": {
      "kind": "text",
      "label": "Filer Federal ID Number"
    },
    "BOX_2ND_TIN_NOT_IND": {
      "kind": "checkbox",
      "label": "2nd TIN Not IND"
    },
    "createdBy": {
      "kind": "text"
    },
    "userId": {
      "kind": "text"
    },
    "corrected": {
      "kind": "text"
    },
    "createdAt": {
      "kind": "text"
    }
  },
  "1099-K": {
    "SITE_ID": {
      "kind": "text",
      "label": "Site ID"
    },
    "1099_YEAR": {
      "kind": "text",
      "label": "1099 Year"
    },
    "JOB_NUMBER": {
      "kind": "text",
      "label": "Job Number"
    },
    "FILER_FEDERAL_ID_NUMBER": {
      "kind": "text",
      "required": true,
      "label": "Filer Federal ID Number"
    },
    "FILER_NAME": {
      "kind": "text",
      "required": true,
      "label": "Filer Name"
    },
    "FILER_ADDRESS1": {
      "kind": "text",
      "required": true,
      "label": "Filer Address 1"
    },
    "FILER_ADDRESS2": {
      "kind": "text",
      "label": "Filer Address 2"
    },
    "FILER_CITY": {
      "kind": "text",
      "required": true,
      "label": "Filer City"
    },
    "FILER_STATE": {
      "kind": "state",
      "required": true,
      "label": "Filer State"
    },
    "FILER_ZIP": {
      "kind": "zip",
      "required": true,
      "label": "Filer ZIP Code"
    },
    "FILER_PHONE": {
      "kind": "text",
      "required": true,
      "label": "Filer Phone"
    },
    "PAYEE_TIN": {
      "kind": "text",
      "required": true,
      "label": "Payee TIN"
    },
    "PAYEE_NAME": {
      "kind": "text",
      "required": true,
      "label": "Payee Name"
    },
    "PAYEE_NAME2": {
      "kind": "text",
      "label": "Payee Name 2"
    },
    "PAYEE_ADDRESS1": {
      "kind": "text",
      "required": true,
      "label": "Payee Address 1"
    },
    "PAYEE_ADDRESS2": {
      "kind": "text",
      "label": "Payee Address 2"
    },
    "PAYEE_CITY": {
      "kind": "text",
      "required": true,
      "label": "Payee City"
    },
    "PAYEE_STATE": {
      "kind": "state",
      "required": true,
      "label": "Payee State"
    },
    "PAYEE_ZIP": {
      "kind": "zip",
      "required": true,
      "label": "Payee ZIP Code"
    },
    "TYPE_OF_PAYMENT_INDICATOR_PAYMENT_CARD": {
      "kind": "checkbox",
      "label": "Type of Payment Indicator (Payment Card)"
    },
    "TYPE_OF_PAYMENT_INDICATOR_THIRD_PARTY": {
      "kind": "checkbox",
      "label": "Type of Payment Indicator (Third Party)"
    },
    "TYPE_OF_FILER_INDICATOR_PSE": {
      "kind": "checkbox",
      "label": "Type of Filer Indicator (PSE)"
    },
    "TYPE_OF_FILER_INDICATOR_3RD_PARTY": {
      "kind": "checkbox",
      "label": "Type of Filer Indicator (3rd Party)"
    },
    "BOX1A_GROSS_AMT_PAYMENT_CARD_TPN_TRANS": {
      "kind": "amount",
      "label": "Box 1a Gross Amount Payment Card/TPN Transactions"
    },
    "BOX1B_CARD_NOT_PRESENT_TRANS": {
      "kind": "amount",
      "label": "Box 1b Card Not Present Transactions"
    },
    "BOX2_MERCHANT_CAT_CODE": {
      "kind": "text",
      "label": "Box 2 Merchant Category Code"
    },
    "BOX3_NO_PAYMENT_TRANSACTIONS": {
      "kind": "amount",
      "label": "Box 3 Number of Payment Transactions"
    },
    "BOX4_FED_INCOME_TAX_WTHLD": {
      "kind": "amount",
      "label": "Box 4 Federal Income Tax Withheld"
    },
    "BOX5A_JANUARY": {
      "kind": "amount",
      "label": "Box 5a January"
    },
    "BOX5B_FEBRUARY": {
      "kind": "amount",
      "label": "Box 5b February"
    },
    "BOX5C_MARCH": {
      "kind": "amount",
      "label": "Box 5c March"
    },
    "BOX5D_APRIL": {
      "kind": "amount",
      "label": "Box 5d April"
    },
    "BOX5E_MAY": {
      "kind": "amount",
      "label": "Box 5e May"
    },
    "BOX5F_JUNE": {
      "kind": "amount",
      "label": "Box 5f June"
    },
    "BOX5G_JULY": {
      "kind": "amount",
      "label": "Box 5g July"
    },
    "BOX5H_AUGUST": {
      "kind": "amount",
      "label": "Box 5h August"
    },
    "BOX5I_SEPTEMBER": {
      "kind": "amount",
      "label": "Box 5i September"
    },
    "BOX5J_OCTOBER": {
      "kind": "amount",
      "label": "Box 5j October"
    },
    "BOX5K_NOVEMBER": {
      "kind": "amount",
      "label": "Box 5k November"
    },
    "BOX5L_DECEMBER": {
      "kind": "amount",
      "label": "Box 5l December"
    },
    "BOX6A_STATE": {
      "kind": "text",
      "label": "Box 6A State"
    },
    "BOX6B_STATE": {
      "kind": "text",
      "label": "Box 6B State"
    },
    "BOX7A_STATE_ID_NO": {
      "kind": "text",
      "label": "Box 7A State ID Number"
    },
    "BOX7B_STATE_ID_NO": {
      "kind": "text",
      "label": "Box 7B State ID Number"
    },
    "BOX8A_STATE_TAX": {
      "kind": "amount",
      "label": "Box 8A State Income Tax Withheld"
    },
    "BOX8B_STATE_TAX": {
      "kind": "amount",
      "label": "Box 8B State Income Tax Withheld"
    },
    "PSE_NAME": {
      "kind": "text",
      "required": true,
      "label": "PSE Name"
    },
    "PSE_TELEPHONE": {
      "kind": "text",
      "required": true,
      "label": "PSE Telephone"
    },
    "PAYEE_INTERNAL_ACCT_NO": {
      "kind": "text",
      "label": "Payee Internal Account Number"
    },
    "createdBy": {
      "kind": "text"
    },
    "userId": {
      "kind": "text"
    },
    "corrected": {
      "kind": "text"
    },
    "createdAt": {
      "kind": "text"
    }
  },
  "1099-LTC": {
    "SITE_ID": {
      "kind": "text",
      "label": "Site ID"
    },
    "POLICYHOLDER_TAX_ID": {
      "kind": "tin",
      "required": true,
      "label": "Policyholder Tax ID"
    },
    "POLICYHOLDER_NAME": {
      "kind": "text",
      "required": true,
      "label": "Policyholder Name"
    },
    "POLICYHOLDER_STREET_ADDRESS_1": {
      "kind": "text",
      "required": true,
      "label": "Policyholder Street Address 1"
    },
    "POLICYHOLDER_STREET_ADDRESS_2": {
      "kind": "text",
      "label": "Policyholder Street Address 2"
    },
    "POLICYHOLDER_CITY": {
      "kind": "text",
      "required": true,
      "label": "Policyholder City"
    },
    "POLICYHOLDER_STATE": {
      "kind": "state",
      "required": true,
      "label": "Policyholder State"
    },
    "POLICYHOLDER_ZIP_CODE": {
      "kind": "zip",
      "required": true,
      "label": "Policyholder Zip Code"
    },
    "INSURED_SSN": {
      "kind": "tin",
      "required": true,
      "label": "Insured SSN"
    },
    "INSURED_NAME": {
      "kind": "text",
      "required": true,
      "label": "Insured Name"
    },
    "INSURED_STREET_ADDRESS_1": {
      "kind": "text",
      "required": true,
      "label": "Insured Street Address 1"
    },
    "INSURED_STREET_ADDRESS_2": {
      "kind": "text",
      "label": "Insured Street Address 2"
    },
    "INSURED_CITY": {
      "kind": "text",
      "required": true,
      "label": "Insured City"
    },
    "INSURED_STATE": {
      "kind": "state",
      "required": true,
      "label": "Insured State"
    },
    "INSURED_ZIP_CODE": {
      "kind": "zip",
      "required": true,
      "label": "Insured Zip Code"
    },
    "INSURED_ACCOUNT_NUMBER": {
      "kind": "text",
      "required": true,
      "label": "Insured Account Number"
    },
    "BOX1_GROSS_BENEFITS_PAID": {
      "kind": "amount",
      "label": "Box 1 Gross Benefits Paid"
    },
    "BOX2_ACCELERATED_DEATH_BENEFITS_PAID": {
      "kind": "amount",
      "label": "Box 2 Accelerated Death Benefits Paid"
    },
    "PAYMENT_TYPE": {
      "kind": "amount",
      "label": "Payment Type"
    },
    "QUALIFIED_CONTRACT": {
      "kind": "checkbox",
      "label": "Qualified Contract"
    },
    "CHRONICALLY_ILL": {
      "kind": "checkbox",
      "label": "Chronically Ill or Terminal"
    },
    "PAYER_NAME": {
      "kind": "text",
      "required": true,
      "label": "Payer Name"
    },
    "PAYER_STREET_ADDRESS_1": {
      "kind": "text",
      "required": true,
      "label": "Payer Street Address 1"
    },
    "PAYER_STREET_ADDRESS_2": {
      "kind": "text",
      "label": "Payer Street Address 2"
    },
    "FOREIGN_COUNTRY": {
      "kind": "text",
      "label": "Foreign Country"
    },
    "FOREIGN_COUNTRY_INDICATOR": {
      "kind": "checkbox",
      "label": "Foreign Country Indicator"
    },
    "PAYER_CITY": {
      "kind": "text",
      "required": true,
      "label": "Payer City"
    },
    "PAYER_STATE": {
      "kind": "state",
      "required": true,
      "label": "Payer State"
    },
    "PAYER_ZIP_CODE": {
      "kind": "zip",
      "required": true,
      "label": "Payer Zip Code"
    },
    "JOB_NUMBER": {
      "kind": "text",
      "label": "Job Number"
    },
    "PAYER_PHONE": {
      "kind": "text",
      "label": "Payer Phone"
    },
    "PAYERS_TIN": {
      "kind": "tin",
      "required": true,
      "label": "Payer's TIN"
    },
    "1099_YEAR": {
      "kind": "text",
      "label": "1099 Year"
    },
    "DATE_CERTIFIED": {
      "kind": "text",
      "label": "Date Certified"
    },
    "REIMBURSED_AMOUNT": {
      "kind": "amount",
      "decimal": true,
      "label": "Reimbursed Amount"
    },
    "TERMINAL_ILL": {
      "kind": "checkbox",
      "label": "Terminally Ill"
    },
    "PER_DIEM": {
      "kind": "amount",
      "decimal": true,
      "label": "Per Diem"
    },
    "createdBy": {
      "kind": "text"
    },
    "userId": {
      "kind": "text"
    },
    "corrected": {
      "kind": "text"
    },
    "createdAt": {
      "kind": "text"
    }
  },
  "1099-NEC": {
    "1099_YEAR": {
      "kind": "amount",
      "required": true,
      "label": "1099 Year"
    },
    "JOB_NUMBER": {
      "kind": "text",
      "label": "Job Number"
    },
    "ACCOUNT_NUMBER": {
      "kind": "text",
      "label": "Account Number"
    },
    "BOX1_NONEMPLOYEE_COMPENSATION": {
      "kind": "amount",
      "required": true,
      "label": "Nonemployee Compensation"
    },
    "BOX2_DIRECT_SALE": {
      "kind": "checkbox",
      "label": "Direct Sale"
    },
    "BOX3_GOLDEN_PARACHUTE": {
      "kind": "amount",
      "required": true,
      "label": "Golden Parachute"
    },
    "BOX4_FED_INCOME_TAX_WITHHELD": {
      "kind": "amount",
      "required": true,
      "label": "Federal Income Tax Withheld"
    },
    "BOX5_STATE_TAX_WITHHELD1": {
      "kind": "amount",
      "required": true,
      "label": "State Tax Withheld 1"
    },
    "BOX5_STATE_TAX_WITHHELD2": {
      "kind": "amount",
      "label": "State Tax Withheld 2"
    },
    "BOX6_PAYER_STATE_NUMBER1": {
      "kind": "state",
      "required": true,
      "label": "Payer State Number 1"
    },
    "BOX6_PAYER_STATE_NUMBER2": {
      "kind": "state",
      "required": true,
      "label": "Payer State Number 2"
    },
    "BOX7_STATE_INCOME1": {
      "kind": "amount",
      "label": "State Income 1"
    },
    "BOX7_STATE_INCOME2": {
      "kind": "amount",
      "label": "State Income 2"
    },
    "BOX_2ND_TIN_NOT_IND": {
      "kind": "checkbox",
      "label": "2nd TIN not Indicator"
    },
    "FATCA_REQUIREMENT": {
      "kind": "checkbox",
      "label": "FATCA Requirement"
    },
    "FILER_ADDRESS1": {
      "kind": "text",
      "required": true,
      "label": "Filer Address 1"
    },
    "FILER_ADDRESS2": {
      "kind": "text",
      "label": "Filer Address 2"
    },
    "FILER_CITY": {
      "kind": "text",
      "required": true,
      "label": "Filer City"
    },
    "FILER_FEDERAL_ID_NUMBER": {
      "kind": "tin",
      "required": true,
      "label": "Filer Federal ID Number"
    },
    "FILER_NAME": {
      "kind": "text",
      "required": true,
      "label": "Filer Name"
    },
    "FILER_PHONE": {
      "kind": "text",
      "required": true,
      "label": "Filer Phone"
    },
    "FILER_STATE": {
      "kind": "text",
      "required": true,
      "label": "Filer State"
    },
    "FILER_ZIP": {
      "kind": "text",
      "required": true,
      "label": "Filer ZIP"
    },
    "FOREIGN_COUNTRY": {
      "kind": "text",
      "label": "Foreign Country"
    },
    "FOREIGN_COUNTRY_INDICATOR": {
      "kind": "checkbox",
      "label": "Foreign Country Indicator"
    },
    "RECIPIENT_ADDRESS1": {
      "kind": "text",
      "required": true,
      "label": "Recipient Address 1"
    },
    "RECIPIENT_ADDRESS2": {
      "kind": "text",
      "label": "Recipient Address 2"
    },
    "RECIPIENT_ADDRESS3": {
      "kind": "text",
      "label": "Recipient Address 3"
    },
    "RECIPIENT_CITY": {
      "kind": "text",
      "required": true,
      "label": "Recipient City"
    },
    "RECIPIENT_EMAIL": {
      "kind": "text",
      "label": "Recipient Email"
    },
    "RECIPIENT_ID_NUMBER": {
      "kind": "text",
      "required": true,
      "label": "Recipient ID Number"
    },
    "RECIPIENT_NAME": {
      "kind": "text",
      "required": true,
      "label": "Recipient Name"
    },
    "RECIPIENT_NAME2": {
      "kind": "text",
      "label": "Recipient Name 2"
    },
    "RECIPIENT_STATE": {
      "kind": "text",
      "label": "Recipient State"
    },
    "RECIPIENT_ZIP": {
      "kind": "zip",
      "required": true,
      "label": "Recipient ZIP"
    },
    "SITE_ID": {
      "kind": "amount",
      "required": true,
      "label": "Site ID"
    },
    "SSN/TIN": {
      "kind": "text",
      "required": true,
      "label": "SSN/TIN"
    },
    "createdBy": {
      "kind": "text"
    },
    "userId": {
      "kind": "text"
    },
    "corrected": {
      "kind": "text"
    },
    "createdAt": {
      "kind": "text"
    }
  },
  "1099-R": {
    "JOB_NUMBER": {
      "kind": "text",
      "label": "Job Number"
    },
    "SITE_ID": {
      "kind": "text",
      "required": true,
      "label": "Site ID"
    },
    "FILER_FEDERAL_ID_NUMBER": {
      "kind": "tin",
      "required": true,
      "label": "Filer Federal ID Number"
    },
    "FILER_NAME": {
      "kind": "text",
      "required": true,
      "label": "Filer Name"
    },
    "FILER_ADDRESS1": {
      "kind": "text",
      "required": true,
      "label": "Filer Address 1"
    },
    "FILER_ADDRESS2": {
      "kind": "text",
      "label": "Filer Address 2"
    },
    "FILER_CITY": {
      "kind": "text",
      "required": true,
      "label": "Filer City"
    },
    "FILER_STATE": {
      "kind": "text",
      "required": true,
      "label": "Filer State"
    },
    "FILER_ZIP": {
      "kind": "text",
      "required": true,
      "label": "Filer ZIP"
    },
    "FILER_PHONE": {
      "kind": "text",
      "required": true,
      "label": "Filer Phone"
    },
    "RECIPIENT_ID_NUMBER": {
      "kind": "text",
      "required": true,
      "label": "Recipient ID Number"
    },
    "RECIPIENT_NAME": {
      "kind": "text",
      "required": true,
      "label": "Recipient Name"
    },
    "RECIPIENT_NAME2": {
      "kind": "text",
      "required": true,
      "label": "Recipient Name 2"
    },
    "RECIPIENT_ADDRESS1": {
      "kind": "text",
      "required": true,
      "label": "Recipient Address 1"
    },
    "RECIPIENT_ADDRESS2": {
      "kind": "text",
      "label": "Recipient Address 2"
    },
    "RECIPIENT_CITY": {
      "kind": "text",
      "required": true,
      "label": "Recipient City"
    },
    "RECIPIENT_STATE": {
      "kind": "text",
      "required": true,
      "label": "Recipient State"
    },
    "RECIPIENT_ZIP": {
      "kind": "text",
      "required": true,
      "label": "Recipient ZIP"
    },
    "RECIPIENT_EMAIL": {
      "kind": "text",
      "label": "Recipient Email"
    },
    "ACCOUNT_NUMBER": {
      "kind": "text",
      "label": "Account Number"
    },
    "BOX1_GROSS_DISTRIBUTION": {
      "kind": "amount",
      "label": "Gross Distribution"
    },
    "BOX2A_TAXABLE_AMOUNT": {
      "kind": "amount",
      "label": "Taxable Amount"
    },
    "BOX2B_TAXABLE_AMOUNT_IND": {
      "kind": "checkbox",
      "label": "Taxable Amount Indicator"
    },
    "BOX2B_TOTAL_DISTRIBUTION_IND": {
      "kind": "checkbox",
      "label": "Total Distribution Indicator"
    },
    "BOX3_CAPITAL_GAIN": {
      "kind": "amount",
      "label": "Capital Gain"
    },
    "BOX4_FEDERAL_INCOME_TAX_WITHHELD": {
      "kind": "amount",
      "label": "Federal Income Tax Withheld"
    },
    "BOX5_EMPLOYEE_CONTRIBUTIONS": {
      "kind": "amount",
      "label": "Employee Contributions"
    },
    "BOX6_UNREALIZED_APPRECIATION": {
      "kind": "amount",
      "label": "Unrealized Appreciation"
    },
    "BOX7_DISTRIBUTION_CODES": {
      "kind": "text",
      "required": true,
      "label": "Distribution Codes"
    },
    "BOX7_IRA_SEP_SIMPLE_IND": {
      "kind": "checkbox",
      "label": "IRA SEP SIMPLE Indicator"
    },
    "BOX8_OTHER": {
      "kind": "amount",
      "label": "Other"
    },
    "BOX8_PERCENT": {
      "kind": "amount",
      "label": "Percent"
    },
    "BOX9A_PERCENT_OF_DISTRIBUTION": {
      "kind": "amount",
      "label": "Percent of Distribution"
    },
    "BOX9B_TOT_EMPLOYEE_CONTRIBUTIONS": {
      "kind": "amount",
      "label": "Total Employee Contributions"
    },
    "BOX10_AMOUNT_ALLOCABLE": {
      "kind": "amount",
      "label": "Amount Allocable"
    },
    "BOX11_ROTH_CONTRIBUTION": {
      "kind": "text",
      "label": "Roth Contribution"
    },
    "BOX12_FATCA_REQUIREMENT": {
      "kind": "checkbox",
      "label": "FATCA Requirement"
    },
    "BOX13_DATE_OF_PAYMENT": {
      "kind": "text",
      "label": "Date of Payment"
    },
    "BOX14_STATE_TAX_WITHHELD1": {
      "kind": "amount",
      "label": "State Tax Withheld 1"
    },
    "BOX14_STATE_TAX_WITHHELD2": {
      "kind": "amount",
      "label": "State Tax Withheld 2"
    },
    "BOX15_PAYERS_STATE_NO1": {
      "kind": "text",
      "label": "Payer's State Number 1"
    },
    "BOX15_PAYERS_STATE_NO2": {
      "kind": "text",
      "label": "Payer's State Number 2"
    },
    "BOX16_STATE_DISTRIBUTION1": {
      "kind": "amount",
      "label": "State Distribution 1"
    },
    "BOX16_STATE_DISTRIBUTION2": {
      "kind": "amount",
      "label": "State Distribution 2"
    },
    "BOX17_LOCAL_TAX_WITHHELD1": {
      "kind": "amount",
      "label": "Local Tax Withheld 1"
    },
    "BOX17_LOCAL_TAX_WITHHELD2": {
      "kind": "amount",
      "label": "Local Tax Withheld 2"
    },
    "BOX18_NAME_OF_LOCALITY1": {
      "kind": "text",
      "label": "Name of Locality 1"
    },
    "BOX18_NAME_OF_LOCALITY2": {
      "kind": "text",
      "label": "Name of Locality 2"
    },
    "BOX19_LOCAL_DISTRIBUTION1": {
      "kind": "amount",
      "label": "Local Distribution 1"
    },
    "BOX19_LOCAL_DISTRIBUTION2": {
      "kind": "amount",
      "label": "Local Distribution 2"
    },
    "1099_YEAR": {
      "kind": "text",
      "required": true,
      "label": "Year"
    },
    "SSN_TIN": {
      "kind": "text",
      "label": "SSN/TIN"
    },
    "FOREIGN_COUNTRY": {
      "kind": "text",
      "label": "Foreign Country"
    },
    "FOREIGN_COUNTRY_INDICATOR": {
      "kind": "checkbox",
      "label": "Foreign Country indicator"
    },
    "createdBy": {
      "kind": "text"
    },
    "userId": {
      "kind": "text"
    },
    "corrected": {
      "kind": "text"
    },
    "createdAt": {
      "kind": "text"
    }
  },
  "1099-SA": {
    "SITE_ID": {
      "kind": "text",
      "required": true,
      "label": "Site ID"
    },
    "FILER_FEDERAL_ID_NUMBER": {
      "kind": "text",
      "required": true,
      "label": "Filer Federal ID Number"
    },
    "FILER_NAME": {
      "kind": "text",
      "required": true,
      "label": "Filer Name"
    },
    "FILER_ADDRESS1": {
      "kind": "text",
      "required": true,
      "label": "Filer Address 1"
    },
    "FILER_ADDRESS2": {
      "kind": "text",
      "label": "Filer Address 2"
    },
    "FILER_CITY": {
      "kind": "text",
      "required": true,
      "label": "Filer City"
    },
    "FILER_STATE": {
      "kind": "text",
      "required": true,
      "label": "Filer State"
    },
    "FILER_ZIP": {
      "kind": "zip",
      "required": true,
      "label": "Filer ZIP"
    },
    "JOB_NUMBER": {
      "kind": "text",
      "label": "Job Number"
    },
    "FILER_PHONE": {
      "kind": "text",
      "label": "Filer Phone"
    },
    "BOX_RECIPIENT_ID_NUMBER": {
      "kind": "tin",
      "required": true,
      "label": "Recipient ID Number"
    },
    "RECIPIENT_NAME": {
      "kind": "text",
      "required": true,
      "label": "Recipient Name"
    },
    "RECIPIENT_NAME2": {
      "kind": "text",
      "required": true,
      "label": "Recipient Name 2"
    },
    "RECIPIENT_ADDRESS1": {
      "kind": "text",
      "required": true,
      "label": "Recipient Address"
    },
    "RECIPIENT_ADDRESS2": {
      "kind": "text",
      "label": "Recipient Address 1"
    },
    "RECIPIENT_ADDRESS3": {
      "kind": "text",
      "label": "Recipient Address 3"
    },
    "RECIPIENT_CITY": {
      "kind": "text",
      "required": true,
      "label": "Recipient City"
    },
    "RECIPIENT_STATE": {
      "kind": "text",
      "required": true,
      "label": "Recipient State"
    },
    "RECIPIENT_ZIP": {
      "kind": "zip",
      "required": true,
      "label": "Recipient ZIP"
    },
    "EMAIL": {
      "kind": "text",
      "label": "Email"
    },
    "BOX_ACCOUNT_NUMBER": {
      "kind": "text",
      "label": "Account Number"
    },
    "BOX1_GROSS_DISTRIBUTION": {
      "kind": "text",
      "label": "Gross Distribution"
    },
    "BOX2_EARNING_ON_EXCESS": {
      "kind": "amount",
      "label": "Earnings on Excess Contributions"
    },
    "BOX3_DISTRIBUTION_CODE": {
      "kind": "text",
      "label": "Distribution Code"
    },
    "BOX4_FMV_ON_DATE_OF_DEATH": {
      "kind": "amount",
      "label": "FMV on Date of Death"
    },
    "BOX5_HSA": {
      "kind": "checkbox",
      "label": "HSA"
    },
    "BOX5_ARCHER_MSA": {
      "kind": "checkbox",
      "label": "Archer MSA"
    },
    "FOREIGN_COUNTRY": {
      "kind": "text",
      "label": "Foreign Country"
    },
    "FOREIGN_COUNTRY_INDICATOR": {
      "kind": "checkbox",
      "label": "Foreign Country Indicator"
    },
    "BOX5_MA_MSA": {
      "kind": "checkbox",
      "label": "MA MSA"
    },
    "1099_YEAR": {
      "kind": "text",
      "required": true,
      "label": "1099 Year"
    },
    "createdBy": {
      "kind": "text"
    },
    "userId": {
      "kind": "text"
    },
    "corrected": {
      "kind": "text"
    },
    "createdAt": {
      "kind": "text"
    }
  },
  "5498-SA": {
    "SITE_ID": {
      "kind": "text",
      "required": true,
      "label": "Site ID"
    },
    "FILER_FEDERAL_ID_NUMBER": {
      "kind": "text",
      "required": true,
      "label": "Filer Federal ID Number"
    },
    "FILER_NAME": {
      "kind": "text",
      "required": true,
      "label": "Filer Name"
    },
    "FILER_ADDRESS1": {
      "kind": "text",
      "required": true,
      "label": "Filer Address 1"
    },
    "FILER_ADDRESS2": {
      "kind": "text",
      "label": "Filer Address 2"
    },
    "FILER_CITY": {
      "kind": "text",
      "required": true,
      "label": "Filer City"
    },
    "FILER_STATE": {
      "kind": "text",
      "required": true,
      "label": "Filer State"
    },
    "FILER_ZIP": {
      "kind": "text",
      "required": true,
      "label": "Filer ZIP"
    },
    "JOB_NUMBER": {
      "kind": "text",
      "label": "Job Number"
    },
    "FILER_PHONE": {
      "kind": "text",
      "label": "Filer Phone"
    },
    "RECIPIENT_ID_NUMBER": {
      "kind": "text",
      "required": true,
      "label": "Recipient ID Number"
    },
    "RECIPIENT_NAME": {
      "kind": "text",
      "required": true,
      "label": "Recipient Name"
    },
    "RECIPIENT_NAME2": {
      "kind": "text",
      "label": "Recipient Name 2"
    },
    "RECIPIENT_ADDRESS1": {
      "kind": "text",
      "required": true,
      "label": "Recipient Address 1"
    },
    "RECIPIENT_ADDRESS2": {
      "kind": "text",
      "label": "Recipient Address 2"
    },
    "RECIPIENT_ADDRESS3": {
      "kind": "text",
      "label": "Recipient Address 3"
    },
    "RECIPIENT_CITY": {
      "kind": "text",
      "required": true,
      "label": "Recipient City"
    },
    "RECIPIENT_STATE": {
      "kind": "text",
      "required": true,
      "label": "Recipient State"
    },
    "RECIPIENT_ZIP": {
      "kind": "text",
      "required": true,
      "label": "Recipient ZIP"
    },
    "ACCOUNT_NUMBER": {
      "kind": "text",
      "label": "Account Number"
    },
    "BOX1_ARCHER_MSA_CONTRIBUTIONS": {
      "kind": "amount",
      "label": "Archer MSA Contributions"
    },
    "BOX2_TOTAL_CONTRIBUTIONS": {
      "kind": "amount",
      "label": "Total Contributions"
    },
    "BOX3_TOTAL_HSA_ARCHER_CONTRIBUTIONS": {
      "kind": "amount",
      "label": "Total HSA/Archer Contributions"
    },
    "BOX4_ROLLOVER_CONTRIBUTIONS": {
      "kind": "amount",
      "label": "Rollover Contributions"
    },
    "BOX5_FAIR_MARKET_VALUE_HSA_ARCHER_MSA_MA_MSA": {
      "kind": "amount",
      "label": "Fair Market Value HSA/Archer MSA/MA MSA"
    },
    "BOX6_HSA": {
      "kind": "checkbox",
      "label": "HSA"
    },
    "BOX6_ARCHER_MSA": {
      "kind": "checkbox",
      "label": "Archer MSA"
    },
    "BOX6_MA_MSA": {
      "kind": "checkbox",
      "label": "MA MSA"
    },
    "FOREIGN_COUNTRY": {
      "kind": "text",
      "label": "Foreign Country"
    },
    "FOREIGN_COUNTRY_INDICATOR": {
      "kind": "checkbox",
      "label": "Foreign Country Indicator"
    },
    "1099_YEAR": {
      "kind": "text",
      "required": true,
      "label": "1099 Year"
    },
    "createdBy": {
      "kind": "text"
    },
    "userId": {
      "kind": "text"
    },
    "corrected": {
      "kind": "text"
    },
    "createdAt": {
      "kind": "text"
    }
  }
};

const YEARS = Object.keys(FIELD_RULES_BY_YEAR).sort();

/**
 * The rules for one form in one tax year: that year if core-api defines it, otherwise the
 * nearest year at or below it, otherwise the un-versioned definition.
 */
export function fieldRules(formType: string, taxYear: number): FormFieldRules {
  const exact = FIELD_RULES_BY_YEAR[String(taxYear)]?.[formType];
  if (exact) return exact;
  for (const year of YEARS.slice().reverse()) {
    if (Number(year) > taxYear) continue;
    const hit = FIELD_RULES_BY_YEAR[year][formType];
    if (hit) return hit;
  }
  const newest = YEARS[YEARS.length - 1];
  return FIELD_RULES_BY_YEAR[newest]?.[formType] ?? FIELD_RULES_BASE[formType] ?? {};
}

/** True when this column holds money on this form. */
export function isAmountField(formType: string, taxYear: number, field: string): boolean {
  return fieldRules(formType, taxYear)[field]?.kind === "amount";
}
